/**
 * Investor-Portal — kurzfristige Liquiditätsplanung (#1838 Phase 2).
 *
 * Aufbau nach dem freigegebenen Prototyp:
 *   1. Zeitraum + liquide Mittel je Konto (mit Abgleich-Zeitpunkt)
 *   2. KPI-Zeile
 *   3. Kontostandsverlauf — zwei Kurven (Fälligkeit vs. erwartetes Zahlungsverhalten)
 *   4. Nicht terminierte Verpflichtungen (deutsche USt) — Entscheidung 4
 *   5. Größte Bewegungen je Woche + Kontorahmen
 *   6. Offene Forderungen mit Zahlungsverhalten (Überzug überschreibbar)
 *   7. Offene Verbindlichkeiten mit Stundung / Ratenplan
 *   8. Planung je Monat (jeder Wert und jeder Zahltag überschreibbar)
 *
 * #1839 Phase 3b ergänzt: Abstimmungstabelle zum Tiefpunkt, Monatsvergleich
 * IST/PROGNOSE/SOLL, gemessene Zahlungsregeln (überschreibbar), zweifelhafte
 * Altforderungen mit Rückholung, Fixkosten als ANNAHME mit Einmal-Abschlag.
 *
 * #1840 ergänzt: IST-Kontostandslinie vor dem Startpunkt (Rückblick 3/6 Monate
 * oder ab Jänner, Selbsttest gegen den Startsaldo), Monats-Brücke je Monat
 * (IST aus #1191, PROGNOSE aus dem Modell, Zeilen aufklappbar), DE-USt-Karte
 * mit Vorjahres-Anteil und Buchhaltungsstand, Plan-Ist-Drift mit Run-Rate,
 * vier Tabellen-Karten einklappbar (Zustand je Browser).
 *
 * Jede Zahl trägt ihre Herkunft: IST (aus Odoo) · PLAN (aus der Planrechnung) ·
 * MANUELL (hier gesetzt) · ANNAHME (abgeleitet, kein Istwert). Odoo wird
 * ausschließlich gelesen — jede Eingabe landet in unseren eigenen Tabellen.
 *
 * @since v2.880.0
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot,
} from 'recharts';

const API = import.meta.env.VITE_API_URL || '';

const MONATE = ['Jän.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

function eur(v, stellen = 0) {
  if (v === null || v === undefined || isNaN(v)) return '–';
  return new Intl.NumberFormat('de-AT', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: stellen, maximumFractionDigits: stellen,
  }).format(v);
}
function datum(iso) {
  if (!iso) return '–';
  const [j, m, t] = String(iso).slice(0, 10).split('-');
  return `${t}.${m}.${j}`;
}
function kurzDatum(iso) {
  if (!iso) return '';
  const [, m, t] = String(iso).slice(0, 10).split('-');
  return `${t}.${m}.`;
}
function heuteIso() {
  return new Date().toISOString().slice(0, 10);
}

/** Erster des Folgemonats — sinnvollere Vorbelegung als „heute" (#1860-Nachbesserung). */
function ersterFolgemonatIso() {
  const d = new Date();
  return `${d.getMonth() === 11 ? d.getFullYear() + 1 : d.getFullYear()}-${String((d.getMonth() + 1) % 12 + 1).padStart(2, '0')}-01`;
}

/**
 * Zahleneingabe aus einem Textfeld: leer, Komma und Punkt sind alle erlaubt.
 * Stehen BEIDE Zeichen drin (18.440,53), ist der Punkt der Tausenderpunkt;
 * steht nur eines drin, ist es das Dezimalzeichen. Rückgabe `null` bei leer,
 * `NaN` bei unlesbar — beides muss der Aufrufer unterscheiden können.
 */
function dez(text) {
  const s = String(text ?? '').trim().replace(/\s|€/g, '');
  if (s === '') return null;
  const hatKomma = s.includes(',');
  const roh = hatKomma ? s.replace(/\./g, '').replace(',', '.') : s;
  return Number(roh);
}

/**
 * #1841 Teil D: runde Y-Achsen-Schritte. Der kleinste Kandidat, der den
 * Wertebereich in höchstens 8 Schritte teilt, gewinnt; Ober- und Untergrenze
 * werden auf den Schritt gerundet — nie ein krummer Achsenwert.
 */
function rundeTicks(min, max) {
  const range = Math.max(1, max - min);
  const schritt = [5000, 10000, 20000, 25000, 50000, 100000].find(s => range / s <= 8) || 200000;
  const von = Math.floor(min / schritt) * schritt;
  const bisT = Math.ceil(max / schritt) * schritt;
  const ticks = [];
  for (let t = von; t <= bisT; t += schritt) ticks.push(t);
  return { ticks, von, bis: bisT };
}

/** #1843 Punkt 10: ein Name für die Zeile, überall derselbe. */
const VARIABLE_KOSTEN_TITEL = 'Porto, Fracht, Material, Leasing';

/** Zahltag eines Einzelpostens — ein Tag, oder ein Zeitraum bei Verteilung. */
function zahltagText(p) {
  if (!p.zahltag_von) return '';
  return p.zahltag_von === p.zahltag_bis
    ? kurzDatum(p.zahltag_von)
    : `${kurzDatum(p.zahltag_von)} – ${kurzDatum(p.zahltag_bis)}`;
}

/** #1860: „12 Raten à 5.000 € ab 15.10." — Kurztext einer Vereinbarung. */
function vereinbarungText(ver) {
  if (!ver) return '';
  if (ver.art === 'uneinbringlich') return 'uneinbringlich';
  if (ver.art === 'raten') {
    const raten = ver.raten || [];
    if (!raten.length) return 'Ratenplan';
    const erste = raten[0].betrag_eur;
    const gleich = raten.slice(0, -1).every(r => Math.abs(r.betrag_eur - erste) < 0.01);
    const je = gleich ? ` à ${eur(erste)}` : '';
    return `${raten.length} Raten${je} ab ${kurzDatum(raten[0].datum)}`;
  }
  if (ver.art === 'einmal') return `Einmalzahlung ${kurzDatum(ver.zahlung_am)}`;
  return `Stundung bis ${kurzDatum(ver.zahlung_am)}`;
}

/**
 * #1861: die beiden Forderungs-Quellen (zweifelhafte Altposten und die
 * Kundenliste) benennen dieselben Felder unterschiedlich. Normalisiert wird
 * genau hier, damit der Vereinbarungs-Dialog EINEN Zeilen-Aufbau kennt.
 */
function arZeileNorm(z, kunde, partnerId) {
  return {
    move_line_id: z.move_line_id ?? z.id,
    beleg: z.beleg,
    faellig_am: z.faellig_am || z.faellig,
    offen_eur: z.betrag_eur,
    kunde: kunde ?? z.kunde,
    partner_id: partnerId ?? z.partner_id,
    vereinbarung: z.vereinbarung || null,
  };
}

function arZeilenNormalisiert(d) {
  return [
    ...(d.zweifelhaft?.liste || []).map(z => arZeileNorm(z)),
    ...(d.forderungen?.liste || []).flatMap(f => (f.zeilen || []).map(z => arZeileNorm(z, f.kunde, f.partner_id))),
  ];
}

/** Herkunfts-Marke — die Farbe unterscheidet, der Text erklärt. */
const HERKUNFT = {
  IST:     { label: 'IST',     cls: 'bg-[#EFF6FF] text-[#338edc] border-[#BFDBFE]', titel: 'Aus Odoo übernommen' },
  PLAN:    { label: 'PLAN',    cls: 'bg-[#EEF2FF] text-[#6366F1] border-[#C7D2FE]', titel: 'Aus der Planrechnung' },
  MANUELL: { label: 'MANUELL', cls: 'bg-[#ECFEFF] text-[#0891B2] border-[#A5F3FC]', titel: 'Hier von Hand gesetzt' },
  ANNAHME: { label: 'ANNAHME', cls: 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]', titel: 'Abgeleitete Annahme, kein Istwert' },
  PROGNOSE: { label: 'PROGNOSE', cls: 'bg-[#FDF2F8] text-[#BE185D] border-[#FBCFE8]', titel: 'Aus dem Modell errechnet' },
  ENTWURF: { label: 'ENTWURF', cls: 'bg-[#F5F3FF] text-[#7C3AED] border-[#DDD6FE]', titel: 'In Odoo erfasst, aber noch nicht gebucht' },
  FORECAST: { label: 'FORECAST', cls: 'bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]', titel: 'Rollierender Forecast — überschreibt den eingefrorenen Plan (#1857)' },
  SZENARIO: { label: 'SZENARIO', cls: 'bg-[#FFF7ED] text-[#EA580C] border-[#FED7AA]', titel: 'Probeweise geändert — nicht gespeichert (#1851)' },
  VEREINBARUNG: { label: 'VEREINBARUNG', cls: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]', titel: 'Schriftlich zugesagte Zahlungsvereinbarung (#1860)' },
};

/**
 * #1859-Nachbesserung (7): Marken, die in der Executive Summary vorkommen
 * können — als Legende unter der Karte, damit der Prosa-Text nicht hinter
 * jeder Zahl einen Chip tragen muss.
 */
const SUMMARY_LEGENDE = ['IST', 'PROGNOSE', 'VEREINBARUNG', 'ANNAHME', 'MANUELL'];

/** #1851: nur diese Felder sind im Szenario überschreibbar (USt rechnet mit). */
const SZENARIO_FELDER = ['umsatz', 'personal', 'fixkosten', 'variable_kosten'];
const SZENARIO_FELD_LABEL = {
  umsatz: 'Umsatz', personal: 'Personal', fixkosten: 'Fixkosten', variable_kosten: 'Variable Kosten',
};
function Marke({ art }) {
  const m = HERKUNFT[art];
  if (!m) return null;
  return (
    <span title={m.titel}
      className={`inline-block px-1.5 py-0.5 rounded border text-[9px] font-semibold tracking-wide align-middle ${m.cls}`}>
      {m.label}
    </span>
  );
}

/** #1859: Marken-Token wie „[IST]" im Prosa-Text als Chips rendern. */
function TextMitMarken({ text }) {
  const teile = String(text || '').split(/(\[(?:IST|PLAN|MANUELL|ANNAHME|PROGNOSE|ENTWURF|FORECAST|SZENARIO|VEREINBARUNG)\])/g);
  return (
    <p className="text-sm text-[#1A1A2E] leading-relaxed">
      {teile.map((t, i) => {
        const m = t.match(/^\[([A-Z]+)\]$/);
        return m ? <Fragment key={i}><Marke art={m[1]} />{' '}</Fragment> : <Fragment key={i}>{t}</Fragment>;
      })}
    </p>
  );
}

/**
 * #1859: Executive Summary für Wirtschaftsprüfer/Bank — farbige Karte direkt
 * unter dem Kurvendiagramm, BEWUSST nicht einklappbar: die drei Fragen (warum
 * Bedarf, warum diese Höhe, wofür verwendet) müssen ohne Klick lesbar sein (AK1).
 * Anzeige-Präzedenz MANUELL > KI > REGELBASIERT; die regelbasierte Fassung
 * kommt immer mit — die Karte ist nie leer (AK6). Die KI formuliert nur, sie
 * rechnet nicht: ein Text mit einer Zahl außerhalb des Datensatzes wird
 * serverseitig verworfen und hier als Hinweis gezeigt (AK5).
 */
function ZusammenfassungKarte({ z, szenarioAktiv, speichert, schreibe }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [entwurf, setEntwurf] = useState([]);
  const ds = z.datensatz;
  /**
   * #1859 (1): eine KI-Fassung, deren Zahlen es im aktuellen Datenstand nicht
   * mehr gibt, wird vom Server zurückgehalten (`zurueckgehalten`) — dann greift
   * die regelbasierte Fassung. Sie ist maschinell erzeugt und mit einem Klick
   * neu formuliert; ihr Text neben widersprechenden Tabellen zu zeigen wäre
   * der Fehler, den Manuel gemeldet hat.
   */
  const kiAnzeigbar = z.ki && !z.ki.zurueckgehalten ? z.ki : null;
  const quelle = z.manuell ? 'manuell' : (kiAnzeigbar ? 'ki' : 'regel');
  const fassung = z.manuell || kiAnzeigbar || z.regel;
  const abschnitte = fassung?.abschnitte || [];
  const zeit = (iso) => (iso ? `${datum(iso)} ${String(iso).slice(11, 16)}`.trim() : '–');
  /**
   * #1860-Nachbesserung: verglichen werden die ZAHLEN, nicht das Datum. Eine
   * Vereinbarung, die heute gesetzt wird, ändert Kurve, Tiefpunkt und Bedarf,
   * aber nicht den Datenstands-TAG — die alte Prosa wäre unbemerkt stehen
   * geblieben. Fehlt der Fingerabdruck (Fassung von vor dieser Änderung),
   * gilt weiter der Datumsvergleich.
   */
  const veraltet = quelle !== 'regel' && !!fassung && (
    (fassung.fingerabdruck && z.fingerabdruck)
      ? fassung.fingerabdruck !== z.fingerabdruck
      : (!!fassung.stand && !!ds?.stand && fassung.stand !== ds.stand));
  const av = ds?.alt_verbindlichkeiten;
  // #1859 (4): die drei Lieferantenwerte mit ihren Bezeichnungen aus dem
  // Datensatz — nicht zusammengeführt, sondern eindeutig beschriftet.
  const vb = ds?.verbindlichkeiten_bestand;
  const gsD = ds?.gutschriften;

  /**
   * #1859 A1: bearbeitet wird die VORLAGE mit ihren Stellen, nicht der
   * gesetzte Text. Wer eine Zahl abtippt, friert sie ein — genau das war der
   * gemeldete Widerspruch (Prosa vom Vortag neben frischen Tabellen). Wer eine
   * Stelle stehen lässt, bekommt beim nächsten Laden die aktuelle Zahl.
   */
  const startBearbeiten = () => {
    const quellAbschnitte = fassung?.abschnitte_vorlage || abschnitte;
    setEntwurf(quellAbschnitte.map(a => ({ titel: a.titel, text: a.text })));
    setBearbeiten(true);
  };
  const speichernManuell = async () => {
    await schreibe('zusammenfassung/manuell', { abschnitte: entwurf, bis: ds?.bis }, 'PUT');
    setBearbeiten(false);
  };

  return (
    <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Executive Summary — für Wirtschaftsprüfer und Bank</h2>
          <p className="text-xs text-[#6B7280] mt-1 max-w-3xl">
            {quelle === 'manuell' && <>Manuelle Fassung vom {zeit(z.manuell.erstellt_am)} (Datenstand {datum(z.manuell.stand)}). </>}
            {quelle === 'ki' && <>KI-formulierte Fassung vom {zeit(z.ki.erstellt_am)} (Datenstand {datum(z.ki.stand)}, Modell {z.ki.modell || '–'}) — jede Zahl gegen den Datensatz geprüft. </>}
            {quelle === 'regel' && <>Regelbasierte Fassung aus dem aktuellen Datenstand {datum(ds?.stand)} — ohne KI erzeugt. </>}
            Jede Zahl trägt ihre Herkunft.
          </p>
          {z.ki?.zurueckgehalten && (
            <p className="text-xs text-[#B91C1C] mt-1 font-medium">
              Die KI-Fassung vom {zeit(z.ki.erstellt_am)} wird <strong>nicht angezeigt</strong>:
              {z.ki.widerspruch && <>
                {' '}Sie nennt {(z.ki.fremde_zahlen || []).length === 1 ? 'eine Zahl' : `${(z.ki.fremde_zahlen || []).length} Zahlen`},
                {' '}die es im aktuellen Datenstand nicht mehr gibt
                {(z.ki.fremde_zahlen || []).length > 0 && <> ({(z.ki.fremde_zahlen || []).slice(0, 6).join(', ')})</>}.
              </>}
              {z.ki.behauptung && <>
                {' '}Sie behauptet etwas über Zahlungsverhalten, das diese Planung nicht wissen kann
                {' '}({(z.ki.behauptungen || []).slice(0, 3).join('; ')}) — sie liest Salden, keine Zahlungsvorgänge.
              </>}
              {/* #1859 A1: eine Stelle, die sich nicht setzen lässt, stünde als
                  „{name}" mitten im Satz — vor einem Prüfer schlimmer als eine
                  falsche Zahl. */}
              {(z.ki.offene_stellen || []).length > 0 && <>
                {' '}Eine Stelle im Text lässt sich im aktuellen Datenstand nicht setzen
                {' '}({z.ki.offene_stellen.map(n => `{${n}}`).join(', ')}).
              </>}
              {' '}Angezeigt wird stattdessen die regelbasierte Fassung aus dem aktuellen Stand.
              {' '}„Zusammenfassung aktualisieren" formuliert sie neu.
            </p>
          )}
          {quelle === 'manuell' && (z.manuell?.widerspruch || z.manuell?.behauptung) && (
            <p className="text-xs text-[#B91C1C] mt-1 font-medium">
              Achtung, diese manuelle Fassung:
              {z.manuell.widerspruch && <>
                {' '}Sie nennt Zahlen, die es im aktuellen Datenstand nicht gibt
                {(z.manuell.fremde_zahlen || []).length > 0 && <> ({(z.manuell.fremde_zahlen || []).slice(0, 6).join(', ')})</>},
                {' '}und widerspricht damit den Tabellen darunter.
              </>}
              {z.manuell.behauptung && <>
                {' '}Sie behauptet etwas über Zahlungsverhalten, das diese Planung nicht wissen kann
                {' '}({(z.manuell.behauptungen || []).slice(0, 3).join('; ')}).
              </>}
              {' '}Bitte bearbeiten oder entfernen.
            </p>
          )}
          {veraltet && !z.ki?.zurueckgehalten && (
            <p className="text-xs text-[#B45309] mt-1">
              Diese Fassung beruht auf anderen Zahlen als der aktuelle Stand
              {fassung.stand && ds?.stand && fassung.stand !== ds.stand
                ? <> (Fassung {datum(fassung.stand)}, aktuell {datum(ds.stand)})</>
                : <> vom {datum(ds?.stand)} — inzwischen wurde etwas geändert</>}
              {' '}— „Zusammenfassung aktualisieren" empfohlen.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!bearbeiten && (
            <>
              <button
                onClick={() => schreibe('zusammenfassung/aktualisieren', { bis: ds?.bis, basis: ds?.basis }, 'POST')}
                disabled={speichert || szenarioAktiv}
                title={szenarioAktiv ? 'Szenario aktiv — erst übernehmen oder verwerfen' : 'KI formuliert den aktuellen Datensatz neu; sie rechnet nicht'}
                className="px-3 py-1.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold disabled:opacity-50 transition-colors">
                Zusammenfassung aktualisieren
              </button>
              <button
                onClick={startBearbeiten}
                disabled={speichert || szenarioAktiv}
                className="px-3 py-1.5 rounded-[10px] border border-[#BFDBFE] bg-white text-xs font-medium text-[#1A1A2E] hover:bg-[#FAFAFE] disabled:opacity-50">
                Bearbeiten
              </button>
              {z.manuell && (
                <button
                  onClick={() => schreibe('zusammenfassung/manuell', {}, 'DELETE')}
                  disabled={speichert || szenarioAktiv}
                  title="Zurück zur KI- bzw. regelbasierten Fassung"
                  className="px-3 py-1.5 rounded-[10px] border border-[#BFDBFE] bg-white text-xs font-medium text-[#6B7280] hover:bg-[#FAFAFE] disabled:opacity-50">
                  Manuelle Fassung entfernen
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* #1859: der Konsistenz-Check läuft VOR der Anzeige und warnt sichtbar,
          statt still auszugeben. Er verwirft nichts — ein Zahlenwerk, das sich
          widerspricht, ist eine echte Datenlage (etwa Odoo-Drift nach einer
          Vereinbarung); wer sie ausblendet, nimmt dem Prüfer die Chance, sie zu
          bemerken. Der Block steht ÜBER dem Text: eine Warnung unter dem
          Fließtext liest niemand, der die drei Antworten sucht. */}
      {/* #1859 A3: Tageswechsel-Hinweis. Seit die Zahlen beim Anzeigen gesetzt
          werden, kann der Text zahlenmäßig nicht mehr veralten — genau deshalb
          braucht der Leser diesen Block: unter der gleichbleibenden Prosa hat
          sich etwas bewegt. Gemessen am 08.09.: die überfälligen Forderungen
          sprangen über Nacht um 184.615,17 €, weil 123 Monatsrechnungen fällig
          wurden. Ohne diesen Satz bleibt ein Sprung ein Rätsel. */}
      {z.datenstand && !z.datenstand.ok && (
        <div className="mb-3 rounded border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2">
          <div className="text-xs font-semibold text-[#B45309]">
            Seit dieser Text formuliert wurde ({datum(z.datenstand.stand_fassung)}), haben sich{' '}
            {z.datenstand.aenderungen.length}
            {z.datenstand.aenderungen.length === 1 ? ' Kennzahl' : ' Kennzahlen'} wesentlich bewegt
          </div>
          <ul className="mt-1 space-y-0.5">
            {z.datenstand.aenderungen.map((a, i) => (
              <li key={i} className="text-[11px] text-[#1A1A2E]">
                {a.label}: <span className="tabular-nums">{eur(a.damals_eur, 2)} → {eur(a.heute_eur, 2)}</span>
                <span className="tabular-nums text-[#6B7280]">
                  {' '}({a.differenz_eur > 0 ? '+' : ''}{eur(a.differenz_eur, 2)}
                  {a.prozent != null && <>, {String(a.prozent).replace('.', ',')} %</>})
                </span>
                {a.erlaeuterung && <span className="text-[#6B7280]"> — {a.erlaeuterung}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[11px] text-[#6B7280]">
            Die Zahlen im Text sind aktuell — sie werden beim Anzeigen aus dem heutigen Datenstand gesetzt.
            Der Hinweis betrifft die Aussagen, die auf den älteren Zahlen beruhen.
          </div>
        </div>
      )}

      {z.konsistenz && !z.konsistenz.ok && (
        <div className="mb-3 rounded border border-[#FECACA] bg-[#FEF2F2] px-3 py-2">
          <div className="text-xs font-semibold text-[#B91C1C]">
            Zahlen stimmen nicht überein — {z.konsistenz.befunde.length}
            {z.konsistenz.befunde.length === 1 ? ' Abweichung' : ' Abweichungen'} zwischen Text, Tabellen und Karten
          </div>
          <ul className="mt-1 space-y-0.5">
            {z.konsistenz.befunde.map((b, i) => (
              <li key={i} className="text-[11px] text-[#1A1A2E]">
                {b.regel}
                {b.differenz_eur != null && (
                  <span className="tabular-nums text-[#6B7280]">
                    {' '}— gemessen {eur(b.gemessen_eur, 2)}, erwartet {eur(b.erwartet_eur, 2)},
                    {' '}Differenz {eur(b.differenz_eur, 2)}
                  </span>
                )}
                {b.erlaeuterung && <span className="text-[#6B7280]"> — {b.erlaeuterung}</span>}
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[11px] text-[#6B7280]">
            Die Zahlen werden trotzdem angezeigt: die Abweichung ist eine Datenlage, kein Anzeigefehler.
            Bitte vor der Weitergabe an Prüfer oder Bank klären.
          </div>
        </div>
      )}

      {bearbeiten ? (
        <div className="space-y-3">
          <p className="text-xs text-[#6B7280] max-w-3xl">
            Zahlen als <strong>Stelle</strong> in geschweiften Klammern schreiben, etwa{' '}
            <code className="bg-white px-1 rounded border border-[#BFDBFE]">{'{ar_ueberfaellig_eur}'}</code> —
            sie wird bei jeder Anzeige aus dem aktuellen Datenstand gesetzt. Eine abgetippte Zahl bleibt stehen,
            auch wenn sich die Daten ändern, und widerspricht dann den Tabellen darunter.
          </p>
          {entwurf.map((a, i) => (
            <div key={i}>
              <input
                value={a.titel}
                onChange={e => setEntwurf(l => l.map((x, j) => (j === i ? { ...x, titel: e.target.value } : x)))}
                className="w-full px-2 py-1 text-sm font-semibold border border-[#BFDBFE] rounded-md mb-1 bg-white focus:outline-none focus:border-[#338edc]" />
              <textarea
                value={a.text} rows={4}
                onChange={e => setEntwurf(l => l.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                className="w-full px-2 py-1 text-sm border border-[#BFDBFE] rounded-md bg-white focus:outline-none focus:border-[#338edc]" />
            </div>
          ))}
          {/* Die verfügbaren Stellen mit ihrem heutigen Wert — eine Liste, die
              man nicht sieht, wird nicht benutzt; dann werden Zahlen getippt. */}
          {z.platzhalter?.length > 0 && (
            <details className="text-xs text-[#6B7280]">
              <summary className="cursor-pointer select-none font-medium text-[#4B5563]">
                Verfügbare Stellen ({z.platzhalter.length})
              </summary>
              <div className="mt-1.5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5 max-h-64 overflow-y-auto">
                {z.platzhalter.map(p => (
                  <div key={p.name} className="flex gap-2">
                    <code className="text-[#338edc] whitespace-nowrap">{`{${p.name}}`}</code>
                    <span className="truncate" title={`${p.label} — heute: ${p.wert}`}>{p.label} · {p.wert}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          <div className="flex gap-2">
            <button onClick={speichernManuell} disabled={speichert}
              className="px-3 py-1.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold disabled:opacity-50">
              Als manuelle Fassung speichern
            </button>
            <button onClick={() => setBearbeiten(false)}
              className="px-3 py-1.5 rounded-[10px] border border-[#BFDBFE] bg-white text-xs font-medium text-[#6B7280] hover:bg-[#FAFAFE]">
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {abschnitte.map((a, i) => (
            <div key={i}>
              <h3 className="text-xs font-semibold text-[#1A1A2E] mb-0.5">{a.titel}</h3>
              <TextMitMarken text={a.text} />
            </div>
          ))}
        </div>
      )}

      {/* #1859 STRAFFUNG: die Aufteilung nach Verwendung stand als Aufzählung im
          Fließtext — vier Gruppen mit je Betrag und Anteil sind acht Zahlen in
          einem Satz. Sie steht jetzt als Tabelle, der Text nennt nur noch die
          größte Gruppe. Die Gruppenbeträge summieren sich exakt auf die
          Gesamtsumme (Cent-Arithmetik in baueDatensatz), deshalb trägt die
          Tabelle eine Summenzeile — sie ist der Beleg, dass nichts fehlt. */}
      {ds?.abfluesse?.gruppen?.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-[#1A1A2E] mb-0.5">Verwendung der Mittel</h3>
          <p className="text-xs text-[#6B7280] mb-1.5 max-w-xl">
            Alle Abflüsse bis {ds.abfluesse.bis ? datum(ds.abfluesse.bis) : '–'} nach Verwendung.
            Stand {ds.stand ? datum(ds.stand) : '–'}.
          </p>
          <table className="text-sm max-w-xl w-full">
            <tbody>
              {ds.abfluesse.gruppen.map((g, i) => (
                <tr key={g.schluessel || i} className="border-b border-[#DBEAFE]">
                  {/* #1859 (4): der Zusatz nennt die Beziehung zur zweiten
                      Lieferantenzahl. Ohne ihn wurde der Gesamtbestand mit dem
                      überfälligen Teil verwechselt. */}
                  <td className="py-1 text-[#4B5563]">
                    {g.label}
                    {g.zusatz && <span className="block text-[11px] text-[#6B7280]">{g.zusatz}</span>}
                  </td>
                  <td className="py-1 text-right tabular-nums text-[#6B7280] w-16 align-top">
                    {g.anteil_prozent == null ? '–' : `${String(g.anteil_prozent).replace('.', ',')} %`}
                  </td>
                  <td className="py-1 text-right tabular-nums text-[#1A1A2E] align-top">{eur(Math.abs(g.betrag_eur), 2)}</td>
                </tr>
              ))}
              <tr>
                <td className="py-1 font-semibold text-[#1A1A2E]">Summe <Marke art={ds.abfluesse.herkunft} /></td>
                <td className="py-1" />
                <td className="py-1 text-right tabular-nums font-semibold text-[#1A1A2E]">
                  {eur(Math.abs(ds.abfluesse.gesamt_eur), 2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* AK3: Vereinbarung getrennt von Annahme, plus Abbaumonat — immer
          strukturiert aus dem Datensatz, unabhängig von der Prosa-Fassung.

          #1859-Nachbesserung (1): die Tabelle wird AUSSCHLIESSLICH aus den
          offenen Kreditorenposten gebildet. Eine freie Verpflichtung (#1860,
          z. B. die DE-USt) hat gar keine Odoo-Kreditorenzeile und kann hier
          strukturell nie auftauchen — „davon mit Ratenplan 0,00 €" stand
          deshalb neben einer Prosa, die einen Ratenplan beschrieb. Beides war
          für sich richtig, die Überschrift war es nicht. Sie sagt jetzt, worauf
          sie sich bezieht; die freie Verpflichtung steht als eigene Tabelle
          darunter. Zusammenzählen wäre die schlechtere Lösung: Kreditorensaldo
          plus Steuerverbindlichkeit ergäbe eine Zahl, die es in keiner
          Auswertung gibt. */}
      {av && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-[#1A1A2E] mb-0.5">Lieferantenverbindlichkeiten und ihr Abbau</h3>
          <p className="text-xs text-[#6B7280] mb-1.5 max-w-xl">
            {av.grundlage} {av.bezug_gesamtbestand}
          </p>
          <table className="text-sm max-w-xl w-full">
            <tbody>
              {/* #1859 (4): jede der drei Lieferantenzahlen mit ihrem Bezug.
                  Der Gesamtbestand steht als eigene Zeile ÜBER dem überfälligen
                  Teil — sie wurden verwechselt, weil sie nie zusammen standen. */}
              {vb && (
                <tr className="border-b border-[#DBEAFE]">
                  <td className="py-1 text-[#4B5563]">{vb.gesamt_label} <Marke art="IST" /></td>
                  <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(vb.gesamt_eur, 2)}</td>
                </tr>
              )}
              <tr className="border-b border-[#DBEAFE]">
                <td className="py-1 text-[#4B5563] pl-3">
                  {vb ? vb.ueberfaellig_label : 'Davon überfällig'}
                  {' '}<Marke art="IST" />
                </td>
                <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(av.ueberfaellig_eur, 2)}</td>
              </tr>
              {vb?.in_karenz_eur > 0 && (
                <tr className="border-b border-[#DBEAFE]">
                  <td className="py-1 text-[#4B5563] pl-3">
                    davon fällig, aber noch in der Karenz ({vb.in_karenz_belege}{' '}
                    {vb.in_karenz_belege === 1 ? 'Beleg' : 'Belege'}) <Marke art="ANNAHME" />
                  </td>
                  <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(vb.in_karenz_eur, 2)}</td>
                </tr>
              )}
              <tr className="border-b border-[#DBEAFE]">
                <td className="py-1 text-[#4B5563] pl-3">davon mit Stundung / Ratenplan <Marke art="VEREINBARUNG" /></td>
                <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(av.mit_vereinbarung_eur, 2)}</td>
              </tr>
              {av.davon_eigene_annahme_eur >= 0.01 && (
                <tr className="border-b border-[#DBEAFE]">
                  <td className="py-1 text-[#4B5563] pl-6">darin eigene Planung, nicht zugesagt <Marke art="ANNAHME" /></td>
                  <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(av.davon_eigene_annahme_eur, 2)}</td>
                </tr>
              )}
              <tr className="border-b border-[#DBEAFE]">
                <td className="py-1 text-[#4B5563] pl-3">davon über Annahme „heute + Lieferanten-Median" <Marke art="ANNAHME" /></td>
                <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(av.ohne_vereinbarung_eur, 2)}</td>
              </tr>
              <tr>
                <td className="py-1 text-[#4B5563]">Abbau dieser Lieferantenverbindlichkeiten nach Modell bis</td>
                <td className="py-1 text-right text-[#1A1A2E]">
                  {av.abbau_monat ? `${MONATE[parseInt(av.abbau_monat.slice(5), 10) - 1]} ${av.abbau_monat.slice(0, 4)}` : '–'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* #1859-Nachbesserung (1)+(4): freie Verpflichtungen stehen getrennt und
          mit EIGENEM Endtermin — der Abbaumonat oben gilt nur für die
          Lieferantenseite, sonst läse sich „Abbau bis Dezember 2026" neben
          einem Ratenplan, der bis August 2027 läuft. */}
      {ds?.freie_verpflichtungen?.liste?.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-[#1A1A2E] mb-0.5">Freie Verpflichtungen (außerhalb der Kreditorenbuchhaltung)</h3>
          <p className="text-xs text-[#6B7280] mb-1.5 max-w-xl">
            Verpflichtungen ohne offenen Posten in Odoo — sie erscheinen deshalb nicht in der Tabelle oben.
          </p>
          <table className="text-sm max-w-xl w-full">
            <tbody>
              {ds.freie_verpflichtungen.liste.map((v, i) => (
                <Fragment key={i}>
                  <tr className="border-b border-[#DBEAFE]">
                    <td className="py-1 text-[#4B5563]">{v.bezeichnung} gesamt <Marke art={v.herkunft} /></td>
                    <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(v.gesamt_eur, 2)}</td>
                  </tr>
                  <tr className="border-b border-[#DBEAFE]">
                    <td className="py-1 text-[#4B5563] pl-3">davon per Ratenplan terminiert, in der Kurve</td>
                    <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(v.terminiert_eur, 2)}</td>
                  </tr>
                  {v.rest_ohne_termin_eur >= 0.01 && (
                    <tr className="border-b border-[#DBEAFE]">
                      <td className="py-1 text-[#4B5563] pl-3">davon ohne Termin, nicht in der Kurve</td>
                      <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(v.rest_ohne_termin_eur, 2)}</td>
                    </tr>
                  )}
                  <tr className={i < ds.freie_verpflichtungen.liste.length - 1 ? 'border-b border-[#DBEAFE]' : ''}>
                    <td className="py-1 text-[#4B5563]">Letzte Rate fällig am</td>
                    <td className="py-1 text-right text-[#1A1A2E]">{v.endtermin ? datum(v.endtermin) : '–'}</td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AK2-Umfeld: was die Kurve NICHT enthält, steht sichtbar daneben —
          eine Vorschau ohne diese Zeile wäre die gefährlichste Auskunft. */}
      {ds?.nicht_in_kurve?.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-[#1A1A2E] mb-1.5">Nicht in der Kurve enthalten</h3>
          <table className="text-sm max-w-xl w-full">
            <tbody>
              {ds.nicht_in_kurve.map((n, i) => (
                <tr key={i} className={i < ds.nicht_in_kurve.length - 1 ? 'border-b border-[#DBEAFE]' : ''}>
                  <td className="py-1 text-[#4B5563]">{n.label} <Marke art={n.herkunft} /></td>
                  <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(n.betrag_eur, 2)}</td>
                </tr>
              ))}
              {ds.ust_de && !ds.ust_de.in_kurve && ds.ust_de.tiefpunkt_mit_ust_de_eur != null && (
                <tr className="border-t border-[#DBEAFE]">
                  <td className="py-1 text-[#4B5563]">Mit Ansatz der DE-USt läge der Tiefpunkt bei <Marke art="PROGNOSE" /></td>
                  <td className="py-1 text-right tabular-nums text-[#1A1A2E] font-medium">{eur(ds.ust_de.tiefpunkt_mit_ust_de_eur, 2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* #1859 (3): Gutschriften. Sie wurden bis zum 08.09. verworfen — die
          Karte zeigte damit brutto statt netto. Die Brücke steht sichtbar, weil
          der angezeigte Bestand und der Kreditorensaldo laut Odoo bewusst
          auseinanderfallen: ein Guthaben ohne offenen Gegenposten mindert
          keine geplante Zahlung und wird nie als Zufluss angesetzt. */}
      {gsD?.belege > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold text-[#1A1A2E] mb-0.5">Gutschriften auf Kreditorenkonten</h3>
          <p className="text-xs text-[#6B7280] mb-1.5 max-w-xl">{gsD.aussage}</p>
          <table className="text-sm max-w-xl w-full">
            <tbody>
              <tr className="border-b border-[#DBEAFE]">
                <td className="py-1 text-[#4B5563]">Rechnungsbestand vor Verrechnung <Marke art="IST" /></td>
                <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(vb?.brutto_eur, 2)}</td>
              </tr>
              <tr className="border-b border-[#DBEAFE]">
                <td className="py-1 text-[#4B5563] pl-3">
                  abzüglich verrechneter Gutschriften ({gsD.belege} {gsD.belege === 1 ? 'Beleg' : 'Belege'} über {eur(gsD.summe_eur, 2)})
                </td>
                <td className="py-1 text-right tabular-nums text-[#1A1A2E]">−{eur(gsD.verrechnet_eur, 2)}</td>
              </tr>
              <tr className="border-b border-[#DBEAFE]">
                <td className="py-1 text-[#1A1A2E] font-medium">Angezeigter offener Bestand</td>
                <td className="py-1 text-right tabular-nums text-[#1A1A2E] font-medium">{eur(vb?.gesamt_eur, 2)}</td>
              </tr>
              {gsD.nicht_verrechnet_eur >= 0.01 && (
                <>
                  <tr className="border-b border-[#DBEAFE]">
                    <td className="py-1 text-[#4B5563] pl-3">Gutschriften ohne offenen Gegenposten, nicht als Zufluss angesetzt</td>
                    <td className="py-1 text-right tabular-nums text-[#1A1A2E]">−{eur(gsD.nicht_verrechnet_eur, 2)}</td>
                  </tr>
                  <tr>
                    <td className="py-1 text-[#4B5563]">Kreditorensaldo laut Odoo <Marke art="IST" /></td>
                    <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(vb?.odoo_netto_eur, 2)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* #1859 (5): Methodik gehört unter die Karte, nicht in den Fließtext.
          Als 29-Wörter-Satz mitten im Absatz hat sie die Lesbarkeitsprüfung
          gerissen; hier ist sie vollständig belegt und stört den Leser nicht,
          der die drei Antworten sucht. */}
      {ds?.fussnoten?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#DBEAFE] space-y-1.5">
          {ds.fussnoten.map((f, i) => (
            <p key={i} className="text-[11px] text-[#6B7280] max-w-3xl">
              <span className="font-medium text-[#4B5563]">{f.titel}:</span> {f.text}
            </p>
          ))}
        </div>
      )}

      {/* #1859-Nachbesserung (7): die Herkunft bleibt vollständig belegt, sie
          steht nur nicht mehr hinter jeder einzelnen Zahl. Ein Chip je Zahl
          hat den Prosa-Text zerhackt; jetzt trägt der Text höchstens eine Marke
          je Satz, und was dort fehlt, erklärt diese Legende. */}
      <div className="mt-4 pt-3 border-t border-[#DBEAFE] flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="text-xs text-[#6B7280]">Herkunft der Zahlen:</span>
        {SUMMARY_LEGENDE.map(art => (
          <span key={art} className="inline-flex items-center gap-1.5">
            <Marke art={art} />
            <span className="text-xs text-[#6B7280]">{HERKUNFT[art].titel}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * #1840 Teil D: Karten mit `id` sind einklappbar — Default ZU, Zustand je
 * Browser in localStorage (`liq_karte_<id>`). Eingeklappt trägt die Kopfzeile
 * die Kernaussage, damit die Karte auch zu etwas sagt. Karten ohne `id`
 * verhalten sich wie bisher (immer offen).
 */
function Card({ id, titel, hinweis, kopfzeile, rechts, defaultOffen = false, erzwinge, children }) {
  const einklappbar = Boolean(id);
  const [offen, setOffen] = useState(() => {
    if (!einklappbar) return true;
    try {
      const s = localStorage.getItem(`liq_karte_${id}`);
      if (s === 'offen') return true;
      if (s === 'zu') return false;
    } catch { /* privater Modus */ }
    return defaultOffen;
  });
  // #1841 Teil E: „Alle auf-/zuklappen" — der Zähler `n` ändert sich bei jedem
  // Klick, sonst liefe ein zweites „alle zu" ins Leere.
  useEffect(() => {
    if (!einklappbar || !erzwinge?.n) return;
    const o = erzwinge.v === 'auf';
    setOffen(o);
    try { localStorage.setItem(`liq_karte_${id}`, o ? 'offen' : 'zu'); } catch { /* privater Modus */ }
  }, [erzwinge, einklappbar, id]);
  const umschalten = () => {
    if (!einklappbar) return;
    setOffen(o => {
      try { localStorage.setItem(`liq_karte_${id}`, !o ? 'offen' : 'zu'); } catch { /* privater Modus */ }
      return !o;
    });
  };
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      {(titel || rechts) && (
        <div className={`flex items-start justify-between gap-3 ${offen ? 'mb-4' : ''}`}>
          <div className={einklappbar ? 'cursor-pointer select-none' : ''} onClick={umschalten}>
            <h2 className="text-sm font-semibold text-[#1A1A2E]">
              {einklappbar && <span className="text-[#9CA3AF] mr-1.5">{offen ? '▾' : '▸'}</span>}
              {titel}
            </h2>
            {!offen && kopfzeile && <p className="text-xs text-[#6B7280] mt-1 max-w-3xl">{kopfzeile}</p>}
            {offen && hinweis && <p className="text-xs text-[#6B7280] mt-1 max-w-3xl">{hinweis}</p>}
          </div>
          {rechts}
        </div>
      )}
      {offen && children}
    </div>
  );
}

/** Zahleneingabe, die erst beim Verlassen des Feldes speichert. */
function ZahlFeld({ wert, onSave, breite = 'w-28', suffix, placeholder }) {
  const [text, setText] = useState(wert === null || wert === undefined ? '' : String(wert));
  useEffect(() => { setText(wert === null || wert === undefined ? '' : String(wert)); }, [wert]);
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="text" inputMode="decimal" value={text} placeholder={placeholder}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        onBlur={() => {
          const roh = text.trim().replace(/\./g, '').replace(',', '.');
          if (roh === '') return onSave(null);
          const z = Number(roh);
          if (!isNaN(z)) onSave(z);
        }}
        className={`${breite} px-2 py-1 text-sm text-right border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#338edc]`}
      />
      {suffix && <span className="text-xs text-[#9CA3AF]">{suffix}</span>}
    </span>
  );
}

/**
 * #1849: Export als PDF-Bericht / Excel-Arbeitsmappe. Der Dialog stellt nur
 * Zeitraum, Format, Detailtiefe und Abschnitte zusammen — gerechnet wird
 * ausschließlich serverseitig aus denselben Service-Funktionen wie die Seite.
 */
const EXPORT_ABSCHNITTE = [
  { key: 'offene_posten', label: 'Offene Posten (Forderungen + Verbindlichkeiten)' },
  { key: 'zweifelhaft', label: 'Zweifelhafte Altforderungen' },
  { key: 'regeln', label: 'Zahlungsregeln und Annahmen' },
  { key: 'ust_de', label: 'Deutsche Umsatzsteuer' },
];

function ExportDialog({ bis, basis, onClose }) {
  const [format, setFormat] = useState('beide');
  const [detailtiefe, setDetailtiefe] = useState('verdichtet');
  const [abschnitte, setAbschnitte] = useState(() => new Set(EXPORT_ABSCHNITTE.map(a => a.key)));
  const [zeitraumBis, setZeitraumBis] = useState(bis ? String(bis).slice(0, 10) : '');
  const [laeuft, setLaeuft] = useState(null);
  const [fehler, setFehler] = useState(null);

  const toggleAbschnitt = (key) => setAbschnitte(prev => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const ladeDatei = async (fmt) => {
    const params = new URLSearchParams({ format: fmt, detailtiefe });
    if (zeitraumBis) params.set('bis', zeitraumBis);
    if (basis) params.set('basis', basis); // #1857: Export = dieselbe Basis wie die Seite
    params.set('abschnitte', [...abschnitte].join(','));
    const res = await fetch(`${API}/api/investor/liquiditaet/export?${params}`, { credentials: 'include' });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `Export (${fmt.toUpperCase()}) fehlgeschlagen`);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] || `Liquiditaetsplanung.${fmt}`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const exportiere = async () => {
    setFehler(null);
    const formate = format === 'beide' ? ['pdf', 'xlsx'] : [format];
    try {
      for (const fmt of formate) {
        setLaeuft(fmt === 'pdf' ? 'PDF-Bericht wird erstellt…' : 'Excel-Arbeitsmappe wird erstellt…');
        await ladeDatei(fmt);
      }
      onClose();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={laeuft ? undefined : onClose}>
      <div className="bg-white border border-[#E5E7EB] rounded-xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div>
          <h2 className="text-sm font-semibold text-[#1A1A2E]">Export</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">PDF-Bericht und Excel-Arbeitsmappe — Zahlen exakt wie auf dieser Seite.</p>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-[#6B7280] w-24">Zeitraum bis</label>
          <input type="date" value={zeitraumBis} onChange={e => setZeitraumBis(e.target.value)}
            className="px-2 py-1 text-sm border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#338edc]" />
        </div>

        <div className="flex items-start gap-2">
          <span className="text-xs text-[#6B7280] w-24 pt-1">Format</span>
          <div className="flex gap-2">
            {[['pdf', 'PDF'], ['xlsx', 'Excel'], ['beide', 'Beide']].map(([v, l]) => (
              <button key={v} onClick={() => setFormat(v)}
                className={`text-xs px-3 py-1.5 rounded-md border ${format === v
                  ? 'bg-white border-[#338edc] text-[#338edc] font-semibold shadow-sm'
                  : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2">
          <span className="text-xs text-[#6B7280] w-24 pt-1">Detailtiefe</span>
          <div className="space-y-1.5">
            <label className="flex items-start gap-2 text-xs text-[#1A1A2E] cursor-pointer">
              <input type="radio" name="liq-detail" checked={detailtiefe === 'verdichtet'} onChange={() => setDetailtiefe('verdichtet')} className="mt-0.5" />
              <span><strong>Verdichtet</strong> — ohne Einzelposten und ohne Partnernamen (für Investoren)</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[#1A1A2E] cursor-pointer">
              <input type="radio" name="liq-detail" checked={detailtiefe === 'detail'} onChange={() => setDetailtiefe('detail')} className="mt-0.5" />
              <span><strong>Mit offenen Posten</strong> — je Partner zusammengefasst (für Bank / Wirtschaftsprüfer)</span>
            </label>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <span className="text-xs text-[#6B7280] w-24 pt-1">Abschnitte</span>
          <div className="space-y-1.5">
            {EXPORT_ABSCHNITTE.map(a => (
              <label key={a.key} className="flex items-start gap-2 text-xs text-[#1A1A2E] cursor-pointer">
                <input type="checkbox" checked={abschnitte.has(a.key)} onChange={() => toggleAbschnitt(a.key)} className="mt-0.5" />
                <span>{a.label}</span>
              </label>
            ))}
          </div>
        </div>

        {fehler && <div className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-lg">{fehler}</div>}

        <div className="flex items-center justify-end gap-2 pt-1">
          {laeuft && (
            <span className="flex items-center gap-2 text-xs text-[#6B7280] mr-auto">
              <span className="animate-spin h-3.5 w-3.5 border-2 border-[#338edc] border-t-transparent rounded-full" />
              {laeuft}
            </span>
          )}
          <button onClick={onClose} disabled={Boolean(laeuft)}
            className="text-xs px-3 py-1.5 rounded-md border bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]">
            Abbrechen
          </button>
          <button onClick={exportiere} disabled={Boolean(laeuft)}
            className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-60">
            Exportieren
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InvestorLiquiditaet() {
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [bis, setBis] = useState('');
  // #1860-Nachbesserung: Rückmeldung im BLICKFELD, nicht am Seitenanfang.
  const [hinweis, setHinweis] = useState(null);
  const [speichert, setSpeichert] = useState(false);
  const [vereinbarungFuer, setVereinbarungFuer] = useState(null);
  // #1860: Sammelvereinbarung — angehakte Belege (move_line_id → Zeile).
  const [sammelAuswahl, setSammelAuswahl] = useState({});
  const [monatsvergleich, setMonatsvergleich] = useState(null);
  const [fixkostenOffen, setFixkostenOffen] = useState(false);
  const [variableOffen, setVariableOffen] = useState(false);
  const [offeneAbstimmung, setOffeneAbstimmung] = useState(null);
  const [historie, setHistorie] = useState(null);
  const [rueckblick, setRueckblick] = useState('3m');
  const [offenerMonat, setOffenerMonat] = useState(heuteIso().slice(0, 7));
  const [alleKarten, setAlleKarten] = useState({ v: null, n: 0 });
  const [exportOffen, setExportOffen] = useState(false);
  // #1857: welche Ebene die Monatswerte liefert — Default Forecast.
  const [basis, setBasis] = useState('forecast');

  // #1851: Szenario-Modus — flüchtiger Overlay-Zustand, lebt NUR in
  // sessionStorage (Tab zu = Szenario weg). Kein Wert erreicht die DB, bis
  // die Übernahme ausdrücklich bestätigt wird.
  const [szenarioAktiv, setSzenarioAktiv] = useState(() => {
    try { return sessionStorage.getItem('liq_szenario_aktiv') === '1'; } catch { return false; }
  });
  const [szenarioWerte, setSzenarioWerte] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('liq_szenario_werte') || '{}') || {}; } catch { return {}; }
  });
  const [szenarioDaten, setSzenarioDaten] = useState(null);
  const [szenarioFehler, setSzenarioFehler] = useState(null);
  const [szenarioRechnet, setSzenarioRechnet] = useState(false);
  const [uebernahmeOffen, setUebernahmeOffen] = useState(false);
  useEffect(() => {
    try {
      sessionStorage.setItem('liq_szenario_aktiv', szenarioAktiv ? '1' : '0');
      sessionStorage.setItem('liq_szenario_werte', JSON.stringify(szenarioWerte));
    } catch { /* privater Modus */ }
  }, [szenarioAktiv, szenarioWerte]);
  /** Overlay als Liste — Schlüssel `jahr-monat|feld`, Wert in EUR. */
  const szenarioListe = useMemo(() => Object.entries(szenarioWerte).map(([k, wert_eur]) => {
    const [ym, feld] = k.split('|');
    const [jahr, monat] = ym.split('-').map(Number);
    return { jahr, monat, feld, wert_eur };
  }), [szenarioWerte]);
  const setSzenarioWert = useCallback((jahr, monat, feld, v) => {
    setSzenarioWerte(w => {
      const k = `${jahr}-${monat}|${feld}`;
      const n = { ...w };
      if (v === null || v === undefined) delete n[k]; else n[k] = v;
      return n;
    });
  }, []);
  // Das Szenario setzt IMMER auf dem Forecast auf (#1857) — beim Einschalten
  // wird die Basis erzwungen, der Umschalter ist solange gesperrt.
  const szenarioUmschalten = () => {
    setSzenarioAktiv(a => {
      if (!a) setBasis('forecast');
      return !a;
    });
  };

  // #1840 Teil B: IST-Kontostandslinie vor dem Startpunkt — eigener Endpoint,
  // die Seite bleibt ohne ihn nutzbar. `von` ist immer ein Monatserster.
  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const h = new Date();
        const von = rueckblick === 'jahr'
          ? `${h.getFullYear()}-01-01`
          : (() => {
              const d0 = new Date(h.getFullYear(), h.getMonth() - (rueckblick === '6m' ? 6 : 3), 1);
              return `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, '0')}-01`;
            })();
        const res = await fetch(`${API}/api/investor/liquiditaet/historie?von=${von}`, { credentials: 'include' });
        if (res.ok && aktiv) setHistorie(await res.json());
      } catch { /* Rückblick ist Zusatz — die Vorschau bleibt ohne ihn nutzbar */ }
    })();
    return () => { aktiv = false; };
  }, [rueckblick]);

  // #1839 Entscheidung 10: IST/PROGNOSE/SOLL je Monat — eigener Endpoint,
  // gecacht im Service; lädt nach jeder Änderung mit. #1857-BUG-Fix: das
  // Enddatum der Vorschau reist mit, sonst rechnet das Chart mit seinem
  // eigenen (kürzeren) Horizont und der letzte Monat sieht eingebrochen aus.
  const ladeMonatsvergleich = useCallback(async () => {
    try {
      const q = bis ? `?bis=${encodeURIComponent(bis)}` : '';
      const res = await fetch(`${API}/api/investor/liquiditaet/monatsvergleich${q}`, { credentials: 'include' });
      if (res.ok) setMonatsvergleich(await res.json());
    } catch { /* Vergleich ist Zusatz — die Seite bleibt ohne ihn nutzbar */ }
  }, [bis]);
  useEffect(() => { ladeMonatsvergleich(); }, [ladeMonatsvergleich]);

  const lade = useCallback(async (bisWert) => {
    setLaden(true); setFehler(null);
    try {
      const p = new URLSearchParams();
      if (bisWert) p.set('bis', bisWert);
      p.set('basis', basis);
      const res = await fetch(`${API}/api/investor/liquiditaet?${p}`, { credentials: 'include' });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Die Vorschau konnte nicht geladen werden');
      setDaten(j);
      setBis(b => b || j.bis);
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaden(false);
    }
  }, [basis]);

  // Neuladen bei Mount und bei Basis-Wechsel; das gewählte Enddatum bleibt.
  useEffect(() => { lade(bis || undefined); }, [lade]); // eslint-disable-line

  // #1851: Szenario rechnen — POST wegen der Nutzlast, der Server schreibt
  // dabei NICHTS (AK2). Dieselbe computeLiquiditaet, zweiter Parametersatz.
  useEffect(() => {
    if (!szenarioAktiv || !szenarioListe.length) {
      setSzenarioDaten(null); setSzenarioFehler(null);
      return undefined;
    }
    let aktiv = true;
    (async () => {
      setSzenarioRechnet(true); setSzenarioFehler(null);
      try {
        const res = await fetch(`${API}/api/investor/liquiditaet/szenario`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ bis: bis || undefined, basis: 'forecast', werte: szenarioListe }),
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j.error || 'Die Szenario-Rechnung ist fehlgeschlagen');
        if (aktiv) setSzenarioDaten(j);
      } catch (e) {
        if (aktiv) { setSzenarioFehler(e.message); setSzenarioDaten(null); }
      } finally {
        if (aktiv) setSzenarioRechnet(false);
      }
    })();
    return () => { aktiv = false; };
  }, [szenarioAktiv, szenarioListe, bis]);

  /**
   * Jede Änderung schreibt und lädt neu — die Kurve muss sofort mitziehen (AK5).
   *
   * #1860-Nachbesserung: `werfen` gibt den Fehlschlag an den Aufrufer zurück.
   * Vorher verschluckte diese Funktion JEDEN Fehler, der Dialog sah ein
   * erfülltes Promise, schloss sich — und die Meldung landete im Banner ganz
   * oben auf der Seite, gemessen zwei Bildschirmhöhen ausserhalb des Blickfelds.
   * Genau das war Manuels „kein Fehler, kein Hinweis, nichts passiert".
   */
  const schreibe = useCallback(async (pfad, body, methode = 'PUT', opt = {}) => {
    // #1851: solange das Szenario läuft, ist die Basis schreibgesperrt —
    // sonst wäre „nichts wird gespeichert" nicht mehr wahr.
    if (szenarioAktiv) {
      const m = 'Szenario aktiv — Änderungen an der Basis sind gesperrt. Erst übernehmen oder verwerfen.';
      setFehler(m);
      if (opt.werfen) throw new Error(m);
      return;
    }
    setSpeichert(true); setFehler(null);
    try {
      const res = await fetch(`${API}/api/investor/liquiditaet/${pfad}`, {
        method: methode,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body || {}),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Speichern fehlgeschlagen');
      await lade(bis);
      await ladeMonatsvergleich();
      if (opt.erfolg) setHinweis({ art: 'ok', text: opt.erfolg });
    } catch (e) {
      setFehler(e.message);
      setHinweis({ art: 'fehler', text: e.message });
      if (opt.werfen) throw e;
    } finally {
      setSpeichert(false);
    }
  }, [bis, lade, ladeMonatsvergleich, szenarioAktiv]);

  // Erfolg verschwindet von selbst, ein Fehlschlag bleibt stehen bis zum Wegklicken.
  useEffect(() => {
    if (hinweis?.art !== 'ok') return undefined;
    const t = setTimeout(() => setHinweis(null), 5000);
    return () => clearTimeout(t);
  }, [hinweis]);

  const setEinstellung = (schluessel, wert) => schreibe('einstellung', { schluessel, wert });

  const wochen = useMemo(() => {
    if (!daten?.kurve?.length) return [];
    const out = [];
    for (let i = 0; i < daten.kurve.length; i += 7) {
      const block = daten.kurve.slice(i, i + 7);
      const groeszter = block.reduce((a, t) =>
        (t.groeszter_posten && (!a || Math.abs(t.groeszter_posten.betrag_eur) > Math.abs(a.betrag_eur))
          ? t.groeszter_posten : a), null);
      out.push({
        von: block[0].datum,
        bis: block[block.length - 1].datum,
        zufluss: block.reduce((s, t) => s + t.zufluss, 0),
        abfluss: block.reduce((s, t) => s + t.abfluss, 0),
        ende: block[block.length - 1].erwartet,
        groeszter,
      });
    }
    return out;
  }, [daten]);

  // #1840 Teil B: IST-Tage (Vergangenheit) + Vorschau-Kurve (ab heute) in einer
  // Reihe. Der Anschlusspunkt liegt AM heutigen Tag — die Bruchstelle bleibt
  // als „heute"-Linie sichtbar, die Kurven werden nie vermischt.
  const kurveMitHistorie = useMemo(() => {
    if (!daten?.kurve?.length) return [];
    if (!historie?.ist_tage?.length) return daten.kurve;
    const vergangenheit = historie.ist_tage
      .filter(t => t.datum < daten.stand)
      .map(t => ({ datum: t.datum, ist: t.ist }));
    const kurve = daten.kurve.map((t, i) => (i === 0 ? { ...t, ist: historie.ist_ende_eur } : t));
    return [...vergangenheit, ...kurve];
  }, [daten, historie]);

  // #1851: die Szenario-Linie in DIESELBE Reihe mischen (Merge nach Datum) —
  // Tooltip und Achsen sehen beide Linien gemeinsam, die Basis bleibt unberührt.
  const kurveMitSzenario = useMemo(() => {
    if (!szenarioAktiv || !szenarioDaten?.kurve?.length) return kurveMitHistorie;
    const je = new Map(szenarioDaten.kurve.map(t => [t.datum, t.erwartet]));
    return kurveMitHistorie.map(t => (je.has(t.datum) ? { ...t, szenario_erwartet: je.get(t.datum) } : t));
  }, [kurveMitHistorie, szenarioAktiv, szenarioDaten]);

  // #1841 Teil D1: runde Achsenschritte statt Auto-Zoom-Krummwerten. Die Achse
  // reicht weiter bis Startsaldo/Kontorahmen (Entscheidung 9), nur gerundet.
  const kurveTicks = useMemo(() => {
    if (!daten?.kurve?.length) return null;
    let min = Math.min(0, daten.kontorahmen?.gesetzt_eur || 0);
    let max = daten.startsaldo_eur || 0;
    for (const t of kurveMitSzenario) {
      for (const k of ['ist', 'faellig', 'erwartet', 'szenario_erwartet']) {
        if (t[k] != null) { if (t[k] < min) min = t[k]; if (t[k] > max) max = t[k]; }
      }
    }
    return rundeTicks(min, max);
  }, [daten, kurveMitSzenario]);

  // AK1-Selbsttest: die IST-Linie muss exakt am Startsaldo der Vorschau enden —
  // beide Werte sind unabhängig berechnet, eine Abweichung wird ANGEZEIGT.
  const istAbweichung = historie && daten
    ? Math.round((historie.ist_ende_eur - daten.startsaldo_eur) * 100) / 100
    : 0;

  if (laden && !daten) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!daten) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center">
        <p className="text-sm text-[#1A1A2E]">Die Liquiditätsvorschau konnte nicht geladen werden.</p>
        {fehler && <p className="text-xs text-red-600 mt-2">{fehler}</p>}
        <button onClick={() => lade()} className="mt-4 text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium">
          Erneut versuchen
        </button>
      </div>
    );
  }

  const d = daten;
  const st = d.staleness || {};
  // #1859 (2): die Karenz für den Begriff „überfällig". EINE Quelle für alle
  // Karten, Tabellen und Hinweise auf dieser Seite.
  const kar = d.ueberfaelligkeits_luecke?.karenz || null;

  // #1861 AK6: EINE Stelle öffnet eine bestehende Vereinbarung zum Bearbeiten —
  // sie sucht die gedeckten Belege je nach Richtung auf der Forderungs- oder auf
  // der Verbindlichkeitsseite. Ein zweiter Aufbau würde beim Speichern Belege
  // verlieren, weil `move_line_ids` aus den übergebenen Zeilen neu entsteht.
  const oeffneVereinbarung = (u) => {
    const gedeckt = u.richtung === 'forderung'
      ? arZeilenNormalisiert(d).filter(z => z.vereinbarung?.id === u.id)
      : (d.verbindlichkeiten?.liste || []).filter(z => z.vereinbarung?.id === u.id);
    setVereinbarungFuer({
      modus: u.typ === 'frei' ? (u.frei_schluessel === 'ust_de' ? 'ust_de' : 'frei') : u.typ,
      richtung: u.richtung,
      vorhanden: u,
      zeile: u.typ === 'beleg' ? gedeckt[0] : undefined,
      zeilen: u.typ === 'glaeubiger' ? gedeckt : undefined,
      gesamt_eur: u.gesamt_eur,
      frei_schluessel: u.frei_schluessel,
    });
  };
  const stalenessStil = st.stufe === 'warnung'
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : st.stufe === 'hinweis'
      ? 'bg-[#FFFBEB] border-[#FDE68A] text-[#B45309]'
      : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]';

  return (
    <div className="space-y-5">
      {/* #1860-Nachbesserung: Rückmeldung liegt fest im Blickfeld. Das Banner
          am Seitenanfang war bei gescrollter Seite gemessen 2.139 px oberhalb
          des Fensters — praktisch unsichtbar. */}
      {hinweis && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-lg w-[calc(100%-2rem)]">
          <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border text-xs shadow-lg ${
            hinweis.art === 'ok'
              ? 'bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]'
              : 'bg-red-50 border-red-200 text-red-800'}`}>
            <span className="font-semibold shrink-0">{hinweis.art === 'ok' ? 'Gespeichert' : 'Nicht gespeichert'}</span>
            <span className="min-w-0 flex-1">{hinweis.text}</span>
            <button type="button" onClick={() => setHinweis(null)}
              className="shrink-0 text-current opacity-60 hover:opacity-100" aria-label="Meldung schließen">✕</button>
          </div>
        </div>
      )}

      {/* Kopf */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-[#1A1A2E]">Liquiditätsplanung</h1>
          <p className="text-xs text-[#6B7280] mt-1">
            Stand {datum(d.stand)} · Vorschau bis {datum(d.bis)} · Odoo wird ausschließlich gelesen.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {speichert && <span className="text-xs text-[#6B7280]">Speichere…</span>}
          <button onClick={() => setAlleKarten(s => ({ v: 'auf', n: s.n + 1 }))}
            className="text-[11px] px-2 py-1 rounded-md border bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280] hover:text-[#1A1A2E]">
            Alle aufklappen
          </button>
          <button onClick={() => setAlleKarten(s => ({ v: 'zu', n: s.n + 1 }))}
            className="text-[11px] px-2 py-1 rounded-md border bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280] hover:text-[#1A1A2E]">
            Alle zuklappen
          </button>
          {/* #1851: Export nur aus der Basis — im Szenario gesperrt, sonst
              trüge ein Bericht ungespeicherte Probewerte nach draußen. */}
          <button onClick={() => setExportOffen(true)} disabled={szenarioAktiv}
            title={szenarioAktiv ? 'Export ist im Szenario gesperrt — der Bericht kommt immer aus der Basis' : undefined}
            className={`text-[11px] px-2.5 py-1 rounded-md border font-medium ${szenarioAktiv
              ? 'bg-[#F8F9FC] border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
              : 'bg-white border-[#338edc] text-[#338edc] hover:bg-[#EFF6FF]'}`}>
            Export
          </button>
          {/* #1851: Schalter Szenario — Overlay-Zustand, nichts wird gespeichert */}
          <button onClick={szenarioUmschalten}
            title="Planwerte probeweise ändern — nichts wird gespeichert, bis Sie übernehmen"
            className={`text-[11px] px-2.5 py-1 rounded-md border font-medium ${szenarioAktiv
              ? 'bg-[#FFF7ED] border-[#EA580C] text-[#EA580C]'
              : 'bg-white border-[#E5E7EB] text-[#6B7280] hover:text-[#1A1A2E]'}`}>
            Szenario{szenarioAktiv ? ' AN' : ''}
          </button>
          {/* #1857: Umschalter Kurven-Basis — Default Forecast.
              #1851: im Szenario gesperrt, es setzt immer auf dem Forecast auf. */}
          <div className={`flex rounded-md border border-[#E5E7EB] overflow-hidden ${szenarioAktiv ? 'opacity-50' : ''}`}
            title={szenarioAktiv
              ? 'Im Szenario gesperrt — das Szenario setzt immer auf dem Forecast auf'
              : 'Welche Ebene die Monatswerte (Umsatz, Personal) liefert'}>
            {[['forecast', 'Forecast'], ['plan', 'Plan']].map(([k, l]) => (
              <button key={k} onClick={() => setBasis(k)} disabled={szenarioAktiv}
                className={`text-[11px] px-2.5 py-1 ${basis === k ? 'bg-white shadow-sm font-medium text-[#1A1A2E]' : 'bg-[#F8F9FC] text-[#6B7280]'}`}>
                {l}
              </button>
            ))}
          </div>
          <label className="text-xs text-[#6B7280]">Vorschau bis</label>
          <input
            type="date" value={bis ? String(bis).slice(0, 10) : ''}
            onChange={e => { setBis(e.target.value); lade(e.target.value); }}
            className="px-2 py-1 text-sm border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#338edc]"
          />
        </div>
      </div>

      {fehler && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-lg">{fehler}</div>
      )}

      {/* #1857: sichtbarer Hinweis, auf welcher Basis gerechnet wird */}
      <div className="text-xs px-3 py-2 rounded-lg border border-[#E5E7EB] bg-[#F8F9FC] text-[#6B7280]">
        {d.basis === 'plan'
          ? <>Diese Vorschau rechnet auf der <strong>eingefrorenen Plan-Basis</strong> — gepflegte Forecast-Werte werden bewusst ignoriert.</>
          : <>Diese Vorschau rechnet auf der <strong>Forecast-Basis</strong> <Marke art="FORECAST" /> — wo kein Forecast gepflegt ist, gilt der Plan. Umschalten auf „Plan" zeigt die eingefrorene Ausgangsrechnung.</>}
      </div>

      {/* #1851: Szenario-Banner — Zustand, Zähler, Übernehmen/Verwerfen. */}
      {szenarioAktiv && (
        <div className="flex items-center justify-between gap-3 flex-wrap text-xs px-3 py-2 rounded-lg border border-[#FED7AA] bg-[#FFF7ED] text-[#9A3412]">
          <span>
            <strong>Szenario aktiv — nicht gespeichert.</strong>{' '}
            {szenarioListe.length === 0
              ? 'Noch kein Wert geändert — Werte in „Planung je Monat" antippen.'
              : `${szenarioListe.length} ${szenarioListe.length === 1 ? 'Wert' : 'Werte'} probeweise geändert.`}
            {szenarioRechnet && ' Rechne…'}
            {' '}Schließen des Tabs verwirft alles.
          </span>
          <span className="flex items-center gap-2">
            <button onClick={() => setUebernahmeOffen(true)} disabled={!szenarioListe.length}
              className={`px-2.5 py-1 rounded-md border font-medium ${szenarioListe.length
                ? 'bg-[#EA580C] border-[#EA580C] text-white hover:bg-[#C2410C]'
                : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'}`}>
              Übernehmen…
            </button>
            <button onClick={() => setSzenarioWerte({})} disabled={!szenarioListe.length}
              className={`px-2.5 py-1 rounded-md border ${szenarioListe.length
                ? 'bg-white border-[#FED7AA] text-[#9A3412] hover:bg-[#FFF7ED]'
                : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'}`}>
              Verwerfen
            </button>
          </span>
        </div>
      )}
      {szenarioAktiv && szenarioFehler && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-lg">
          Szenario: {szenarioFehler}
        </div>
      )}

      {/* #1851: Delta-Kachel — Tiefpunkt Basis → Szenario + Kontorahmen-Bedarf. */}
      {szenarioAktiv && szenarioDaten && (
        <div className="bg-white border border-[#FED7AA] rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF] mb-2">
            Was ändert das Szenario? <Marke art="SZENARIO" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-[11px] text-[#6B7280]">Tiefster Punkt (Basis)</div>
              <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{eur(d.tiefster_punkt.erwartet_eur)}</div>
              <div className="text-[11px] text-[#6B7280]">am {datum(d.tiefster_punkt.datum)}</div>
            </div>
            <div>
              <div className="text-[11px] text-[#6B7280]">Tiefster Punkt (Szenario)</div>
              <div className="text-lg font-semibold text-[#EA580C] tabular-nums">{eur(szenarioDaten.tiefster_punkt.erwartet_eur)}</div>
              <div className="text-[11px] text-[#6B7280]">am {datum(szenarioDaten.tiefster_punkt.datum)}</div>
            </div>
            <div>
              {(() => {
                const dTief = Math.round((szenarioDaten.tiefster_punkt.erwartet_eur - d.tiefster_punkt.erwartet_eur) * 100) / 100;
                const dBedarf = Math.round((szenarioDaten.kontorahmen.bedarf_eur - d.kontorahmen.bedarf_eur) * 100) / 100;
                return (
                  <>
                    <div className="text-[11px] text-[#6B7280]">Delta Tiefpunkt</div>
                    <div className={`text-lg font-semibold tabular-nums ${dTief < 0 ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>
                      {dTief > 0 ? '+' : ''}{eur(dTief)}
                    </div>
                    <div className="text-[11px] text-[#6B7280]">
                      Kontorahmen-Bedarf: {eur(d.kontorahmen.bedarf_eur)} → {eur(szenarioDaten.kontorahmen.bedarf_eur)}
                      {' '}({dBedarf > 0 ? '+' : ''}{eur(dBedarf)})
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {exportOffen && !szenarioAktiv && <ExportDialog bis={bis || d.bis} basis={basis} onClose={() => setExportOffen(false)} />}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <Kpi
          titel="Liquide Mittel heute" wert={d.startsaldo_eur} marke="IST"
          meta={`${d.konten.length} ${d.konten.length === 1 ? 'Konto' : 'Konten'}${d.ruhend_ausgeblendet ? ` · ${d.ruhend_ausgeblendet} ${d.ruhend_ausgeblendet === 1 ? 'ruhendes Konto' : 'ruhende Konten'} ausgeblendet` : ''}`}
        />
        {/* #1859 (2): „überfällig" trägt jetzt eine Karenz. Sie steht AN der
            Kennzahl, nicht in einer Fußnote — sonst liest sich die Zahl weiter
            taggenau, und der Sprung am Tag nach dem Monats-Zahlungsziel bleibt
            unerklärlich (gemessen 08.09.: 123 Rechnungen über 184.615,17 €
            kippten über Nacht hinein). Der Betrag ist IST, die Karenz ANNAHME. */}
        <Kpi
          titel="Offene Forderungen" wert={d.forderungen.summe_eur} marke="IST"
          meta={`${eur(d.forderungen.ueberfaellig_eur, 2)} davon überfällig${
            kar?.tage ? ` (über ${kar.tage} Tage über Fälligkeit)` : ''}${
            d.zweifelhaft?.summe_eur > 0 ? ` · ${eur(d.zweifelhaft.summe_eur, 2)} zweifelhaft separat ausgewiesen` : ''}`}
          zusatz={<>
            {d.forderungen.in_karenz_eur > 0 && (
              <span className="block">In Karenz: {eur(d.forderungen.in_karenz_eur, 2)} aus {d.forderungen.in_karenz_belege}{' '}
                {d.forderungen.in_karenz_belege === 1 ? 'Beleg' : 'Belegen'} <Marke art="ANNAHME" />
                {kar?.ar_naechster_wechsel && <> — überfällig ab {datum(kar.ar_naechster_wechsel.datum)}</>}
              </span>
            )}
            {d.entwuerfe?.belege > 0 && (
              <span className="block">davon <strong>{d.entwuerfe.belege}</strong> Entwürfe über {eur(d.entwuerfe.brutto_eur, 2)} brutto,
                noch nicht gebucht <Marke art="ENTWURF" /></span>
            )}
          </>}
        />
        <Kpi
          titel="Offene Verbindlichkeiten" wert={d.verbindlichkeiten.summe_eur} marke="IST"
          meta={`${eur(d.verbindlichkeiten.ueberfaellig_eur, 2)} davon überfällig${
            kar?.tage ? ` (über ${kar.tage} Tage über Fälligkeit)` : ''}`}
          zusatz={<>
            {d.verbindlichkeiten.in_karenz_eur > 0 && (
              <span className="block">In Karenz: {eur(d.verbindlichkeiten.in_karenz_eur, 2)} aus {d.verbindlichkeiten.in_karenz_belege}{' '}
                {d.verbindlichkeiten.in_karenz_belege === 1 ? 'Beleg' : 'Belegen'} <Marke art="ANNAHME" />
                {kar?.ap_naechster_wechsel && <> — überfällig ab {datum(kar.ap_naechster_wechsel.datum)}</>}
              </span>
            )}
            {/* #1859 (3): die Zahl ist netto. Der Weg dorthin steht daneben,
                sonst wäre die Änderung gegenüber gestern unerklärlich. */}
            {d.verbindlichkeiten.gutschriften?.belege > 0 && (
              <span className="block">
                Rechnungsbestand {eur(d.verbindlichkeiten.summe_brutto_eur, 2)} abzüglich{' '}
                {eur(d.verbindlichkeiten.gutschriften.verrechnet_eur, 2)} verrechneter Gutschriften
              </span>
            )}
          </>}
        />
        {/* #1843 Punkt 5: was der Kurvenstart nicht mehr vorwegnimmt, steht hier. */}
        <Kpi
          titel="Überfälligkeits-Lücke" wert={d.ueberfaelligkeits_luecke.luecke_eur} marke="IST"
          meta={`${eur(d.ueberfaelligkeits_luecke.ar_ueberfaellig_eur, 2)} überfällige Forderungen − ${eur(d.ueberfaelligkeits_luecke.ap_ueberfaellig_eur, 2)} überfällige Verbindlichkeiten`}
          zusatz={<>
            <span className="block">Stand, wenn heute alles Überfällige beidseitig ausgeglichen würde: {eur(d.startsaldo_eur, 2)} + {eur(d.ueberfaelligkeits_luecke.luecke_eur, 2)}
              {' = '}<strong>{eur(d.ueberfaelligkeits_luecke.stand_wenn_bezahlt_eur, 2)}</strong></span>
            {kar?.tage > 0 && (
              <span className="block mt-1">{kar.aussage} <Marke art="ANNAHME" /></span>
            )}
          </>}
        />
        <Kpi
          titel="Tiefster Punkt" wert={d.tiefster_punkt.erwartet_eur} marke="PROGNOSE"
          meta={`am ${datum(d.tiefster_punkt.datum)} · laut erwartetem Zahlungsverhalten`}
        />
      </div>

      {/* Kurve */}
      <Card
        id="kurve" defaultOffen erzwinge={alleKarten}
        titel="Kontostandsverlauf"
        kopfzeile={`Tiefster Punkt ${eur(d.tiefster_punkt.erwartet_eur)} am ${datum(d.tiefster_punkt.datum)}`}
        hinweis={'Der Abstand zwischen beiden Linien zeigt, wie viel Liquidität allein durch Zahlungsverzug gebunden ist. Links der „heute"-Linie steht der echte Kontostand aus der Buchhaltung (IST).'}
        rechts={
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-[#6B7280] mr-1">Rückblick</span>
            {[['3m', '3 Monate'], ['6m', '6 Monate'], ['jahr', 'ab Jänner']].map(([w, l]) => (
              <button key={w} onClick={() => setRueckblick(w)}
                className={`px-2 py-1 text-[11px] rounded-md border ${rueckblick === w
                  ? 'bg-white border-[#338edc] text-[#338edc] font-medium shadow-sm'
                  : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'}`}>
                {l}
              </button>
            ))}
          </div>
        }
      >
        {/* AK1: eingebauter Selbsttest — geht die Naht nicht auf, steht es hier. */}
        {historie && Math.abs(istAbweichung) > 0.005 && (
          <div className="mb-3 text-xs px-3 py-2 rounded-lg border bg-amber-50 border-amber-200 text-amber-900">
            Selbsttest: Die IST-Linie endet bei {eur(historie.ist_ende_eur, 2)}, der Startsaldo der
            Vorschau ist {eur(d.startsaldo_eur, 2)} — Abweichung {eur(istAbweichung, 2)}.
            {historie.zukunft_gebucht_eur !== 0 && (
              <> Wahrscheinliche Ursache: {eur(historie.zukunft_gebucht_eur, 2)} sind mit Datum nach heute
              gebucht — im Startsaldo enthalten, in der Tageslinie erst später.</>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-4 text-[11px] text-[#6B7280] mb-3">
          <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 bg-[#1A1A2E] inline-block" />IST-Kontostand (Rückblick)</span>
          <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 bg-[#338edc] inline-block" />Laut erwartetem Zahlungsverhalten</span>
          <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 bg-[#6366F1] inline-block" style={{ borderTop: '2px dashed #6366F1', background: 'none' }} />Laut Fälligkeit</span>
          <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 bg-[#9CA3AF] inline-block" />Null-Linie</span>
          <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 inline-block" style={{ borderTop: '2px dashed #B45309' }} />Kontorahmen</span>
          <span className="flex items-center gap-1.5"><i className="w-2 h-2 rounded-full bg-[#DC2626] inline-block" />Tiefster Punkt</span>
          {szenarioAktiv && szenarioDaten && (
            <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 bg-[#EA580C] inline-block" />Szenario (nicht gespeichert)</span>
          )}
        </div>
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={kurveMitSzenario} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="datum" tickFormatter={kurzDatum} tick={{ fontSize: 11, fill: '#9CA3AF' }}
                axisLine={{ stroke: '#E5E7EB' }} tickLine={false} minTickGap={28} />
              {/* Entscheidung 9: die Y-Achse reicht immer bis zum Startsaldo,
                  sonst versteckt Auto-Zoom die Fallhöhe der Kurve.
                  #1841 Teil D1: runde Schritte statt krummer Auto-Werte. */}
              <YAxis tickFormatter={v => eur(v)} tick={{ fontSize: 11, fill: '#9CA3AF' }}
                axisLine={false} tickLine={false} width={92}
                ticks={kurveTicks?.ticks}
                domain={kurveTicks ? [kurveTicks.von, kurveTicks.bis] : [
                  min => Math.min(min, d.kontorahmen.gesetzt_eur, 0),
                  max => Math.max(max, d.startsaldo_eur),
                ]} />
              <Tooltip
                formatter={(v, n) => [eur(v, 2), n]}
                labelFormatter={l => {
                  // #1843 Punkt 6: am Stichtag tragen beide Vorschaulinien den
                  // Startsaldo — Tag 0 hat keine Ströme mehr.
                  if (l === d.stand) return `${datum(l)} · heute, alle Linien auf dem Startsaldo`;
                  const tag = d.kurve.find(t => t.datum === l);
                  return `${datum(l)}${tag?.groeszter_posten ? ` · ${tag.groeszter_posten.text}` : ''}`;
                }}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }}
              />
              <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
              {d.kontorahmen.gesetzt_eur < 0 && (
                <ReferenceLine y={d.kontorahmen.gesetzt_eur} stroke="#B45309" strokeDasharray="4 3" />
              )}
              {/* #1840 Teil B: die Bruchstelle zwischen IST und Vorschau ist sichtbar. */}
              {/* #1841 Teil D2: das Label liegt IN der Zeichenfläche, sonst
                  schneidet der Chart-Rand es ab. */}
              {historie && (
                <ReferenceLine x={d.stand} stroke="#9CA3AF" strokeDasharray="2 2"
                  label={{ value: 'heute', position: 'insideTop', fontSize: 10, fill: '#6B7280', dy: 4 }} />
              )}
              {/* #1841 Teil D3: beim langen Rückblick tritt die IST-Linie
                  zurück, damit die Vorschau lesbar bleibt. */}
              <Line type="monotone" dataKey="ist" name="IST-Kontostand"
                stroke={rueckblick === 'jahr' ? '#9CA3AF' : '#1A1A2E'}
                strokeWidth={rueckblick === 'jahr' ? 1 : 1.5} dot={false} />
              <Line type="monotone" dataKey="faellig" name="Laut Fälligkeit" stroke="#6366F1"
                strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
              <Line type="monotone" dataKey="erwartet" name="Laut erwartetem Zahlungsverhalten" stroke="#338edc"
                strokeWidth={2} dot={false} />
              {/* #1851: zweite Linie — dasselbe Modell mit Szenario-Overlay.
                  Die Fälligkeitslinie bleibt bewusst Basis-only. */}
              {szenarioAktiv && szenarioDaten && (
                <Line type="monotone" dataKey="szenario_erwartet" name="Szenario" stroke="#EA580C"
                  strokeWidth={2} dot={false} />
              )}
              {/* Entscheidung 9: der Tiefpunkt ist die Kernaussage der Kurve. */}
              <ReferenceDot x={d.tiefster_punkt.datum} y={d.tiefster_punkt.erwartet_eur}
                r={4} fill="#DC2626" stroke="#fff" strokeWidth={1.5} />
              {szenarioAktiv && szenarioDaten && (
                <ReferenceDot x={szenarioDaten.tiefster_punkt.datum} y={szenarioDaten.tiefster_punkt.erwartet_eur}
                  r={4} fill="#EA580C" stroke="#fff" strokeWidth={1.5} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

      </Card>

      {/* #1859: Executive Summary — farbig, direkt unter dem Kurvendiagramm, nie einklappbar (AK1). */}
      {d.zusammenfassung && (
        <ZusammenfassungKarte z={d.zusammenfassung} szenarioAktiv={szenarioAktiv}
          speichert={speichert} schreibe={schreibe} />
      )}

      {/* #1840 Teil C: Monats-Brücke — Anfang + benannte Ströme = Ende, je Monat.
          #1841 Teil E1: die Abstimmung zum Tiefpunkt lebt jetzt HIER — beide
          Tabellen sagen „Anfang + Ströme = Ende", zwei Vollkarten wären doppelt. */}
      {((historie?.monate?.length || 0) + (d.monatsbruecken?.length || 0) > 0) && (
        <Card
          id="bruecke" erzwinge={alleKarten}
          titel="Monats-Brücke und Abstimmung"
          kopfzeile={d.abstimmung
            ? `Startsaldo ${eur(d.abstimmung.startsaldo_eur)} → Tiefpunkt ${eur(d.abstimmung.endsaldo_eur)} am ${datum(d.abstimmung.bis)} — jede Brücke geht exakt auf`
            : 'Anfangssaldo plus benannte Ströme = Endsaldo, je Monat'}
          hinweis="Anfangssaldo plus benannte Ströme ergibt exakt den Endsaldo — je Monat bis zum Horizontende. Vergangene Monate kommen aus den echten Bankbewegungen (IST, gleiche Rechnung wie der Monatsbericht), künftige aus dem Modell (PROGNOSE). Jede Zeile lässt sich zu ihren Einzelposten aufklappen."
        >
          {/* Entscheidung 10: Abstimmung — Startsaldo + benannte Ströme = Tiefpunkt. */}
          {d.abstimmung && (
            <div className="mb-5">
              <h3 className="text-xs font-semibold text-[#1A1A2E] mb-1">
                Abstimmung zum tiefsten Punkt ({datum(d.abstimmung.bis)})
              </h3>
              <p className="text-[11px] text-[#6B7280] mb-3 max-w-3xl">
                Jede Zeile ist ein benannter Strom bis zum Tiefpunkt der Kurve. Startsaldo plus alle
                Ströme ergeben exakt den Kurvenwert — geht die Tabelle nicht auf, ist das Modell falsch.
              </p>
              <table className="w-full text-sm max-w-2xl">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                    <th className="pb-1.5 font-semibold">Position</th>
                    <th className="pb-1.5 font-semibold text-right">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[#F3F4F6]">
                    <td className="py-1.5 text-[#1A1A2E]">Startsaldo <Marke art="IST" /></td>
                    <td className="py-1.5 text-right text-[#1A1A2E] tabular-nums">{eur(d.abstimmung.startsaldo_eur, 2)}</td>
                  </tr>
                  {/* #1843 AK9: auch hier bis zum Einzelposten aufklappbar —
                      dieselbe Ereignisliste wie die Brücke, keine zweite Rechnung. */}
                  {d.abstimmung.zeilen.map(z => (
                    <Fragment key={z.kategorie}>
                      <tr className="border-b border-[#F3F4F6]">
                        <td className="py-1.5 text-[#4B5563]">
                          {z.posten?.length > 0 ? (
                            <button
                              onClick={() => setOffeneAbstimmung(o => (o === z.kategorie ? null : z.kategorie))}
                              className="hover:underline text-left">
                              <span className="text-[#9CA3AF] mr-1">{offeneAbstimmung === z.kategorie ? '▾' : '▸'}</span>
                              {z.label}
                            </button>
                          ) : z.label}
                          {' '}<Marke art={z.herkunft} />
                        </td>
                        <td className={`py-1.5 text-right tabular-nums ${z.betrag_eur < 0 ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>
                          {eur(z.betrag_eur, 2)}
                        </td>
                      </tr>
                      {offeneAbstimmung === z.kategorie && z.posten?.map((p, i) => (
                        <tr key={i} className="border-b border-[#F3F4F6] bg-[#F8F9FC]">
                          <td className="py-1 pl-6 text-[11px] text-[#6B7280]">
                            {p.text}
                            <span className="ml-2 text-[10px] text-[#9CA3AF]">{zahltagText(p)}</span>
                          </td>
                          <td className="py-1 text-right text-[11px] text-[#6B7280] tabular-nums">{eur(p.betrag_eur, 2)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  <tr>
                    <td className="py-1.5 font-semibold text-[#1A1A2E]">
                      Saldo am {datum(d.abstimmung.bis)} <Marke art="PROGNOSE" />
                    </td>
                    <td className="py-1.5 text-right font-semibold text-[#1A1A2E] tabular-nums">
                      {eur(d.abstimmung.endsaldo_eur, 2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <h3 className="text-xs font-semibold text-[#1A1A2E] mb-2">Brücke je Monat</h3>
          <div className="space-y-2">
            {[...(historie?.monate || []), ...(d.monatsbruecken || [])].map(m => (
              <BrueckenMonat
                key={m.monat}
                m={m}
                stand={d.stand}
                offen={offenerMonat === m.monat}
                onToggle={() => setOffenerMonat(o => (o === m.monat ? null : m.monat))}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Entscheidung 10: Monatsvergleich IST / PROGNOSE / SOLL.
          #1841 Teil C: SOLL auch für vergangene Monate, gleiche Farbe je Quelle
          oben wie unten (das Vorzeichen sagt die Richtung), der laufende Monat
          stapelt IST bis heute + Prognose-Rest, die Abweichung steht im Tooltip. */}
      {monatsvergleich?.monate?.length > 0 && (
        <Card
          id="monatsvergleich" erzwinge={alleKarten}
          titel="Monatsvergleich: IST · PROGNOSE · SOLL"
          kopfzeile={`${monatsvergleich.monate.length} Monate — Bankbewegung gegen Modell und Plan`}
          hinweis={'Vergangene Monate zeigen die echten Bankbewegungen (IST) neben dem Plan (SOLL), künftige die Modellrechnung (PROGNOSE) neben dem Plan. Einzahlungen nach oben, Auszahlungen nach unten — je Quelle dieselbe Farbe. Der laufende Monat stapelt IST bis heute und den Prognose-Rest in einem Balken.'}
        >
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={monatsvergleich.monate.map(m => ({
                  monat: m.monat,
                  teilmonat: m.teilmonat || false,
                  ist_ein: m.ist_ein_eur ?? null,
                  prognose_ein: m.prognose_ein_eur ?? null,
                  soll_ein: m.soll_ein_eur ?? null,
                  ist_aus: m.ist_aus_eur != null ? -m.ist_aus_eur : null,
                  prognose_aus: m.prognose_aus_eur != null ? -m.prognose_aus_eur : null,
                  soll_aus: m.soll_aus_eur != null ? -m.soll_aus_eur : null,
                }))}
                margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
              >
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="monat" tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis tickFormatter={v => eur(v)} tick={{ fontSize: 11, fill: '#9CA3AF' }}
                  axisLine={false} tickLine={false} width={92} />
                <Tooltip content={<MonatsvergleichTooltip />} />
                <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} />
                {/* Ein Balken je Monat und Richtung: IST + Prognose-Rest gestapelt. */}
                <Bar dataKey="ist_ein" name="IST Einzahlungen" stackId="ein" fill="#338edc" />
                <Bar dataKey="prognose_ein" name="PROGNOSE Einzahlungen" stackId="ein" fill="#BE185D" />
                <Bar dataKey="soll_ein" name="SOLL Einzahlungen" stackId="soll" fill="#6366F1" />
                <Bar dataKey="ist_aus" name="IST Auszahlungen" stackId="ein" fill="#338edc" />
                <Bar dataKey="prognose_aus" name="PROGNOSE Auszahlungen" stackId="ein" fill="#BE185D" />
                <Bar dataKey="soll_aus" name="SOLL Auszahlungen" stackId="soll" fill="#6366F1" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-4 text-[11px] text-[#6B7280] mt-3">
            <span className="flex items-center gap-1.5"><i className="w-3 h-3 bg-[#338edc] inline-block rounded-sm" />IST (Bankbewegung, gleiche Rechnung wie der Monatsbericht)</span>
            <span className="flex items-center gap-1.5"><i className="w-3 h-3 bg-[#BE185D] inline-block rounded-sm" />PROGNOSE (dieses Modell)</span>
            <span className="flex items-center gap-1.5"><i className="w-3 h-3 bg-[#6366F1] inline-block rounded-sm" />SOLL (Planrechnung)</span>
            <span>Nach oben = Einzahlungen, nach unten = Auszahlungen.</span>
          </div>
        </Card>
      )}

      {/* Kontorahmen — laut Reihenfolge E4 direkt nach Brücke/Vergleich, Default offen */}
      <Card
        id="kontorahmen" defaultOffen erzwinge={alleKarten}
        titel="Kontorahmen"
        kopfzeile={d.kontorahmen.unterschritten_am
          ? `Wird am ${datum(d.kontorahmen.unterschritten_am)} unterschritten`
          : 'Im gewählten Zeitraum nicht unterschritten'}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <span className="text-[11px] text-[#6B7280] block mb-1">
              Gesetzte Linie{' '}
              {d.kontorahmen.gesetzt_eur
                ? <Marke art="MANUELL" />
                : <span className="text-[10px] text-[#9CA3AF]">nicht gesetzt</span>}
            </span>
            <ZahlFeld
              wert={Math.abs(d.kontorahmen.gesetzt_eur) || ''}
              placeholder="0"
              suffix="EUR"
              onSave={v => setEinstellung('kontorahmen_eur', Math.abs(Number(v) || 0))}
            />
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <div>
                <div className="text-[#1A1A2E]">Faustregel</div>
                <div className="text-[11px] text-[#6B7280]">½ Ø-Monatsumsatz · {d.faustregel.zeitraum_text}</div>
              </div>
              <div className="text-[#1A1A2E] tabular-nums font-medium">{eur(d.kontorahmen.faustregel_eur)}</div>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <div>
                <div className="text-[#1A1A2E]">Rechnerischer Bedarf</div>
                <div className="text-[11px] text-[#6B7280]">tiefster Punkt + 20 % Puffer</div>
              </div>
              <div className="text-[#1A1A2E] tabular-nums font-medium">{eur(d.kontorahmen.bedarf_eur)}</div>
            </div>
          </div>
          <div>
            <div className={`text-xs px-3 py-2 rounded-lg border ${
              d.kontorahmen.unterschritten_am
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'}`}>
              {d.kontorahmen.unterschritten_am
                ? <>Die Linie wird am <strong>{datum(d.kontorahmen.unterschritten_am)}</strong> unterschritten
                    {d.kontorahmen.unterschritten_in_tagen > 0 && <> — in {d.kontorahmen.unterschritten_in_tagen} Tagen</>}.</>
                : <>Die Linie wird im gewählten Zeitraum nicht unterschritten.</>}
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-3">
              Der rechnerische Bedarf entsteht aus euren eigenen Zahlen und ist damit gegenüber einer Bank
              begründbar — die Faustregel ist nur ein Erfahrungswert.
            </p>
          </div>
        </div>
      </Card>

      {/* Konten + Abgleich — eingeklappt (E4) */}
      <Card
        id="konten" erzwinge={alleKarten}
        titel="Liquide Mittel je Konto"
        kopfzeile={`${eur(d.startsaldo_eur, 2)} auf ${d.konten.length} ${d.konten.length === 1 ? 'Konto' : 'Konten'}${d.ruhend_ausgeblendet ? ` · ${d.ruhend_ausgeblendet} ruhende ausgeblendet` : ''}`}
        hinweis="Der Startsaldo der Vorschau ist die Summe dieser Konten zum letzten Abgleich."
        rechts={
          <div className={`text-[11px] px-2.5 py-1.5 rounded-lg border ${stalenessStil}`}>
            Abgeglichen {st.last_refresh ? datum(st.last_refresh) : '–'}
            {st.alter_stunden != null && <> · vor {st.alter_stunden} h</>}
            {st.stufe === 'warnung' && <> · <strong>Daten sind veraltet</strong></>}
            {st.stufe === 'hinweis' && <> · länger kein Abgleich</>}
          </div>
        }
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
              <th className="pb-2 font-semibold">Konto</th>
              <th className="pb-2 font-semibold">IBAN</th>
              <th className="pb-2 font-semibold text-right">Saldo</th>
              <th className="pb-2 font-semibold text-right">Abgeglichen</th>
            </tr>
          </thead>
          <tbody>
            {d.konten.map(k => (
              <tr key={k.code} className="border-b border-[#F3F4F6] last:border-0">
                <td className="py-2">
                  <span className="text-[#1A1A2E]">{k.name}</span>
                  <span className="text-[11px] text-[#9CA3AF] ml-2">{k.code}</span>
                  {k.name_weicht_ab && (
                    <span className="ml-2 text-[10px] text-[#B45309]" title={`In Odoo: ${k.odoo_name}`}>
                      abweichend benannt
                    </span>
                  )}
                </td>
                <td className="py-2 text-[11px] text-[#6B7280] font-mono">{k.iban || '–'}</td>
                <td className="py-2 text-right text-[#1A1A2E] tabular-nums">{eur(k.saldo_eur, 2)}</td>
                <td className="py-2 text-right text-[11px] text-[#6B7280]">{k.last_sync ? datum(k.last_sync) : '–'}</td>
              </tr>
            ))}
            <tr>
              <td className="pt-2 font-semibold text-[#1A1A2E]" colSpan={2}>Startsaldo</td>
              <td className="pt-2 text-right font-semibold text-[#1A1A2E] tabular-nums">{eur(d.startsaldo_eur, 2)}</td>
              <td />
            </tr>
          </tbody>
        </table>
        {d.ruhend_ausgeblendet > 0 && (
          <p className="text-[11px] text-[#9CA3AF] mt-3">
            {d.ruhend_ausgeblendet} {d.ruhend_ausgeblendet === 1 ? 'ruhendes Konto' : 'ruhende Konten'} ausgeblendet
            — ohne Bewegung in den letzten {d.einstellungen?.ruhend_monate || 12} Monaten.
            {' '}{d.ruhend_ausgeblendet === 1 ? 'Es bleibt' : 'Sie bleiben'} in Odoo erhalten.
          </p>
        )}
      </Card>

      {/* Wochen — eingeklappt (E4) */}
      <Card
        id="wochen" erzwinge={alleKarten}
        titel="Größte Bewegungen je Woche"
        kopfzeile={`${wochen.length} Wochen — Zufluss, Abfluss, Saldo und größter Posten`}
      >
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                  <th className="pb-2 font-semibold">Woche</th>
                  <th className="pb-2 font-semibold text-right">Zufluss</th>
                  <th className="pb-2 font-semibold text-right">Abfluss</th>
                  <th className="pb-2 font-semibold text-right">Saldo Ende</th>
                  <th className="pb-2 font-semibold">Größter Posten</th>
                </tr>
              </thead>
              <tbody>
                {wochen.map(w => (
                  <tr key={w.von} className="border-b border-[#F3F4F6] last:border-0">
                    <td className="py-2 text-[#4B5563] whitespace-nowrap">{kurzDatum(w.von)}–{kurzDatum(w.bis)}</td>
                    <td className="py-2 text-right text-[#1A1A2E] tabular-nums">{eur(w.zufluss)}</td>
                    <td className="py-2 text-right text-[#1A1A2E] tabular-nums">{eur(-w.abfluss)}</td>
                    <td className="py-2 text-right text-[#1A1A2E] tabular-nums font-medium">{eur(w.ende)}</td>
                    <td className="py-2 text-[11px] text-[#6B7280]">
                      {w.groeszter ? `${w.groeszter.text} · ${eur(w.groeszter.betrag_eur)}` : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
      </Card>

      {/* Forderungen */}
      <Card
        id="forderungen" erzwinge={alleKarten}
        titel="Offene Forderungen und Zahlungsverhalten"
        kopfzeile={`${eur(d.forderungen.summe_eur, 2)} offen · ${eur(d.forderungen.ueberfaellig_eur, 2)} überfällig · ${d.forderungen.liste.length} Kunden`}
        hinweis={`Der Überzug ist der Median aus der Zahlungshistorie — ein einzelner Ausreißer würde den Mittelwert unbrauchbar machen. Über ${d.forderungen.grenze_tage} Tagen Median wird kein Erwartungswert unterstellt: die Position läuft nur in der Linie „laut Fälligkeit", bis hier ein Wert gesetzt wird. Ohne eigene Historie (unter ${d.forderungen.min_belege} Belegen) greift der globale gemessene Kunden-Median als gekennzeichnete Annahme.`}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB] [&>th]:sticky [&>th]:top-0 [&>th]:bg-white">
              <th className="pb-2 font-semibold">Kunde</th>
              <th className="pb-2 font-semibold text-right">Offen</th>
              <th className="pb-2 font-semibold text-right">Fällig ab</th>
              <th className="pb-2 font-semibold text-right">Überzug (Median)</th>
              <th className="pb-2 font-semibold text-right">Überzug angewandt</th>
              <th className="pb-2 font-semibold">Grundlage</th>
              <th className="pb-2 font-semibold">Zahlungserwartung</th>
            </tr>
          </thead>
          <tbody>
            {d.forderungen.liste.map(f => (
              <tr key={f.partner_id} className="border-b border-[#F3F4F6] last:border-0">
                <td className="py-2 text-[#1A1A2E]">
                  {f.kunde}
                  <span className="text-[11px] text-[#9CA3AF] ml-2">{f.belege} Beleg{f.belege === 1 ? '' : 'e'}</span>
                </td>
                <td className="py-2 text-right text-[#1A1A2E] tabular-nums">{eur(f.offen_eur, 2)}</td>
                <td className="py-2 text-right text-[11px] text-[#6B7280] whitespace-nowrap">
                  {datum(f.faellig_am)}
                  {f.ueberfaellig_eur > 0 && (
                    <span className="block text-[#B45309]">{eur(f.ueberfaellig_eur)} überfällig</span>
                  )}
                </td>
                <td className="py-2 text-right text-[#4B5563] tabular-nums">
                  {f.median_tage === null ? '–' : `${f.median_tage} T`}
                  <span className="block text-[10px] text-[#9CA3AF]">aus {f.historie_belege} Beleg{f.historie_belege === 1 ? '' : 'en'}</span>
                </td>
                <td className="py-2 text-right">
                  <ZahlFeld
                    wert={f.ueberzug_manuell}
                    breite="w-20" suffix="T"
                    placeholder={f.in_erwartet ? String(f.angewandt_tage) : '—'}
                    onSave={v => schreibe('ueberzug', {
                      partner_id: f.partner_id, partner_name: f.kunde, ueberzug_tage: v,
                    })}
                  />
                </td>
                <td className="py-2">
                  {f.grundlage === 'manuell' && <><Marke art="MANUELL" /> <span className="text-[11px] text-[#6B7280]">gesetzt</span></>}
                  {f.grundlage === 'historie' && <span className="text-[11px] text-[#6B7280]">gemessener Median</span>}
                  {f.grundlage === 'ueber_grenze' && (
                    <span className="text-[11px] text-[#B45309]" title={`Median über ${d.forderungen.grenze_tage} Tagen`}>
                      nur laut Fälligkeit
                    </span>
                  )}
                  {f.grundlage === 'global_annahme' && (
                    <><Marke art="ANNAHME" />{' '}
                    <span className="text-[11px] text-[#6B7280]"
                      title={`Weniger als ${d.forderungen.min_belege} eigene Belege — globaler gemessener Kunden-Median`}>
                      globaler Median
                    </span></>
                  )}
                </td>
                {/* #1861: die Zahlungserwartung ist nicht auf zweifelhafte Posten
                    beschränkt — derselbe Mechanismus greift für jede Forderung. */}
                <td className="py-2">
                  {(() => {
                    const ohne = (f.zeilen || []).filter(z => !z.vereinbarung);
                    return (
                      <span className="flex items-center gap-2 text-[11px] text-[#6B7280]">
                        {ohne.length > 0 ? (
                          <button
                            onClick={() => setVereinbarungFuer({
                              modus: 'glaeubiger', richtung: 'forderung',
                              zeilen: ohne.map(z => arZeileNorm(z, f.kunde, f.partner_id)),
                              gesamt_eur: Math.round(ohne.reduce((s, z) => s + z.betrag_eur, 0) * 100) / 100,
                            })}
                            title={`Eine Erwartung für alle ${ohne.length} offenen Belege dieses Kunden setzen`}
                            className="px-2 py-1 rounded-md border border-[#E5E7EB] text-[#338edc] hover:border-[#338edc] whitespace-nowrap">
                            Erwartung setzen
                          </button>
                        ) : (
                          <span className="text-[#9CA3AF]">alle Belege gesetzt</span>
                        )}
                        {f.vereinbart_eur > 0 && (
                          <span className="whitespace-nowrap"><Marke art="VEREINBARUNG" /> {eur(f.vereinbart_eur)}</span>
                        )}
                        {f.uneinbringlich_eur > 0 && (
                          <span className="text-[#B91C1C] whitespace-nowrap">{eur(f.uneinbringlich_eur)} uneinbringlich</span>
                        )}
                      </span>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.forderungen.ohne_erwartung_eur > 0 && (
          <p className="text-[11px] text-[#B45309] mt-3">
            {eur(d.forderungen.ohne_erwartung_eur, 2)} laufen nur in der Linie „laut Fälligkeit" —
            für diese Kunden liegt kein belastbares Zahlungsverhalten vor. Ein hier gesetzter Wert nimmt sie
            in die erwartete Kurve auf.
          </p>
        )}
        {d.entwuerfe?.belege > 0 && <EntwurfsBlock e={d.entwuerfe} />}
      </Card>

      {/* Entscheidung 6: zweifelhafte Altforderungen — in KEINER Kurve.
          #1841 Teil B: je Kunde gruppiert, Kleinstbeträge als Sammelzeile. */}
      {d.zweifelhaft?.liste?.length > 0 && (
        <Card
          id="zweifelhaft" erzwinge={alleKarten}
          titel="Zweifelhafte Altforderungen"
          kopfzeile={`${eur(d.zweifelhaft.summe_eur, 2)} offen · ${eur(d.zweifelhaft.verbunden_eur)} bei verbundenen Unternehmen${
            d.zweifelhaft.vereinbart_eur > 0 ? ` · ${eur(d.zweifelhaft.vereinbart_eur)} mit Zahlungserwartung` : ''}${
            d.zweifelhaft.uneinbringlich_eur > 0 ? ` · ${eur(d.zweifelhaft.uneinbringlich_eur)} uneinbringlich` : ''}${
            d.zweifelhaft.zurueckgeholt_eur > 0 ? ` · ${eur(d.zweifelhaft.zurueckgeholt_eur)} zurückgeholt` : ''}`}
          hinweis={`Über ${d.zweifelhaft.grenze_tage} Tage überfällig — diese Posten liegen bewusst in keiner Kurve: wer seit einem halben Jahr nicht zahlt, ist kein planbarer Zufluss. Eine Zahlungserwartung (Einmalzahlung, Ratenplan oder Sammelvereinbarung je Kunde) holt den Posten an seinen Terminen in die Kurve zurück; „uneinbringlich" hält ihn dauerhaft aus beiden Kurven und weist ihn eigens aus. Verbundene Unternehmen sind gekennzeichnet.`}
          rechts={
            <div className="text-right text-[11px] text-[#6B7280]">
              <div className="text-lg font-semibold text-[#1A1A2E] tabular-nums">{eur(d.zweifelhaft.summe_eur, 2)}</div>
              <div>{eur(d.zweifelhaft.verbunden_eur)} bei verbundenen Unternehmen</div>
              {d.zweifelhaft.vereinbart_eur > 0 && (
                <div>{eur(d.zweifelhaft.vereinbart_eur)} mit Zahlungserwartung in der Kurve</div>
              )}
              {d.zweifelhaft.zurueckgeholt_eur > 0 && (
                <div>{eur(d.zweifelhaft.zurueckgeholt_eur)} zurückgeholt in die Kurve</div>
              )}
            </div>
          }
        >
          {/* #1861 AK2: die Kennzeichnung ist eine PLANUNGSANGABE — sie bucht
              nichts. Odoo bleibt unverändert, die Wertberichtigung gehört in
              die Buchhaltung. Der Satz steht dort, wo die Summe steht. */}
          {d.zweifelhaft.uneinbringlich_eur > 0 && (
            <div className="mb-3 rounded border border-[#FECACA] bg-[#FEF2F2] px-3 py-2">
              <div className="text-sm font-semibold text-[#1A1A2E] tabular-nums">
                Davon uneinbringlich gekennzeichnet: {eur(d.zweifelhaft.uneinbringlich_eur, 2)}
                {d.zweifelhaft.uneinbringlich_verbunden_eur > 0 && (
                  <span className="font-normal text-[#6B7280]">
                    {' '}· davon {eur(d.zweifelhaft.uneinbringlich_verbunden_eur, 2)} bei verbundenen Unternehmen
                  </span>
                )}
              </div>
              <div className="mt-1 text-[11px] text-[#6B7280]">
                Bleibt dauerhaft aus beiden Kurven. Die Kennzeichnung ist eine Planungsangabe —
                die Wertberichtigung ist in der Buchhaltung separat zu buchen; hier wird nichts nach Odoo geschrieben.
              </div>
            </div>
          )}
          <ZweifelhaftGruppen
            liste={d.zweifelhaft.liste}
            bagatellGrenze={Number(d.einstellungen?.zweifelhaft_bagatell_eur ?? 5)}
            schreibe={schreibe}
            oeffneErwartung={setVereinbarungFuer}
            bearbeite={id => {
              const u = d.vereinbarungen?.liste?.find(x => x.id === id);
              if (u) oeffneVereinbarung(u);
            }}
          />
        </Card>
      )}

      {/* Verbindlichkeiten */}
      <Card
        id="verbindlichkeiten" erzwinge={alleKarten}
        titel="Offene Verbindlichkeiten"
        kopfzeile={`${eur(d.verbindlichkeiten.summe_eur, 2)} offen · ${eur(d.verbindlichkeiten.ueberfaellig_eur, 2)} überfällig${kar?.tage ? ` (über ${kar.tage} Tage)` : ''} · ${d.verbindlichkeiten.liste.length} Belege`}
        hinweis={`Ohne Vereinbarung wird eine Rechnung nicht am Papiertermin erwartet, sondern nach dem gemessenen Zahlverhalten gegenüber dem Lieferanten (Rechnungsdatum + Median). Läge dieser Termin in der Vergangenheit, wird derselbe Lieferanten-Median ab heute gerechnet, mindestens aber ${d.regeln.lieferanten_verzug.wert.ueberfaellig_min_tage} Tage — als gekennzeichnete Annahme. So verteilen sich überfällige Rechnungen nach Lieferant statt an einem einzigen Stichtag zusammenzufallen. Stundungen und Ratenpläne liegen in unserer eigenen Tabelle und überleben jeden Odoo-Abgleich.`}
      >
        {/* #1859 (2)+(3): die beiden Regeln, die diese Zahl formen, stehen ÜBER
            der Liste — sonst sucht man den Unterschied zum Odoo-Saldo in der
            Tabelle. Die Karenz ist einstellbar; sie verändert nur das Etikett
            „überfällig", nie die Kurve. */}
        <div className="mb-3 px-3 py-2 bg-[#F8F9FC] border border-[#E5E7EB] rounded-md text-xs space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[#4B5563]">Als überfällig gilt ein Posten erst</span>
            <ZahlFeld wert={d.einstellungen?.ueberfaellig_karenz_tage ?? 3} breite="w-14" suffix="Tage"
              onSave={v => setEinstellung('ueberfaellig_karenz_tage', Math.max(0, Math.min(30, Math.round(Number(v) || 0))))} />
            <span className="text-[#4B5563]">nach Fälligkeit — auf Forderungs- und Verbindlichkeitsseite gleich</span>
            <Marke art="ANNAHME" />
          </div>
          {kar?.begruendung && <p className="text-[#6B7280] max-w-3xl">{kar.begruendung}</p>}
          {d.verbindlichkeiten.gutschriften?.belege > 0 && (
            <p className="text-[#4B5563]">
              Rechnungsbestand {eur(d.verbindlichkeiten.summe_brutto_eur, 2)} abzüglich{' '}
              {eur(d.verbindlichkeiten.gutschriften.verrechnet_eur, 2)} verrechneter Gutschriften
              {' '}({d.verbindlichkeiten.gutschriften.belege} {d.verbindlichkeiten.gutschriften.belege === 1 ? 'Beleg' : 'Belege'} über{' '}
              {eur(d.verbindlichkeiten.gutschriften.summe_eur, 2)}) = <strong>{eur(d.verbindlichkeiten.summe_eur, 2)}</strong>.
              {d.verbindlichkeiten.gutschriften.nicht_verrechnet_eur >= 0.01 && <>
                {' '}Für {eur(d.verbindlichkeiten.gutschriften.nicht_verrechnet_eur, 2)} gibt es keinen offenen Gegenposten;
                sie mindern keine geplante Zahlung. Kreditorensaldo laut Odoo: {eur(d.verbindlichkeiten.summe_odoo_netto_eur, 2)}.
              </>}
            </p>
          )}
          {d.verbindlichkeiten.gutschriften?.liste?.length > 0 && (
            <details>
              <summary className="cursor-pointer select-none text-[#338edc]">Gutschriften im Einzelnen</summary>
              <table className="mt-1.5 w-full max-w-2xl">
                <tbody>
                  {d.verbindlichkeiten.gutschriften.liste.map(g => (
                    <tr key={g.move_line_id} className="border-b border-[#E5E7EB]">
                      <td className="py-1 text-[#4B5563]">{g.lieferant}</td>
                      <td className="py-1 text-[#6B7280]">{g.beleg}</td>
                      <td className="py-1 text-[#6B7280]">{datum(g.faellig_am)}</td>
                      <td className="py-1 text-right tabular-nums text-[#1A1A2E]">{eur(g.betrag_eur, 2)}</td>
                      <td className="py-1 text-right text-[11px] text-[#B45309] whitespace-nowrap">
                        {g.offen_ohne_gegenposten ? `${eur(g.rest_ohne_gegenposten_eur, 2)} ohne Gegenposten` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
        {/* #1860: Sammelvereinbarung — angehakte Belege EINES Gläubigers. */}
        {Object.keys(sammelAuswahl).length > 0 && (() => {
          const zeilen = Object.values(sammelAuswahl);
          const gleicherGlaeubiger = zeilen.every(z => z.lieferant === zeilen[0].lieferant);
          const summe = zeilen.reduce((s, z) => s + z.offen_eur, 0);
          return (
            <div className="flex items-center gap-3 mb-3 px-3 py-2 bg-[#F8F9FC] border border-[#E5E7EB] rounded-md text-xs">
              <span className="text-[#1A1A2E]">
                {zeilen.length} Beleg{zeilen.length > 1 ? 'e' : ''} angehakt · {eur(summe, 2)}
              </span>
              {gleicherGlaeubiger ? (
                <button
                  onClick={() => setVereinbarungFuer({ modus: 'glaeubiger', zeilen, gesamt_eur: Math.round(summe * 100) / 100 })}
                  className="text-[#338edc] font-medium hover:underline">
                  Sammelvereinbarung für {zeilen[0].lieferant} treffen
                </button>
              ) : (
                <span className="text-[#B45309]">Eine Sammelvereinbarung gilt je Gläubiger — bitte nur Belege EINES Lieferanten anhaken.</span>
              )}
              <button onClick={() => setSammelAuswahl({})} className="text-[#6B7280] hover:underline ml-auto">Auswahl leeren</button>
            </div>
          );
        })()}
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB] [&>th]:sticky [&>th]:top-0 [&>th]:bg-white">
              <th className="pb-2 w-6" aria-label="Sammel-Auswahl"></th>
              <th className="pb-2 font-semibold">Lieferant</th>
              <th className="pb-2 font-semibold">Beleg</th>
              <th className="pb-2 font-semibold text-right">Offen</th>
              <th className="pb-2 font-semibold text-right">Fällig</th>
              <th className="pb-2 font-semibold">Vereinbarung</th>
              <th className="pb-2 font-semibold text-right">Erwartet</th>
            </tr>
          </thead>
          <tbody>
            {d.verbindlichkeiten.liste.slice(0, 40).map(v => (
              <tr key={v.move_line_id} className="border-b border-[#F3F4F6] last:border-0">
                <td className="py-2">
                  {!v.vereinbarung && (
                    <input type="checkbox" checked={!!sammelAuswahl[v.move_line_id]}
                      onChange={e => setSammelAuswahl(a => {
                        const n = { ...a };
                        if (e.target.checked) n[v.move_line_id] = v; else delete n[v.move_line_id];
                        return n;
                      })}
                      title="Für Sammelvereinbarung anhaken"
                      className="align-middle accent-[#338edc]" />
                  )}
                </td>
                <td className="py-2 text-[#1A1A2E]">{v.lieferant}</td>
                <td className="py-2 text-[11px] text-[#6B7280]">{v.beleg}</td>
                <td className="py-2 text-right text-[#1A1A2E] tabular-nums">{eur(v.offen_eur, 2)}</td>
                <td className={`py-2 text-right text-[11px] whitespace-nowrap ${v.ueberfaellig ? 'text-[#B45309]' : 'text-[#6B7280]'}`}>
                  {datum(v.faellig_am)}{v.ueberfaellig && <span className="block">überfällig</span>}
                </td>
                <td className="py-2">
                  {v.vereinbarung ? (
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <Marke art={v.vereinbarung.marke === 'annahme' ? 'ANNAHME' : 'VEREINBARUNG'} />
                      <span className="text-[11px] text-[#6B7280]">
                        {v.vereinbarung.sammel && 'Sammel · '}{vereinbarungText(v.vereinbarung)}
                      </span>
                      <button onClick={() => schreibe(`vereinbarungen/${v.vereinbarung.id}`, null, 'DELETE', { erfolg: 'Vereinbarung beendet — Kurve, Tiefpunkt, Abstimmung, Wochen, Monate und Summary stehen wieder auf dem Stand ohne Vereinbarung.' })}
                        className="text-[11px] text-[#6B7280] hover:text-[#1A1A2E] hover:underline">
                        beenden
                      </button>
                    </span>
                  ) : (
                    <button onClick={() => setVereinbarungFuer({ modus: 'beleg', zeile: v, gesamt_eur: v.offen_eur })}
                      className="text-[11px] text-[#338edc] hover:underline">
                      Vereinbarung
                    </button>
                  )}
                </td>
                <td className="py-2 text-right text-[11px] text-[#6B7280] whitespace-nowrap">
                  {v.vereinbarung ? (
                    v.vereinbarung.art === 'raten'
                      ? `ab ${datum(v.vereinbarung.raten?.[0]?.datum)}`
                      : datum(v.vereinbarung.zahlung_am)
                  ) : (
                    <>
                      {datum(v.erwartet_am)}
                      <span className="block text-[10px] text-[#9CA3AF]">
                        {v.erwartet_grundlage === 'lieferanten_median' && `Lieferanten-Median ${v.erwartet_tage} T`}
                        {v.erwartet_grundlage === 'global_median' && `globaler Median ${v.erwartet_tage} T`}
                        {v.erwartet_grundlage === 'ueberfaellig_median' && (
                          <><Marke art="ANNAHME" /> überfällig, heute + {v.erwartet_tage} T</>
                        )}
                        {v.erwartet_grundlage === 'faelligkeit' && 'laut Fälligkeit'}
                      </span>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {d.verbindlichkeiten.liste.length > 40 && (
          <p className="text-[11px] text-[#9CA3AF] mt-3">
            40 von {d.verbindlichkeiten.liste.length} Zeilen gezeigt — die Kurve rechnet mit allen.
          </p>
        )}
      </Card>

      {/* #1860: Zahlungsvereinbarungen — eigene Liste aller Ratenpläne,
          Stundungen und freien Verpflichtungen. Eigene Tabelle, überlebt
          jeden Odoo-Abgleich. */}
      {d.vereinbarungen && (
        <Card
          id="vereinbarungen" erzwinge={alleKarten}
          titel="Zahlungsvereinbarungen"
          kopfzeile={`${d.vereinbarungen.liste.length} Vereinbarung${d.vereinbarungen.liste.length === 1 ? '' : 'en'} · ${eur(d.vereinbarungen.terminiert_eur, 2)} Abfluss terminiert${
            d.vereinbarungen.zufluss_terminiert_eur >= 0.01 ? ` · ${eur(d.vereinbarungen.zufluss_terminiert_eur, 2)} Zufluss terminiert` : ''}${
            d.vereinbarungen.rest_ohne_termin_eur >= 0.01 ? ` · ${eur(d.vereinbarungen.rest_ohne_termin_eur, 2)} Rest ohne Termin` : ''}`}
          hinweis="Ein Mechanismus, zwei Richtungen: Ratenpläne, Stundungen und Einmalzahlungen zu Verbindlichkeiten (Abfluss) und Zahlungserwartungen zu Forderungen (Zufluss) — je Beleg, je Partner (Sammel) oder als freie Verpflichtung ohne Odoo-Beleg (z. B. DE-USt). Jede Rate ist ein eigener Strom in der Kurve und überschreibt die Median-Annahme genau für die betroffenen Beträge. VEREINBARUNG heißt schriftlich zugesagt, ANNAHME ist eigene Planung. Als uneinbringlich gekennzeichnete Forderungen erzeugen keinen Strom. Die Liste liegt in unserer eigenen Tabelle und überlebt jeden Odoo-Abgleich."
        >
          {d.vereinbarungen.liste.length === 0 ? (
            <p className="text-sm text-[#6B7280]">
              Noch keine Vereinbarung. Anlegen über die Zeilen der Verbindlichkeiten-Liste (einzeln oder per Häkchen als Sammelvereinbarung), über „Erwartung setzen" bei den zweifelhaften Altforderungen — oder als freie Verpflichtung.
            </p>
          ) : (
            // #1861 AK6: EINE Tabelle, zweimal gerendert — die Richtung ist ein
            // Filter, kein zweiter Rechenweg.
            [
              ['verbindlichkeit', 'Verbindlichkeiten — Abflüsse', 'Gläubiger / Bezeichnung', 'Vereinbarung'],
              ['forderung', 'Forderungen — Zuflüsse', 'Kunde', 'Zahlungserwartung'],
            ].map(([r, ueberschrift, spalte1, spalte2]) => {
              const liste = d.vereinbarungen.liste.filter(u => (u.richtung || 'verbindlichkeit') === r);
              if (!liste.length) return null;
              return (
                <div key={r} className="mb-5 last:mb-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280] mb-1">
                    {ueberschrift} · {liste.length}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                        <th className="pb-2 font-semibold">{spalte1}</th>
                        <th className="pb-2 font-semibold">{spalte2}</th>
                        <th className="pb-2 font-semibold text-right">Gesamt</th>
                        <th className="pb-2 font-semibold text-right">Rest offen</th>
                        <th className="pb-2 font-semibold text-right">Nächste Rate</th>
                        <th className="pb-2 font-semibold text-right">Endtermin</th>
                        <th className="pb-2" aria-label="Aktionen"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {liste.map(u => (
                        <tr key={u.id} className="border-b border-[#F3F4F6] last:border-0 align-top">
                          <td className="py-2 text-[#1A1A2E]">
                            {u.glaeubiger}
                            {u.verbunden && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]"
                                title="Verbundenes Unternehmen">verbunden</span>
                            )}
                            <span className="block text-[10px] text-[#9CA3AF]">
                              {u.typ === 'frei' ? 'freie Verpflichtung' : u.typ === 'glaeubiger' ? `Sammel · ${u.belege} Belege` : '1 Beleg'}
                              {u.notiz && <> · {u.notiz}</>}
                            </span>
                          </td>
                          <td className="py-2">
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <Marke art={u.herkunft} />
                              <span className={`text-[11px] ${u.art === 'uneinbringlich' ? 'text-[#B91C1C] font-medium' : 'text-[#6B7280]'}`}>
                                {vereinbarungText(u)}
                              </span>
                            </span>
                            {u.art === 'uneinbringlich' && (
                              <span className="block text-[10px] text-[#9CA3AF] mt-0.5">
                                bleibt aus beiden Kurven — Wertberichtigung in der Buchhaltung separat buchen
                              </span>
                            )}
                            {u.in_odoo_erledigt && (
                              <span className="block text-[10px] text-[#9CA3AF] mt-0.5">in Odoo ausgeglichen — fließt nicht mehr</span>
                            )}
                            {u.warnung_summe && (
                              <span className="block text-[10px] text-[#B45309] mt-0.5">Ratensumme weicht vom offenen Betrag ab</span>
                            )}
                          </td>
                          <td className="py-2 text-right text-[#1A1A2E] tabular-nums">{eur(u.gesamt_eur, 2)}</td>
                          <td className="py-2 text-right text-[#1A1A2E] tabular-nums">
                            {eur(u.terminiert_eur, 2)}
                            {u.teilzahlungen_eur >= 0.01 && (
                              <span className="block text-[10px] text-[#9CA3AF]">
                                {eur(u.teilzahlungen_eur, 2)} bereits {r === 'forderung' ? 'eingegangen' : 'gezahlt'}
                              </span>
                            )}
                            {u.rest_ohne_termin_eur >= 0.01 && (
                              <span className="block text-[10px] text-[#B45309]">{eur(u.rest_ohne_termin_eur, 2)} ohne Termin</span>
                            )}
                          </td>
                          <td className="py-2 text-right text-[11px] text-[#6B7280] whitespace-nowrap">
                            {u.naechste_rate ? <>{datum(u.naechste_rate.datum)}<span className="block text-[10px] text-[#9CA3AF]">{eur(u.naechste_rate.betrag_eur, 2)}</span></> : '—'}
                          </td>
                          <td className="py-2 text-right text-[11px] text-[#6B7280] whitespace-nowrap">{u.endtermin ? datum(u.endtermin) : '—'}</td>
                          <td className="py-2 text-right whitespace-nowrap">
                            {(() => {
                              // Bearbeiten braucht die gedeckten Odoo-Zeilen — sind sie
                              // in Odoo schon ausgeglichen, gibt es nichts zu bearbeiten.
                              const quelle = r === 'forderung' ? arZeilenNormalisiert(d) : (d.verbindlichkeiten?.liste || []);
                              const gedeckt = quelle.filter(z => z.vereinbarung?.id === u.id);
                              const kannBearbeiten = u.typ === 'frei' || gedeckt.length > 0;
                              return kannBearbeiten && (
                                <button onClick={() => oeffneVereinbarung(u)}
                                  className="text-[11px] text-[#338edc] hover:underline mr-2">bearbeiten</button>
                              );
                            })()}
                            <button onClick={() => schreibe(`vereinbarungen/${u.id}`, null, 'DELETE', { erfolg: 'Vereinbarung beendet — Kurve, Tiefpunkt, Abstimmung, Wochen, Monate und Summary stehen wieder auf dem Stand ohne Vereinbarung.' })}
                              className="text-[11px] text-[#6B7280] hover:text-[#1A1A2E] hover:underline">beenden</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
          <div className="mt-3">
            <button onClick={() => setVereinbarungFuer({ modus: 'frei' })}
              className="text-xs text-[#338edc] hover:underline font-medium">
              + Freie Verpflichtung anlegen
            </button>
          </div>
        </Card>
      )}

      {/* Planung */}
      <Card
        id="planung" erzwinge={alleKarten}
        titel="Planung je Monat"
        kopfzeile={`${d.planung.length} Monate — jeder Wert und Zahltag überschreibbar${
          d.plan_ist_drift?.run_rate_eur != null && d.plan_ist_drift?.laufender?.plan_eur != null
            ? ` · Plan ${MONATE[d.plan_ist_drift.laufender.monat - 1]} ${eur(d.plan_ist_drift.laufender.plan_eur)} vs. Run-Rate ${eur(d.plan_ist_drift.run_rate_eur)}`
            : ''}`}
        hinweis="Maßgeblich ist nicht der Monat, sondern der Zahltermin. Jeder Wert und jeder Zahltag ist hier überschreibbar und wirkt sofort in der Kurve. Ein geleertes Feld fällt auf den abgeleiteten Wert zurück."
      >
        {/* #1840 Teil E: Plan gegen IST — nur Anzeige, das Modell überschreibt
            den Plan NIE; eine Korrektur bleibt eine manuelle Entscheidung. */}
        {d.plan_ist_drift?.monate?.length > 0 && (
          <div className="mb-4">
            <h3 className="text-xs font-semibold text-[#1A1A2E] mb-2">Plan gegen IST (Umsatz, fakturiert)</h3>
            <table className="w-full text-sm max-w-2xl">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                  <th className="pb-1.5 font-semibold">Monat</th>
                  <th className="pb-1.5 font-semibold text-right">Plan</th>
                  <th className="pb-1.5 font-semibold text-right">IST</th>
                  <th className="pb-1.5 font-semibold text-right">Abweichung</th>
                </tr>
              </thead>
              <tbody>
                {d.plan_ist_drift.monate.map(m => (
                  <tr key={`${m.jahr}-${m.monat}`} className="border-b border-[#F3F4F6] last:border-0">
                    <td className="py-1.5 text-[#4B5563] whitespace-nowrap">{MONATE[m.monat - 1]} {m.jahr}</td>
                    <td className="py-1.5 text-right tabular-nums text-[#1A1A2E]">
                      {eur(m.plan_eur)} <Marke art="PLAN" />
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-[#1A1A2E]">
                      {eur(m.ist_eur)} <Marke art="IST" />
                    </td>
                    <td className={`py-1.5 text-right tabular-nums ${m.abweichung_eur < 0 ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>
                      {m.abweichung_eur === null ? '–' : (
                        <>
                          {eur(m.abweichung_eur)}
                          {m.abweichung_prozent !== null &&
                            ` (${m.abweichung_prozent > 0 ? '+' : ''}${String(m.abweichung_prozent).replace('.', ',')} %)`}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {d.plan_ist_drift.laufender && (
                  <tr>
                    <td className="py-1.5 text-[#4B5563] whitespace-nowrap">
                      {MONATE[d.plan_ist_drift.laufender.monat - 1]} {d.plan_ist_drift.laufender.jahr}
                      <span className="text-[10px] text-[#9CA3AF] ml-1">laufend</span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-[#1A1A2E]">
                      {eur(d.plan_ist_drift.laufender.plan_eur)} <Marke art={d.plan_ist_drift.laufender.plan_herkunft || 'PLAN'} />
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-[#1A1A2E]">
                      {eur(d.plan_ist_drift.laufender.fakturiert_netto_eur)} <Marke art="IST" />
                      {d.plan_ist_drift.laufender.teilsicht && (
                        <span className="block text-[10px] text-[#B45309]">Teilsicht</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right text-[11px] text-[#9CA3AF]">Monat läuft noch</td>
                  </tr>
                )}
              </tbody>
            </table>
            {d.plan_ist_drift.run_rate_eur !== null && d.plan_ist_drift.laufender?.plan_eur != null && (
              <p className="text-[11px] text-[#6B7280] mt-2 max-w-3xl">
                Run-Rate der letzten {d.plan_ist_drift.run_rate_monate} abgeschlossenen Monate:{' '}
                <strong className="text-[#1A1A2E]">{eur(d.plan_ist_drift.run_rate_eur)}</strong> —
                der Plan für {MONATE[d.plan_ist_drift.laufender.monat - 1]} liegt bei{' '}
                <strong className="text-[#1A1A2E]">{eur(d.plan_ist_drift.laufender.plan_eur)}</strong>
                {' '}({(() => {
                  const p = Math.round((d.plan_ist_drift.laufender.plan_eur / d.plan_ist_drift.run_rate_eur - 1) * 1000) / 10;
                  return `${p > 0 ? '+' : ''}${String(p).replace('.', ',')} %`;
                })()}).
                Das Modell überschreibt den Plan nie — eine Korrektur bleibt eine manuelle
                Eingabe in der Tabelle darunter.
              </p>
            )}
          </div>
        )}
        {/* Entscheidung 7: laufender Monat = schon fakturiert + Rest-Plan */}
        {d.fakturiert_ist && (
          <div className="mb-4 text-xs px-3 py-2 rounded-lg border bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]">
            Laufender Monat {MONATE[d.fakturiert_ist.monat - 1]} {d.fakturiert_ist.jahr}:{' '}
            <strong className="text-[#1A1A2E]">{eur(d.fakturiert_ist.fakturiert_netto_eur)}</strong> aus{' '}
            {d.fakturiert_ist.belege} Belegen bereits fakturiert <Marke art="IST" />
            {d.fakturiert_ist.rest_plan_eur !== null && (
              <> · Rest-Plan <strong className="text-[#1A1A2E]">{eur(d.fakturiert_ist.rest_plan_eur)}</strong>{' '}
              <Marke art={d.fakturiert_ist.plan_herkunft || 'PLAN'} /> (Plan minus fakturiert, nie unter null)</>
            )}
            {d.fakturiert_ist.teilsicht && (
              <span className="block mt-1 text-[#B45309]">
                Teilsicht — der Ultimo-Lauf des Monats steht noch aus, die Fakturierung ist unvollständig.
              </span>
            )}
          </div>
        )}
        {/* #1843 Punkt 8/11: der Vormonat ist am Monatsersten noch nicht fertig
            abgerechnet — auf BEIDEN Seiten. Was fehlt, bleibt bis zum Karenztag
            als Rest-Plan in der Kurve, statt am Monatswechsel zu verschwinden. */}
        {d.karenz?.aktiv && (
          <div className="mb-4 text-xs px-3 py-2 rounded-lg border bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]">
            Karenz für {MONATE[d.karenz.monat - 1]} {d.karenz.jahr} läuft bis zum {d.karenz.bis_tag}.{' '}
            <Marke art="PLAN" />
            {d.karenz.umsatz && (
              <span className="block mt-1">
                Umsatz: Plan {eur(d.karenz.umsatz.plan_eur)} − fakturiert {eur(d.karenz.umsatz.fakturiert_netto_eur)}
                {' '}− Entwürfe {eur(d.karenz.umsatz.entwurf_netto_eur)} = Rest-Plan{' '}
                <strong className="text-[#1A1A2E]">{eur(d.karenz.umsatz.rest_plan_eur)}</strong>
              </span>
            )}
            {d.karenz.kosten && (
              <span className="block mt-1">
                Kosten: Plan {eur(d.karenz.kosten.fix_plan_brutto_eur + d.karenz.kosten.var_plan_brutto_eur)} brutto
                {' '}− fakturiert {eur(d.karenz.kosten.fakturiert_brutto_eur)} = Rest-Plan{' '}
                <strong className="text-[#1A1A2E]">{eur(d.karenz.kosten.rest_fix_brutto_eur)}</strong> Fixkosten
                {' '}und <strong className="text-[#1A1A2E]">{eur(d.karenz.kosten.rest_var_brutto_eur)}</strong> variable Kosten
              </span>
            )}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB] [&>th]:sticky [&>th]:top-0 [&>th]:bg-white">
                <th className="pb-2 font-semibold">Monat</th>
                <th className="pb-2 font-semibold text-right">Umsatz</th>
                <th className="pb-2 font-semibold text-right">Personal</th>
                <th className="pb-2 font-semibold text-right">Fixkosten</th>
                <th className="pb-2 font-semibold text-right" title={VARIABLE_KOSTEN_TITEL}>Variable Kosten</th>
                <th className="pb-2 font-semibold text-right">Umsatzsteuer</th>
                <th className="pb-2 font-semibold text-right">Kreditkarte</th>
              </tr>
            </thead>
            <tbody>
              {/* #1851: im Szenario kommen die Zeilen aus der Szenario-Rechnung —
                  so reagieren USt (M−2) und abgeleitete Werte sichtbar mit. */}
              {(szenarioAktiv && szenarioDaten?.planung ? szenarioDaten.planung : d.planung).map(m => (
                <tr key={`${m.jahr}-${m.monat}`} className="border-b border-[#F3F4F6] last:border-0 align-top">
                  <td className="py-2.5 text-[#1A1A2E] whitespace-nowrap">{MONATE[m.monat - 1]} {m.jahr}</td>
                  <PlanZelle m={m} feld="umsatz" wert={m.umsatz_eur} herkunft={m.umsatz_herkunft} schreibe={schreibe}
                    szenarioAktiv={szenarioAktiv} szenarioWerte={szenarioWerte} setSzenarioWert={setSzenarioWert} />
                  <PlanZelle m={m} feld="personal" wert={m.personal_eur} herkunft={m.personal_herkunft}
                    schreibe={schreibe} zusatz={`${Math.round((d.einstellungen?.personal_sv_anteil || 0) * 100)} % am ${m.personal_sv_tag}., Rest am ${m.personal_netto_tag}.`}
                    szenarioAktiv={szenarioAktiv} szenarioWerte={szenarioWerte} setSzenarioWert={setSzenarioWert} />
                  <PlanZelle m={m} feld="fixkosten" wert={m.fixkosten_eur} herkunft={m.fixkosten_herkunft}
                    zahltag={m.fixkosten_tag} schreibe={schreibe}
                    szenarioAktiv={szenarioAktiv} szenarioWerte={szenarioWerte} setSzenarioWert={setSzenarioWert} />
                  <PlanZelle m={m} feld="variable_kosten" wert={m.variable_kosten_eur} herkunft={m.variable_kosten_herkunft}
                    zahltag={m.variable_kosten_tag} schreibe={schreibe}
                    szenarioAktiv={szenarioAktiv} szenarioWerte={szenarioWerte} setSzenarioWert={setSzenarioWert} />
                  <PlanZelle m={m} feld="ust" wert={m.ust_eur} herkunft={m.ust_herkunft}
                    zahltag={m.ust_tag} schreibe={schreibe}
                    szenarioAktiv={szenarioAktiv} szenarioWerte={szenarioWerte} setSzenarioWert={setSzenarioWert} />
                  <PlanZelle m={m} feld="karte" wert={m.karte_eur} herkunft={m.karte_herkunft}
                    zahltag={m.karte_tag} schreibe={schreibe}
                    szenarioAktiv={szenarioAktiv} szenarioWerte={szenarioWerte} setSzenarioWert={setSzenarioWert} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Herleitung */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <Card
          id="herleitung" erzwinge={alleKarten}
          titel="Woraus die Kosten abgeleitet sind"
          kopfzeile={`Fixkosten ${eur(d.fixkosten.wirksam_eur)} · Variable Kosten ${(d.variable_kosten.quote * 100).toFixed(1).replace('.', ',')} % · USt ${(d.ust_satz.satz * 100).toFixed(2).replace('.', ',')} % vom Umsatz`}
        >
          <div className="space-y-4 text-sm">
            <div>
              {/* Entscheidung 4: der Fixblock ist eine ANNAHME aus dem Zwölf-
                  Monats-Schnitt, kein Istwert — Einmaleffekte ausgewiesen. */}
              <div className="flex items-baseline justify-between">
                <span className="text-[#1A1A2E]">Fixkosten <Marke art="ANNAHME" /></span>
                <span className="text-[#1A1A2E] tabular-nums font-medium">{eur(d.fixkosten.wirksam_eur, 2)} / Monat</span>
              </div>
              <p className="text-[11px] text-[#6B7280] mt-1">
                Ø aus {d.fixkosten.konten.length} Aufwandskonten der Klassen 7–9, {d.fixkosten.zeitraum}:
                {' '}{eur(d.fixkosten.schnitt_eur, 2)}, davon {eur(d.fixkosten.einmal_abschlag_eur, 2)} als
                Einmaleffekte abgesetzt (sporadisch bebuchte Konten und Ausreißer-Spitzen).
              </p>
              {d.fixkosten.ausgeschlossen?.length > 0 && (
                <p className="text-[11px] text-[#9CA3AF] mt-1">
                  Nicht zahlungswirksam und deshalb ausgenommen: {d.fixkosten.ausgeschlossen.map(k => k.code).join(', ')}
                  {' '}(Abschreibungen und Wertberichtigungen kosten kein Geld).
                </p>
              )}
              <button onClick={() => setFixkostenOffen(o => !o)}
                className="text-[11px] text-[#338edc] hover:underline mt-1">
                {fixkostenOffen ? 'Kontenliste einklappen' : `Alle ${d.fixkosten.konten.length} Konten zeigen`}
              </button>
              {fixkostenOffen && (
                <div className="mt-2 max-h-56 overflow-y-auto border border-[#E5E7EB] rounded-md">
                  <table className="w-full text-xs">
                    <tbody>
                      {d.fixkosten.konten.map(k => (
                        <tr key={k.code} className="border-b border-[#F3F4F6] last:border-0">
                          <td className="px-2 py-1 text-[#6B7280] whitespace-nowrap">{k.code}</td>
                          <td className="px-2 py-1 text-[#4B5563]">
                            {k.name}
                            {k.einmal && (
                              <span className="ml-1.5 text-[10px] text-[#B45309]"
                                title="Sporadisch bebucht oder Ausreißer-Spitze — als Einmaleffekt abgesetzt">
                                Einmaleffekt
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right text-[#1A1A2E] tabular-nums whitespace-nowrap">
                            {eur(k.schnitt_eur, 2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {/* #1843 Punkt 10: „Versand- und Materialaufwand" sagte nicht, was
                drin ist — jetzt EIN Name plus die Konten mit erwartetem Zahltag. */}
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-[#1A1A2E]">Variable Kosten <Marke art="ANNAHME" /></span>
                <span className="text-[#1A1A2E] tabular-nums font-medium">{eur(d.variable_kosten.schnitt_eur, 2)} / Monat</span>
              </div>
              <p className="text-[11px] text-[#6B7280] mt-1">
                {VARIABLE_KOSTEN_TITEL}: {(d.variable_kosten.quote * 100).toFixed(1).replace('.', ',')} % vom Umsatz,
                aus {d.variable_kosten.konten.length} Konten der Klasse 5 ({d.variable_kosten.zeitraum}).
                Diese Kosten wachsen mit dem Volumen und werden deshalb am Planumsatz mitgerechnet, nicht als
                fester Betrag. Erwarteter Zahltag: {d.variable_kosten.zahltag_tag}. des Folgemonats
                + {d.variable_kosten.verzug_tage} Tage gemessener Verzug, verteilt über
                {' '}{d.variable_kosten.verteil_fenster_tage} Tage.
              </p>
              <button onClick={() => setVariableOffen(o => !o)}
                className="text-[11px] text-[#338edc] hover:underline mt-1">
                {variableOffen ? 'Kontenliste einklappen' : `Alle ${d.variable_kosten.konten.length} Konten zeigen`}
              </button>
              {variableOffen && (
                <div className="mt-2 max-h-56 overflow-y-auto border border-[#E5E7EB] rounded-md">
                  <table className="w-full text-xs">
                    <tbody>
                      {d.variable_kosten.konten.map(k => (
                        <tr key={k.code} className="border-b border-[#F3F4F6] last:border-0">
                          <td className="px-2 py-1 text-[#6B7280] whitespace-nowrap">{k.code}</td>
                          <td className="px-2 py-1 text-[#4B5563]">{k.name}</td>
                          <td className="px-2 py-1 text-right text-[#9CA3AF] tabular-nums whitespace-nowrap">
                            {(k.anteil * 100).toFixed(1).replace('.', ',')} %
                          </td>
                          <td className="px-2 py-1 text-right text-[#1A1A2E] tabular-nums whitespace-nowrap">
                            {eur(k.schnitt_eur, 2)}
                          </td>
                          <td className="px-2 py-1 text-right text-[#6B7280] whitespace-nowrap">
                            ab {datum(k.zahltag_ab)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-[#1A1A2E]">Umsatzsteuer <Marke art="ANNAHME" /></span>
                <span className="text-[#1A1A2E] tabular-nums font-medium">
                  {(d.ust_satz.satz * 100).toFixed(2).replace('.', ',')} % vom Umsatz
                </span>
              </div>
              <p className="text-[11px] text-[#6B7280] mt-1">{d.ust_satz.grundlage}.</p>
            </div>
          </div>
        </Card>

        <Card
          id="karte" erzwinge={alleKarten}
          titel="Kreditkarte"
          kopfzeile={`Ø Abrechnung ${eur(d.karte.schnitt_eur)} · fällig am ${d.karte.zahltag}.`}
        >
          <div className="flex items-baseline justify-between">
            <span className="text-[#1A1A2E]">Monatliche Abrechnung <Marke art="ANNAHME" /></span>
            <span className="text-[#1A1A2E] tabular-nums font-medium">{eur(d.karte.schnitt_eur, 2)}</span>
          </div>
          <p className="text-[11px] text-[#6B7280] mt-2">
            Mittel der letzten {d.karte.abrechnungen} Abrechnungen, fällig am {d.karte.zahltag}. Der Betrag lässt sich
            aus Odoo nicht vorhersagen — er ist bewusst als Annahme gekennzeichnet und in der Planung je Monat
            überschreibbar.
          </p>
          {d.karte.ruhend_codes?.length > 0 && (
            <p className="text-[11px] text-[#9CA3AF] mt-2">
              {d.karte.ruhend_codes.length} {d.karte.ruhend_codes.length === 1 ? 'ruhende Karte' : 'ruhende Karten'}
              {' '}ausgeblendet ({d.karte.ruhend_codes.join(', ')}) —
              ohne Bewegung in den letzten Monaten, in Odoo unverändert erhalten.
            </p>
          )}
          <div className="mt-4">
            <span className="text-[11px] text-[#6B7280] block mb-1">Fester Betrag statt Mittelwert <Marke art="MANUELL" /></span>
            <ZahlFeld
              wert={d.einstellungen?.karte_betrag_eur}
              placeholder={String(Math.round(d.karte.schnitt_eur))}
              suffix="EUR"
              onSave={v => setEinstellung('karte_betrag_eur', v)}
            />
          </div>
        </Card>
      </div>

      {/* Entscheidung 4 — nicht terminierte Verpflichtung (eingeklappt, E4) */}
      {d.ust_de?.vorhanden && (
        <Card
          id="ust_de" erzwinge={alleKarten}
          titel="Nicht terminierte Verpflichtung"
          kopfzeile={`Umsatzsteuer Deutschland ${eur(d.ust_de.gesamt_eur ?? d.ust_de.betrag_eur, 2)} — ${
            d.ust_de.vereinbarung
              ? `${eur(d.ust_de.terminiert_eur, 2)} ${d.ust_de.plan_herkunft === 'ANNAHME'
                ? 'über eigenen Ratenplan (nicht zugesagt)'
                : 'über zugesagte Vereinbarung'} in der Kurve${d.ust_de.rest_eur >= 0.01 ? `, ${eur(d.ust_de.rest_eur, 2)} Rest ohne Termin` : ''}`
              : d.ust_de.in_kurve ? `in der Kurve, fällig ${datum(d.ust_de.termin)}` : 'Termin offen, nicht in der Kurve'}`}
          hinweis="Diese Verpflichtung besteht, hat aber keinen bekannten Zahlungstermin. Sie steht bewusst außerhalb der Kurve — ein erfundenes Datum würde den Verlauf verfälschen. Ein Pauschal-Termin schickt den GESAMTEN Betrag in die Kurve. Eine Zahlungsvereinbarung (#1860, z. B. Ratenplan mit dem Finanzamt) terminiert dagegen nur die zugesagten Raten — der Rest bleibt sichtbar hier stehen. Der Betrag ist der volle offene Stand: laufendes Jahr (3518) plus die per Bilanz-Umbuchung nach 3522 übertragene Vorjahres-Umsatzsteuer."
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm text-[#1A1A2E]">
                Umsatzsteuer Deutschland <span className="text-[11px] text-[#9CA3AF]">{d.ust_de.konto.code} + 3522</span>
                {/* #1859 (4): der Betrag ist der gebuchte Odoo-Saldo = IST. Die
                    Marke der Zahlungsvereinbarung gehört an die Planzeile
                    darunter, nicht an den Saldo — sonst liest man „[ANNAHME]
                    110.643,20 €" und hält den gebuchten Betrag für geschätzt. */}
                {' '}<Marke art={d.ust_de.herkunft} />
              </div>
              <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums mt-1">
                {eur(d.ust_de.gesamt_eur ?? d.ust_de.betrag_eur, 2)}
              </div>
              {/* #1840 Teil A: die Karte zeigt, WORAUS die Summe besteht. */}
              <div className="text-xs text-[#6B7280] mt-1.5 space-y-0.5">
                <div>{eur(d.ust_de.betrag_eur, 2)} laufendes Jahr ({d.ust_de.konto.code} {d.ust_de.konto.name})</div>
                {d.ust_de.vorjahr && (
                  <div>
                    {eur(d.ust_de.vorjahr.betrag_eur, 2)} Vorjahr — per Bilanz-Umbuchung in 3522
                    {d.ust_de.vorjahr.beleg && <span className="text-[#9CA3AF]"> ({d.ust_de.vorjahr.beleg})</span>}
                  </div>
                )}
                {d.ust_de.altsaldo_3522_eur != null && Math.abs(d.ust_de.altsaldo_3522_eur) >= 0.01 && (
                  <div>{eur(d.ust_de.altsaldo_3522_eur, 2)} Altsaldo auf 3522</div>
                )}
              </div>
              <div className="text-xs text-[#6B7280] mt-1.5">
                {d.ust_de.vereinbarung ? (
                  <>
                    <Marke art={d.ust_de.plan_herkunft || 'VEREINBARUNG'} />{' '}
                    {vereinbarungText(d.ust_de.vereinbarung)} — {eur(d.ust_de.terminiert_eur, 2)} in der Kurve
                    {d.ust_de.vereinbarung.teilzahlungen_eur >= 0.01 && <>, {eur(d.ust_de.vereinbarung.teilzahlungen_eur, 2)} bereits gezahlt</>}
                    {d.ust_de.rest_eur >= 0.01 && <>, <span className="text-[#B45309]">{eur(d.ust_de.rest_eur, 2)} Rest ohne Termin</span></>}.
                  </>
                ) : d.ust_de.in_kurve
                  ? `In der Kurve enthalten, fällig am ${datum(d.ust_de.termin)}.`
                  : 'Termin offen — nicht in der Kurve enthalten.'}
              </div>
              {d.ust_de.stand_buchhaltung && (
                <div className="text-[11px] text-[#B45309] mt-1">
                  Stand laut Buchhaltung: gebucht bis {datum(d.ust_de.stand_buchhaltung)} —
                  spätere Monate sind noch nicht enthalten, der echte Stand kann höher liegen.
                </div>
              )}
            </div>
            {d.ust_de.vereinbarung ? (
              <div className="flex items-end gap-2">
                <button
                  onClick={() => setVereinbarungFuer({
                    modus: 'ust_de',
                    vorhanden: d.vereinbarungen?.liste?.find(u => u.id === d.ust_de.vereinbarung.id) || null,
                    gesamt_eur: d.ust_de.gesamt_eur ?? d.ust_de.betrag_eur,
                  })}
                  className="text-xs px-3 py-1.5 rounded-md border border-[#338edc] text-[#338edc] font-medium">
                  Vereinbarung bearbeiten
                </button>
                <button onClick={() => schreibe(`vereinbarungen/${d.ust_de.vereinbarung.id}`, null, 'DELETE', { erfolg: 'Vereinbarung beendet — Kurve, Tiefpunkt, Abstimmung, Wochen, Monate und Summary stehen wieder auf dem Stand ohne Vereinbarung.' })}
                  className="text-xs text-[#6B7280] hover:text-[#1A1A2E] hover:underline pb-1.5">
                  beenden
                </button>
              </div>
            ) : (
              <div className="flex items-end gap-2 flex-wrap">
                <button
                  onClick={() => setVereinbarungFuer({
                    modus: 'ust_de',
                    gesamt_eur: d.ust_de.gesamt_eur ?? d.ust_de.betrag_eur,
                  })}
                  className="text-xs px-3 py-1.5 rounded-md border border-[#338edc] text-[#338edc] font-medium">
                  Ratenplan / Vereinbarung
                </button>
                <label className="block">
                  <span className="text-[11px] text-[#6B7280] block mb-0.5">Pauschal-Termin <Marke art="MANUELL" /></span>
                  <input
                    type="date" value={d.ust_de.termin ? String(d.ust_de.termin).slice(0, 10) : ''}
                    onChange={e => setEinstellung('ust_de_termin', e.target.value || null)}
                    className="px-2 py-1 text-sm border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#338edc]"
                  />
                </label>
                {d.ust_de.termin && (
                  <button onClick={() => setEinstellung('ust_de_termin', null)}
                    className="text-xs text-[#6B7280] hover:text-[#1A1A2E] hover:underline pb-1.5">
                    Termin entfernen
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Entscheidung 1: gemessene Zahlungsregeln — jede überschreibbar.
          #1841 Teil A: Klartext statt JSON, Namen statt IDs, am Seitenende. */}
      {d.regeln && (
        <Card
          id="modell" erzwinge={alleKarten}
          titel="Modell-Einstellungen"
          kopfzeile={`${Object.keys(d.regeln).length} gemessene Zahlungsregeln — jede überschreibbar`}
          hinweis="Jede Regel ist ein Messwert aus den echten Belegen — die Grundlage steht dabei. Ein manueller Wert ersetzt die Messung, sein Löschen fällt auf den Messwert zurück. Es gibt nie einen leeren Zustand."
        >
          <div className="space-y-3">
            {Object.entries(d.regeln).map(([schluessel, r]) => (
              <RegelZeile key={schluessel} schluessel={schluessel} regel={r}
                partnerNamen={d.partner_namen} schreibe={schreibe} />
            ))}
          </div>
        </Card>
      )}

      {/* Legende */}
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 text-xs text-[#6B7280]">
        <strong className="text-[#1A1A2E]">Die Herkunft jeder Zahl ist erkennbar:</strong>{' '}
        <Marke art="IST" /> aus Odoo übernommen ·{' '}
        <Marke art="PLAN" /> aus der Planrechnung ·{' '}
        <Marke art="MANUELL" /> hier gesetzt ·{' '}
        <Marke art="ANNAHME" /> abgeleitet, kein Istwert ·{' '}
        <Marke art="ENTWURF" /> in Odoo erfasst, aber noch nicht gebucht ·{' '}
        <Marke art="PROGNOSE" /> aus dem Modell errechnet ·{' '}
        <Marke art="FORECAST" /> rollierender Forecast (#1857) ·{' '}
        <Marke art="VEREINBARUNG" /> schriftlich zugesagte Zahlungsvereinbarung (#1860)
        {/* #1851/AK4: die SZENARIO-Marke existiert in der Basis-Ansicht nicht —
            auch nicht in der Legende. */}
        {szenarioAktiv && <> ·{' '}<Marke art="SZENARIO" /> probeweise geändert, nicht gespeichert (#1851)</>}.
        <span className="block mt-2">
          Odoo wird ausschließlich gelesen. Jede Eingabe auf dieser Seite liegt in unseren eigenen Tabellen
          und überlebt jeden Abgleich.
        </span>
      </div>

      {vereinbarungFuer && (
        <VereinbarungDialog
          ziel={vereinbarungFuer}
          onClose={() => setVereinbarungFuer(null)}
          onSave={async body => {
            // `werfen: true` — schlägt das Speichern fehl, bleibt der Dialog
            // offen und zeigt den Grund AM Dialog (#1860-Nachbesserung).
            // #1859 (4): die Rückmeldung nennt die GESPEICHERTE Marke. Sie war
            // vorher stumm — wer die Marke im Dialog umgestellt hatte, konnte
            // nicht sehen, ob die Umstellung angekommen ist, und musste sie an
            // der Karte suchen. Die Marke ist der Unterschied zwischen
            // „schriftlich zugesagt" und „eigene Planung"; sie muss bestätigt
            // werden, nicht erschlossen.
            const markeText = body.marke === 'annahme'
              ? '[ANNAHME] — eigene Planung, nicht zugesagt'
              : '[VEREINBARUNG] — schriftlich zugesagt';
            await schreibe('vereinbarung', body, 'PUT', {
              werfen: true,
              erfolg: `${body.richtung === 'forderung' ? 'Zahlungserwartung' : 'Vereinbarung'} gespeichert als `
                + `${markeText}. Kurve, Tiefpunkt, Abstimmung, Wochen, Monate und Summary sind neu gerechnet.`,
            });
            setVereinbarungFuer(null); setSammelAuswahl({});
          }}
        />
      )}

      {/* #1851: Übernahme — der EINZIGE Schreibpfad des Szenario-Modus. */}
      {uebernahmeOffen && (
        <SzenarioUebernahmeDialog
          liste={szenarioListe}
          basisPlanung={d.planung}
          onClose={() => setUebernahmeOffen(false)}
          onUebernommen={async keys => {
            setUebernahmeOffen(false);
            setSzenarioWerte(w => {
              const n = { ...w };
              for (const k of keys) delete n[k];
              return n;
            });
            await lade(bis);
            await ladeMonatsvergleich();
          }}
        />
      )}
    </div>
  );
}

/**
 * #1851: Änderungsliste vor der Übernahme — Monat, Zeile, alt → neu, je Zeile
 * abwählbar. Umsatz/Personal gehen in den Forecast (#1857), Fixkosten und
 * variable Kosten als MANUELL. Abbrechen schreibt nichts (AK3).
 */
function SzenarioUebernahmeDialog({ liste, basisPlanung, onClose, onUebernommen }) {
  const [gewaehlt, setGewaehlt] = useState(() => new Set(liste.map(w => `${w.jahr}-${w.monat}|${w.feld}`)));
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState(null);
  const zeilen = useMemo(() => liste.map(w => {
    const b = basisPlanung.find(p => p.jahr === w.jahr && p.monat === w.monat);
    return {
      ...w,
      key: `${w.jahr}-${w.monat}|${w.feld}`,
      alt: b ? b[`${w.feld}_eur`] : null,
      altHerkunft: b ? b[`${w.feld}_herkunft`] : null,
      ziel: (w.feld === 'umsatz' || w.feld === 'personal') ? 'FORECAST' : 'MANUELL',
    };
  }).sort((a, b) => (a.jahr - b.jahr) || (a.monat - b.monat) || a.feld.localeCompare(b.feld)),
  [liste, basisPlanung]);
  const umschalten = k => setGewaehlt(s => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const manuellBetroffen = zeilen.filter(z =>
    gewaehlt.has(z.key) && z.ziel === 'FORECAST' && z.altHerkunft === 'MANUELL');

  async function uebernehmen() {
    const werte = zeilen.filter(z => gewaehlt.has(z.key))
      .map(({ jahr, monat, feld, wert_eur }) => ({ jahr, monat, feld, wert_eur }));
    if (!werte.length) return;
    setSendet(true); setFehler(null);
    try {
      const res = await fetch(`${API}/api/investor/liquiditaet/szenario/uebernahme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ werte }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Übernahme fehlgeschlagen');
      await onUebernommen(werte.map(w => `${w.jahr}-${w.monat}|${w.feld}`));
    } catch (e) {
      setFehler(e.message); setSendet(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-[#E5E7EB] w-full max-w-2xl p-5" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-[#1A1A2E]">Szenario übernehmen</h3>
        <p className="text-xs text-[#6B7280] mt-1">
          Nur angehakte Zeilen werden geschrieben: Umsatz und Personal in den{' '}
          <strong>Forecast</strong> (#1857, versioniert), Fixkosten und variable Kosten als{' '}
          <Marke art="MANUELL" /> Monatswert. Abbrechen schreibt nichts.
        </p>
        <div className="mt-4 max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                <th className="pb-1.5 font-semibold w-8" />
                <th className="pb-1.5 font-semibold">Monat</th>
                <th className="pb-1.5 font-semibold">Zeile</th>
                <th className="pb-1.5 font-semibold text-right">Alt</th>
                <th className="pb-1.5 font-semibold text-right">Neu</th>
                <th className="pb-1.5 font-semibold text-right">Ziel</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map(z => (
                <tr key={z.key} className="border-b border-[#F3F4F6] last:border-0">
                  <td className="py-1.5">
                    <input type="checkbox" checked={gewaehlt.has(z.key)} onChange={() => umschalten(z.key)}
                      className="accent-[#338edc]" />
                  </td>
                  <td className="py-1.5 text-[#4B5563] whitespace-nowrap">{MONATE[z.monat - 1]} {z.jahr}</td>
                  <td className="py-1.5 text-[#4B5563]">{SZENARIO_FELD_LABEL[z.feld] || z.feld}</td>
                  <td className="py-1.5 text-right tabular-nums text-[#6B7280]">
                    {eur(z.alt)} {z.altHerkunft && <Marke art={z.altHerkunft} />}
                  </td>
                  <td className="py-1.5 text-right tabular-nums font-medium text-[#1A1A2E]">{eur(z.wert_eur)}</td>
                  <td className="py-1.5 text-right text-[11px] text-[#6B7280]">
                    {z.ziel === 'FORECAST' ? <Marke art="FORECAST" /> : <Marke art="MANUELL" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {manuellBetroffen.length > 0 && (
          <div className="mt-3 text-xs px-3 py-2 rounded-lg border bg-amber-50 border-amber-200 text-amber-900">
            {manuellBetroffen.length === 1 ? 'Eine Zeile trägt' : `${manuellBetroffen.length} Zeilen tragen`} aktuell
            einen manuellen Wert — er wird bei der Übernahme entfernt, sonst würde er den
            Forecast-Wert weiter verdecken (MANUELL &gt; FORECAST).
          </div>
        )}
        {fehler && <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{fehler}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={sendet}
            className="text-xs px-3 py-1.5 rounded-md border bg-white border-[#E5E7EB] text-[#6B7280] hover:text-[#1A1A2E]">
            Abbrechen
          </button>
          <button onClick={uebernehmen} disabled={sendet || !gewaehlt.size}
            className={`text-xs px-3 py-1.5 rounded-md font-medium ${sendet || !gewaehlt.size
              ? 'bg-[#F8F9FC] border border-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
              : 'bg-[#338edc] text-white hover:bg-[#2b7bc4]'}`}>
            {sendet ? 'Übernehme…' : `${gewaehlt.size} ${gewaehlt.size === 1 ? 'Wert' : 'Werte'} übernehmen`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * #1859 (3)/A2: die Kennzahlen dieser Seite sind CENTGENAU.
 *
 * Der Fließtext der Executive Summary ist es seit der Straffung; die Karten
 * rundeten weiter auf ganze Euro. Nebeneinander gelesen ergab das „28.147"
 * auf der Karte gegen „28.146,60 €" im Text — dieselbe Zahl, sichtbar
 * verschieden, und genau die Sorte Widerspruch, die gemeldet wurde. Ein
 * Wirtschaftsprüfer rechnet Differenzen nach; eine gerundete Kachel neben
 * einem centgenauen Satz kostet ihn die Zeit, den Unterschied zu suchen.
 * Der Klickpfad prüft die Deckungsgleichheit deshalb ausdrücklich.
 */
function Kpi({ titel, wert, meta, marke, zusatz }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[#9CA3AF]">{titel}</div>
        <Marke art={marke} />
      </div>
      <div className="text-2xl font-semibold text-[#1A1A2E] tabular-nums mt-1.5 break-words">{eur(wert, 2)}</div>
      <div className="text-[11px] text-[#6B7280] mt-1">{meta}</div>
      {zusatz && <div className="text-[11px] text-[#6B7280] mt-1 pt-1 border-t border-[#F3F4F6]">{zusatz}</div>}
    </div>
  );
}

/**
 * #1843 Punkt 7: Entwurfsrechnungen sind in Odoo erfasst, aber noch nicht
 * gebucht — sie fehlen deshalb in jeder Forderungsliste, sind aber fast
 * sicheres Geld. Eigene Marke ENTWURF, je Kunde gruppiert, Einzelbelege beim
 * Aufklappen. Reine Anzeige-Aggregation, der erwartete Zahltag kommt aus dem
 * Dienst.
 */
function EntwurfsBlock({ e }) {
  const [offen, setOffen] = useState(false);
  const gruppen = useMemo(() => {
    const map = new Map();
    for (const b of e.liste) {
      const g = map.get(b.partner_id)
        || { partner_id: b.partner_id, kunde: b.kunde, belege: 0, brutto_eur: 0, erwartet_am: b.erwartet_am };
      g.belege += 1;
      g.brutto_eur = Math.round((g.brutto_eur + b.brutto_eur) * 100) / 100;
      if (b.erwartet_am < g.erwartet_am) g.erwartet_am = b.erwartet_am;
      map.set(b.partner_id, g);
    }
    return [...map.values()].sort((a, b) => b.brutto_eur - a.brutto_eur);
  }, [e.liste]);

  return (
    <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
      <button type="button" onClick={() => setOffen(o => !o)}
        className="w-full flex items-center justify-between gap-2 text-left">
        <span className="text-sm text-[#1A1A2E]">
          davon <strong>{e.belege}</strong> Entwürfe über <strong>{eur(e.brutto_eur, 2)}</strong> brutto,
          noch nicht gebucht <Marke art="ENTWURF" />
        </span>
        <span className="text-[11px] text-[#6B7280]">{offen ? 'zuklappen' : `${gruppen.length} Kunden`}</span>
      </button>
      <p className="text-[11px] text-[#6B7280] mt-1">
        In Odoo unter „zu validierende Rechnungen". Erwarteter Zahltag = heute
        + {e.buchungskarenz_tage} Tage Buchungskarenz + Zahlungsziel + Kundenmedian — dieselbe Rechnung
        wie bei einer gebuchten Forderung, damit die Validierung keinen Sprung in der Kurve erzeugt.
      </p>
      {offen && (
        <table className="w-full text-sm mt-3">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
              <th className="pb-2 font-semibold">Kunde</th>
              <th className="pb-2 font-semibold text-right">Brutto</th>
              <th className="pb-2 font-semibold text-right">Erwartet ab</th>
            </tr>
          </thead>
          <tbody>
            {gruppen.map(g => (
              <tr key={g.partner_id} className="border-b border-[#F3F4F6] last:border-0">
                <td className="py-1.5 text-[#1A1A2E]">
                  {g.kunde}
                  <span className="text-[11px] text-[#9CA3AF] ml-2">{g.belege} Beleg{g.belege === 1 ? '' : 'e'}</span>
                </td>
                <td className="py-1.5 text-right text-[#1A1A2E] tabular-nums">{eur(g.brutto_eur, 2)}</td>
                <td className="py-1.5 text-right text-[11px] text-[#6B7280]">{datum(g.erwartet_am)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * #1841 Teil B: zweifelhafte Altforderungen je Kunde gruppiert — eine Zeile je
 * Kunde (Summe, Belegzahl, älteste Fälligkeit), Einzelbelege beim Aufklappen.
 * Kleinstbeträge unter der Bagatellgrenze stehen als EINE Sammelzeile ohne
 * Datumsfelder: sie sind Kandidaten für eine Ausbuchung in Odoo, keine
 * planbaren Zuflüsse. Reine Frontend-Aggregation, keine neue Rechenlogik.
 */
function ZweifelhaftGruppen({ liste, bagatellGrenze, schreibe, oeffneErwartung, bearbeite }) {
  const [offeneGruppe, setOffeneGruppe] = useState(null);
  const alsZiel = z => arZeileNorm(z);
  const { gruppen, bagatelle } = useMemo(() => {
    const bag = liste.filter(z => Math.abs(z.betrag_eur) < bagatellGrenze && !z.rueckholung && !z.vereinbarung);
    const rest = liste.filter(z => !bag.includes(z));
    const map = new Map();
    for (const z of rest) {
      const g = map.get(z.partner_id) || {
        partner_id: z.partner_id, kunde: z.kunde, verbunden: z.verbunden,
        summe_eur: 0, belege: [], aelteste: null,
      };
      g.summe_eur += z.betrag_eur;
      g.belege.push(z);
      if (!g.aelteste || z.faellig_am < g.aelteste) g.aelteste = z.faellig_am;
      map.set(z.partner_id, g);
    }
    const gruppen = [...map.values()].sort((a, b) => b.summe_eur - a.summe_eur);
    for (const g of gruppen) g.summe_eur = Math.round(g.summe_eur * 100) / 100;
    return { gruppen, bagatelle: bag };
  }, [liste, bagatellGrenze]);
  const bagSumme = Math.round(bagatelle.reduce((s, z) => s + z.betrag_eur, 0) * 100) / 100;

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
          <th className="pb-2 font-semibold">Kunde</th>
          <th className="pb-2 font-semibold text-right">Offen</th>
          <th className="pb-2 font-semibold text-right">Belege</th>
          <th className="pb-2 font-semibold text-right">Älteste Fälligkeit</th>
          <th className="pb-2 font-semibold">Zahlungserwartung</th>
        </tr>
      </thead>
      <tbody>
        {gruppen.map(g => {
          const offen = offeneGruppe === g.partner_id;
          const ohneErwartung = g.belege.filter(z => !z.vereinbarung);
          const unein = g.belege.filter(z => z.uneinbringlich).length;
          return (
            <Fragment key={g.partner_id}>
              <tr className="border-b border-[#F3F4F6]">
                <td className="py-2 text-[#1A1A2E]">
                  <button onClick={() => setOffeneGruppe(o => (o === g.partner_id ? null : g.partner_id))}
                    className="hover:underline text-left">
                    <span className="text-[#9CA3AF] mr-1">{offen ? '▾' : '▸'}</span>
                    {g.kunde}
                  </button>
                  {g.verbunden && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded border bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]"
                      title="Verbundenes Unternehmen">verbunden</span>
                  )}
                </td>
                <td className="py-2 text-right text-[#1A1A2E] tabular-nums font-medium">{eur(g.summe_eur, 2)}</td>
                <td className="py-2 text-right text-[11px] text-[#6B7280]">{g.belege.length}</td>
                <td className="py-2 text-right text-[11px] text-[#B45309] whitespace-nowrap">{datum(g.aelteste)}</td>
                <td className="py-2">
                  <span className="flex items-center gap-2 text-[11px] text-[#6B7280]">
                    {ohneErwartung.length > 0 ? (
                      <button
                        onClick={() => oeffneErwartung({
                          modus: 'glaeubiger', richtung: 'forderung',
                          zeilen: ohneErwartung.map(alsZiel),
                          gesamt_eur: Math.round(ohneErwartung.reduce((s, z) => s + z.betrag_eur, 0) * 100) / 100,
                        })}
                        title={`Eine Erwartung für alle ${ohneErwartung.length} offenen Belege dieses Kunden setzen`}
                        className="px-2 py-1 rounded-md border border-[#E5E7EB] text-[#338edc] hover:border-[#338edc]">
                        Erwartung setzen
                      </button>
                    ) : (
                      <span className="text-[#9CA3AF]">alle Belege haben eine Erwartung</span>
                    )}
                    {ohneErwartung.length < g.belege.length && ohneErwartung.length > 0 && (
                      <span className="text-[#9CA3AF]">{g.belege.length - ohneErwartung.length} bereits gesetzt</span>
                    )}
                    {unein > 0 && (
                      <span className="text-[#B91C1C]">{unein} uneinbringlich</span>
                    )}
                  </span>
                </td>
              </tr>
              {offen && g.belege.map(z => (
                <tr key={z.move_line_id} className="border-b border-[#F3F4F6] bg-[#F8F9FC]">
                  <td className="py-1.5 pl-6 text-[11px] text-[#6B7280]">{z.beleg}</td>
                  <td className="py-1.5 text-right text-[11px] text-[#4B5563] tabular-nums">{eur(z.betrag_eur, 2)}</td>
                  <td />
                  <td className="py-1.5 text-right text-[11px] text-[#B45309] whitespace-nowrap">{datum(z.faellig_am)}</td>
                  <td className="py-1.5">
                    {z.vereinbarung ? (
                      <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
                        <Marke art={z.vereinbarung.marke === 'annahme' ? 'ANNAHME' : 'VEREINBARUNG'} />
                        <span className={z.uneinbringlich ? 'text-[#B91C1C] font-medium' : ''}>
                          {vereinbarungText(z.vereinbarung)}
                        </span>
                        {z.vereinbarung.sammel && <span className="text-[#9CA3AF]">(Sammel)</span>}
                        <button onClick={() => bearbeite(z.vereinbarung.id)}
                          className="text-[#338edc] hover:underline">ändern</button>
                        <button
                          onClick={() => schreibe(`vereinbarungen/${z.vereinbarung.id}`, null, 'DELETE', { erfolg: 'Vereinbarung beendet — Kurve, Tiefpunkt, Abstimmung, Wochen, Monate und Summary stehen wieder auf dem Stand ohne Vereinbarung.' })}
                          className="text-[#6B7280] hover:text-[#1A1A2E] hover:underline">beenden</button>
                      </span>
                    ) : z.rueckholung ? (
                      <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
                        <Marke art="MANUELL" />
                        Zahlung am {datum(z.rueckholung.zahlung_am)}
                        <button
                          onClick={() => schreibe(`rueckholung/${z.move_line_id}`, null, 'DELETE', { erfolg: 'Rückholung zurückgenommen — alle Ansichten sind neu gerechnet.' })}
                          className="text-[#6B7280] hover:text-[#1A1A2E] hover:underline">
                          zurücknehmen
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => oeffneErwartung({
                          modus: 'beleg', richtung: 'forderung',
                          zeile: alsZiel(z), gesamt_eur: z.betrag_eur,
                        })}
                        title="Zahlungserwartung für diesen Beleg setzen"
                        className="px-2 py-1 text-[11px] rounded-md border border-[#E5E7EB] text-[#338edc] hover:border-[#338edc]">
                        Erwartung setzen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </Fragment>
          );
        })}
        {bagatelle.length > 0 && (
          <tr>
            <td className="py-2 text-[11px] text-[#9CA3AF]">
              {bagatelle.length} {bagatelle.length === 1 ? 'Kleinstbetrag' : 'Kleinstbeträge'} unter {eur(bagatellGrenze)}
            </td>
            <td className="py-2 text-right text-[11px] text-[#9CA3AF] tabular-nums">{eur(bagSumme, 2)}</td>
            <td className="py-2 text-right text-[11px] text-[#9CA3AF]">{bagatelle.length}</td>
            <td className="py-2 text-right text-[11px] text-[#9CA3AF]">–</td>
            <td className="py-2 text-[11px] text-[#9CA3AF]">Kandidaten für Ausbuchung in Odoo</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/**
 * #1841 Teil C3: Tooltip des Monatsvergleichs — nennt je Richtung die
 * Abweichung (IST bzw. Prognose minus SOLL), damit der Vergleich ablesbar
 * ist statt nur geschätzt.
 */
function MonatsvergleichTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const ein = (p.ist_ein ?? 0) + (p.prognose_ein ?? 0);
  const aus = Math.abs((p.ist_aus ?? 0) + (p.prognose_aus ?? 0));
  const hatWert = p.ist_ein != null || p.prognose_ein != null;
  const quelle = p.teilmonat ? 'IST + Prognose-Rest' : (p.ist_ein != null ? 'IST' : 'PROGNOSE');
  const abw = (wert, soll) => {
    if (!hatWert || soll == null) return null;
    const dlt = Math.round((wert - soll) * 100) / 100;
    return `${dlt > 0 ? '+' : ''}${eur(dlt)}`;
  };
  const abwEin = abw(ein, p.soll_ein);
  const abwAus = abw(aus, p.soll_aus != null ? Math.abs(p.soll_aus) : null);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold text-[#1A1A2E] mb-1">{label}{p.teilmonat ? ' · laufender Monat' : ''}</div>
      <table className="text-[11px]">
        <tbody>
          <tr>
            <td className="pr-3 text-[#6B7280]">Einzahlungen {quelle}</td>
            <td className="text-right tabular-nums text-[#1A1A2E]">{hatWert ? eur(ein) : '–'}</td>
          </tr>
          <tr>
            <td className="pr-3 text-[#6B7280]">Einzahlungen SOLL</td>
            <td className="text-right tabular-nums text-[#1A1A2E]">{p.soll_ein != null ? eur(p.soll_ein) : '–'}</td>
          </tr>
          {abwEin && (
            <tr>
              <td className="pr-3 text-[#6B7280]">Abweichung</td>
              <td className={`text-right tabular-nums ${ein < (p.soll_ein ?? 0) ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>{abwEin}</td>
            </tr>
          )}
          <tr><td className="pt-1" colSpan={2} /></tr>
          <tr>
            <td className="pr-3 text-[#6B7280]">Auszahlungen {quelle}</td>
            <td className="text-right tabular-nums text-[#1A1A2E]">{hatWert ? eur(aus) : '–'}</td>
          </tr>
          <tr>
            <td className="pr-3 text-[#6B7280]">Auszahlungen SOLL</td>
            <td className="text-right tabular-nums text-[#1A1A2E]">{p.soll_aus != null ? eur(Math.abs(p.soll_aus)) : '–'}</td>
          </tr>
          {abwAus && (
            <tr>
              <td className="pr-3 text-[#6B7280]">Abweichung</td>
              <td className={`text-right tabular-nums ${aus > Math.abs(p.soll_aus ?? 0) ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>{abwAus}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Ein Monat der Brücke (#1840 Teil C): die Kopfzeile ist immer sichtbar
 * (Anfang → Ende), aufgeklappt stehen die benannten Ströme. Jede Zeile mit
 * Einzelposten lässt sich öffnen — die Posten summieren EXAKT auf den
 * Zeilenwert (AK3, Cent-Korrektur im Backend). IST-Monate weisen einen nicht
 * zugeordneten Rest AUS statt ihn wegzurechnen und zeigen die größten echten
 * Bewegungen des Monats.
 */
function BrueckenMonat({ m, offen, onToggle, stand }) {
  const [offeneZeile, setOffeneZeile] = useState(null);
  const [j, mo] = m.monat.split('-');
  const name = `${MONATE[parseInt(mo, 10) - 1]} ${j}`;
  return (
    <div className="border border-[#F3F4F6] rounded-lg">
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left">
        <span className="text-sm text-[#1A1A2E]">
          <span className="text-[#9CA3AF] mr-1">{offen ? '▾' : '▸'}</span>
          {name} <Marke art={m.quelle} />
          {m.teilmonat && (
            <span className="ml-1.5 text-[10px] text-[#B45309]"
              title="Angebrochener Monat — IST bis heute, Rest aus dem Modell">
              IST + Prognose-Rest{stand ? ` ab ${kurzDatum(stand)}` : ''}
            </span>
          )}
        </span>
        <span className="text-xs text-[#6B7280] tabular-nums whitespace-nowrap">
          {eur(m.anfang_eur)} → <strong className="text-[#1A1A2E]">{eur(m.ende_eur)}</strong>
          <span className={`ml-2 ${m.netto_eur < 0 ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>
            ({m.netto_eur > 0 ? '+' : ''}{eur(m.netto_eur)})
          </span>
        </span>
      </button>
      {offen && (
        <div className="px-3 pb-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                <th className="pb-1.5 font-semibold">Position</th>
                <th className="pb-1.5 font-semibold text-right">Betrag</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#F3F4F6]">
                <td className="py-1.5 text-[#1A1A2E]">Anfangssaldo</td>
                <td className="py-1.5 text-right text-[#1A1A2E] tabular-nums">{eur(m.anfang_eur, 2)}</td>
              </tr>
              {m.zeilen.map(z => (
                <Fragment key={z.kategorie}>
                  <tr className="border-b border-[#F3F4F6]">
                    <td className="py-1.5 text-[#4B5563]">
                      {z.posten?.length > 0 ? (
                        <button
                          onClick={() => setOffeneZeile(o => (o === z.kategorie ? null : z.kategorie))}
                          className="hover:underline text-left">
                          <span className="text-[#9CA3AF] mr-1">{offeneZeile === z.kategorie ? '▾' : '▸'}</span>
                          {z.label}
                        </button>
                      ) : z.label}
                      {' '}<Marke art={z.herkunft} />
                      {m.quelle === 'IST' && (z.ein_eur > 0 || z.aus_eur > 0) && (
                        <span className="ml-2 text-[10px] text-[#9CA3AF]">
                          {eur(z.ein_eur)} ein · {eur(-z.aus_eur)} aus
                        </span>
                      )}
                    </td>
                    <td className={`py-1.5 text-right tabular-nums ${z.betrag_eur < 0 ? 'text-[#B45309]' : 'text-[#1A1A2E]'}`}>
                      {eur(z.betrag_eur, 2)}
                    </td>
                  </tr>
                  {offeneZeile === z.kategorie && z.posten?.map((p, i) => (
                    <tr key={i} className="border-b border-[#F3F4F6] bg-[#F8F9FC]">
                      <td className="py-1 pl-6 text-[11px] text-[#6B7280]">
                        {p.text}
                        <span className="ml-2 text-[10px] text-[#9CA3AF]">{zahltagText(p)}</span>
                      </td>
                      <td className="py-1 text-right text-[11px] text-[#6B7280] tabular-nums">{eur(p.betrag_eur, 2)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {m.rest_eur != null && Math.abs(m.rest_eur) >= 0.01 && (
                <tr className="border-b border-[#F3F4F6]">
                  <td className="py-1.5 text-[#B45309]"
                    title="Differenz zwischen Kontobewegung und zugeordneten Strömen — ausgewiesen, nie weggerechnet">
                    Nicht zugeordneter Rest
                  </td>
                  <td className="py-1.5 text-right text-[#B45309] tabular-nums">{eur(m.rest_eur, 2)}</td>
                </tr>
              )}
              <tr>
                <td className="py-1.5 font-semibold text-[#1A1A2E]">Endsaldo {name}</td>
                <td className="py-1.5 text-right font-semibold text-[#1A1A2E] tabular-nums">{eur(m.ende_eur, 2)}</td>
              </tr>
            </tbody>
          </table>
          {m.top_bewegungen?.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-[#9CA3AF] mb-1">Größte Bewegungen des Monats</div>
              <table className="w-full text-[11px]">
                <tbody>
                  {m.top_bewegungen.map((b, i) => (
                    <tr key={i} className="border-b border-[#F3F4F6] last:border-0">
                      <td className="py-0.5 text-[#9CA3AF] whitespace-nowrap">{kurzDatum(b.datum)}</td>
                      <td className="py-0.5 text-[#6B7280] px-2">
                        {b.text}{b.partner && <span className="text-[#9CA3AF]"> · {b.partner}</span>}
                        {b.gegenkonto && <span className="text-[#9CA3AF]"> · {b.gegenkonto}</span>}
                      </td>
                      <td className={`py-0.5 text-right tabular-nums whitespace-nowrap ${b.betrag_eur < 0 ? 'text-[#B45309]' : 'text-[#4B5563]'}`}>
                        {eur(b.betrag_eur, 2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Eine Planzelle: Betrag + optionaler Zahltag, beides überschreibbar.
 * #1851: im Szenario schreibt die Eingabe NUR ins Overlay (sessionStorage) —
 * USt und Kreditkarte bleiben dort reine Anzeige, sie rechnen aus dem Modell mit.
 */
function PlanZelle({ m, feld, wert, herkunft, zahltag, zusatz, schreibe, szenarioAktiv, szenarioWerte, setSzenarioWert }) {
  const speichere = (felder) => schreibe('monatswert', {
    jahr: m.jahr, monat: m.monat, feld,
    wert_eur: felder.wert_eur !== undefined ? felder.wert_eur : (herkunft === 'MANUELL' ? wert : null),
    zahltag: felder.zahltag !== undefined ? felder.zahltag : (zahltag ?? null),
  });
  // B4 (#1839): ein geleertes Feld ist die Rücknahme des Overrides — DELETE
  // statt PUT mit null, sonst bliebe eine Zeile stehen, die wie ein manueller
  // Nullwert aussieht. Ohne Override ist Leeren ein No-op.
  const speichereWert = (v) => {
    if (v === null) {
      if (herkunft === 'MANUELL') schreibe('monatswert', { jahr: m.jahr, monat: m.monat, feld }, 'DELETE');
      return;
    }
    speichere({ wert_eur: v });
  };

  if (szenarioAktiv) {
    const editierbar = SZENARIO_FELDER.includes(feld);
    const gesetzt = szenarioWerte?.[`${m.jahr}-${m.monat}|${feld}`];
    return (
      <td className="py-2.5 text-right">
        {editierbar ? (
          <ZahlFeld
            wert={gesetzt !== undefined ? gesetzt : null}
            placeholder={wert === null || wert === undefined ? '–' : Math.round(wert).toLocaleString('de-AT')}
            breite="w-28"
            onSave={v => setSzenarioWert(m.jahr, m.monat, feld, v)}
          />
        ) : (
          <span className="inline-block w-28 px-2 py-1 text-sm text-right tabular-nums text-[#1A1A2E]">
            {wert === null || wert === undefined ? '–' : Math.round(wert).toLocaleString('de-AT')}
          </span>
        )}
        <div className="text-[10px] text-[#9CA3AF] mt-1 flex items-center justify-end gap-1">
          <Marke art={herkunft} />
          {zahltag != null && <span>am {zahltag}.</span>}
        </div>
        {zusatz && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{zusatz}</div>}
      </td>
    );
  }

  return (
    <td className="py-2.5 text-right">
      <ZahlFeld
        wert={herkunft === 'MANUELL' ? wert : null}
        placeholder={wert === null || wert === undefined ? '–' : Math.round(wert).toLocaleString('de-AT')}
        breite="w-28"
        onSave={speichereWert}
      />
      <div className="text-[10px] text-[#9CA3AF] mt-1 flex items-center justify-end gap-1">
        <Marke art={herkunft} />
        {zahltag != null && (
          <span>
            am{' '}
            <input
              type="number" min={1} max={31} defaultValue={zahltag}
              onBlur={e => {
                const t = parseInt(e.target.value, 10);
                if (t >= 1 && t <= 31 && t !== zahltag) speichere({ zahltag: t });
              }}
              className="w-9 px-1 py-0.5 text-[10px] text-right border border-[#E5E7EB] rounded focus:outline-none focus:border-[#338edc]"
            />
            .
          </span>
        )}
      </div>
      {zusatz && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{zusatz}</div>}
    </td>
  );
}

const REGEL_LABELS = {
  umsatz_rechnung: 'Umsatz → Zahlungseingang',
  personal_split: 'Personal: Netto / SV / Finanzamt / KommSt',
  ust: 'Umsatzsteuer',
  koest: 'Körperschaftsteuer (Quartal)',
  lieferanten_verzug: 'Lieferanten-Zahlverzug',
  bruttierung: 'Bruttierung je Kostenblock',
  verbundene_partner: 'Verbundene Unternehmen',
};

function pct(v, stellen = 1) {
  return `${(Number(v) * 100).toFixed(stellen).replace('.', ',')} %`;
}
function faktorText(v) {
  return `×${String(Math.round(Number(v) * 10000) / 10000).replace('.', ',')}`;
}

/**
 * #1841 Teil A: jede Regel als Klartext-Satz aus ihrem AKTUELLEN Wert (auch
 * ein manueller Override liest sich so) — rohes JSON versteht kein
 * Shareholder. Fehlen erwartete Felder, bleibt das JSON als Rückfall sichtbar.
 */
function RegelKlartext({ schluessel, wert, partnerNamen }) {
  const w = wert || {};
  const name = id => partnerNamen?.[id] || `Partner ${id}`;
  try {
    switch (schluessel) {
      case 'umsatz_rechnung':
        return (
          <p className="text-xs text-[#4B5563] mt-1.5">
            Rechnungen zum Monatsultimo werden im Schnitt nach <strong>{w.ziel_tage} Tagen</strong> bezahlt.
            {' '}<strong>{pct(w.intramonat_anteil)}</strong> der Monatsleistung sind schon unterm Monat
            fakturiert (um den {w.intramonat_tag}.) und binnen <strong>{w.intramonat_zahl_tage} Tagen</strong> bezahlt.
          </p>
        );
      case 'personal_split':
        return (
          <p className="text-xs text-[#4B5563] mt-1.5">
            Vom Personalaufwand fließen <strong>{pct(w.netto)}</strong> als Netto-Gehälter am Monatsende ab;
            im Folgemonat folgen <strong>{pct(w.sv)}</strong> Sozialversicherung (am {w.sv_tag}.),
            {' '}<strong>{pct(w.fa)}</strong> Finanzamt (am {w.fa_tag}.) und
            {' '}<strong>{pct(w.kommst)}</strong> Kommunalsteuer (am {w.kommst_tag}.).
          </p>
        );
      case 'ust':
        return (
          <p className="text-xs text-[#4B5563] mt-1.5">
            <strong>{pct(w.satz, 2)}</strong> vom Netto-Umsatz eines Monats, bezahlt am {w.tag}. des
            {' '}{w.monats_versatz === 1 ? 'Folgemonats' : `${w.monats_versatz}. Folgemonats`}.
          </p>
        );
      case 'koest':
        return (
          <p className="text-xs text-[#4B5563] mt-1.5">
            <strong>{eur(w.betrag_eur)}</strong> Mindest-Körperschaftsteuer je Quartal, fällig am {w.tag}. in
            den Monaten {(w.monate || []).map(m => MONATE[m - 1]).join(', ')}.
          </p>
        );
      case 'lieferanten_verzug': {
        const paare = Object.entries(w.je_partner || {})
          .map(([id, tage]) => ({ id: Number(id), tage: Number(tage) }))
          .sort((a, b) => b.tage - a.tage);
        return (
          <div className="text-xs text-[#4B5563] mt-1.5">
            <p>
              Eingangsrechnungen werden im Schnitt <strong>{w.global_median_tage} Tage</strong> nach dem
              Rechnungsdatum bezahlt. Läge der Termin in der Vergangenheit, wird derselbe Median ab heute
              gerechnet — mindestens aber <strong>{w.ueberfaellig_min_tage} Tage</strong> als Annahme.
              Für Lieferanten mit eigener Zahlungshistorie gilt der je Lieferant gemessene Verzug:
            </p>
            {paare.length > 0 && (
              <table className="mt-1.5 text-[11px] max-w-md w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB]">
                    <th className="pb-1 font-semibold">Lieferant</th>
                    <th className="pb-1 font-semibold text-right">Gemessener Verzug</th>
                  </tr>
                </thead>
                <tbody>
                  {paare.map(p => (
                    <tr key={p.id} className="border-b border-[#F3F4F6] last:border-0">
                      <td className="py-0.5 text-[#4B5563]">{name(p.id)}</td>
                      <td className="py-0.5 text-right tabular-nums text-[#1A1A2E]">{p.tage} Tage</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      }
      case 'bruttierung':
        return (
          <p className="text-xs text-[#4B5563] mt-1.5">
            Cash-Abfluss = Netto-Aufwand mal Faktor: variable Kosten <strong>{faktorText(w.variable_faktor)}</strong>,
            {' '}Fixkosten <strong>{faktorText(w.fix_faktor)}</strong>. Bewusst nie pauschal ×1,20 —
            Reverse Charge, Zoll, Personalleasing und Express-Carrier fließen netto ab.
          </p>
        );
      case 'verbundene_partner': {
        const ids = Array.isArray(wert) ? wert : [];
        return (
          <p className="text-xs text-[#4B5563] mt-1.5">
            Als verbundene Unternehmen gelten: <strong>{ids.map(id => name(id)).join(', ') || '–'}</strong>.
            Ihre Altforderungen sind in der Zweifelhaft-Karte eigens gekennzeichnet.
          </p>
        );
      }
      default:
        break;
    }
  } catch { /* Rückfall unten */ }
  return (
    <code className="block text-[11px] text-[#4B5563] bg-[#F8F9FC] rounded px-2 py-1 mt-1.5 overflow-x-auto whitespace-pre-wrap">
      {JSON.stringify(wert)}
    </code>
  );
}

/**
 * Eine Zahlungsregel: gemessener Wert mit Grundlage, manuell überschreibbar
 * (als JSON — die Regeln sind strukturierte Werte, kein einzelner Betrag).
 */
function RegelZeile({ schluessel, regel, partnerNamen, schreibe }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState('');
  const [parseFehler, setParseFehler] = useState(null);

  const oeffnen = () => {
    setText(JSON.stringify(regel.wert, null, 2));
    setParseFehler(null);
    setOffen(true);
  };
  const speichern = async () => {
    let wert;
    try { wert = JSON.parse(text); } catch { setParseFehler('Kein gültiges JSON'); return; }
    await schreibe('regel', { schluessel, wert });
    setOffen(false);
  };

  return (
    <div className="border border-[#F3F4F6] rounded-lg px-3 py-2.5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm text-[#1A1A2E]">
            {REGEL_LABELS[schluessel] || schluessel}{' '}
            {regel.quelle === 'manuell'
              ? <Marke art="MANUELL" />
              : <span className="text-[10px] px-1.5 py-0.5 rounded border bg-[#EFF6FF] text-[#338edc] border-[#BFDBFE]"
                  title="Aus den echten Belegen gemessen">gemessen</span>}
          </div>
          {regel.notiz && <p className="text-[11px] text-[#0891B2] mt-0.5">Notiz: {regel.notiz}</p>}
          <RegelKlartext schluessel={schluessel} wert={regel.wert} partnerNamen={partnerNamen} />
          <p className="text-[11px] text-[#9CA3AF] mt-1 max-w-3xl">Grundlage: {regel.grundlage}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!offen && (
            <button onClick={oeffnen} className="text-[11px] text-[#338edc] hover:underline">
              überschreiben
            </button>
          )}
          {regel.quelle === 'manuell' && (
            <button onClick={() => schreibe(`regel/${schluessel}`, null, 'DELETE')}
              className="text-[11px] text-[#6B7280] hover:text-[#1A1A2E] hover:underline">
              auf Messwert zurück
            </button>
          )}
        </div>
      </div>
      {offen && (
        <div className="mt-2">
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={4} spellCheck={false}
            className="w-full font-mono text-[11px] px-2 py-1.5 border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#338edc]"
          />
          {parseFehler && <p className="text-[11px] text-red-600 mt-1">{parseFehler}</p>}
          <div className="flex gap-2 mt-1.5">
            <button onClick={speichern}
              className="text-[11px] px-2.5 py-1 rounded-md bg-[#338edc] text-white font-medium">
              Als MANUELL speichern
            </button>
            <button onClick={() => setOffen(false)}
              className="text-[11px] px-2.5 py-1 text-[#6B7280] hover:text-[#1A1A2E]">
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * #1860: Zahlungsvereinbarung — Einmalzahlung, Ratenplan oder Stundung für
 * einen Beleg, einen Gläubiger (Sammel) oder eine freie Verpflichtung (DE-USt).
 * Der Ratenplan-GENERATOR ist nur eine Vorschau: gespeichert wird der
 * Parametersatz, der Server expandiert ihn (EINE Quelle). Die freie Ratenliste
 * wird dagegen als Liste gespeichert. Die Summen-Warnung warnt nur — sie
 * korrigiert NIE still (eine abweichende Zusage ist eine Information).
 */
function VereinbarungDialog({ ziel, onClose, onSave }) {
  const vorhanden = ziel.vorhanden || null;
  // #1861: derselbe Dialog fuer beide Richtungen — die Forderungsseite heisst
  // nur anders und kennt zusaetzlich "uneinbringlich".
  const istForderung = (ziel.richtung || vorhanden?.richtung) === 'forderung';
  const partnerVon = (z) => (z ? (z.lieferant || z.kunde || '') : '');
  const [art, setArt] = useState(vorhanden?.art || 'raten');
  const [marke, setMarke] = useState(vorhanden?.marke || 'vereinbarung');
  const [zahlungAm, setZahlungAm] = useState(vorhanden?.zahlung_am ? String(vorhanden.zahlung_am).slice(0, 10) : heuteIso());
  const [ratenModus, setRatenModus] = useState(vorhanden?.art === 'raten' && !vorhanden?.ratenplan ? 'frei' : 'generator');
  const [anzahl, setAnzahl] = useState(vorhanden?.ratenplan?.anzahl || 3);
  const [intervall, setIntervall] = useState(vorhanden?.ratenplan?.intervall || 'monatlich');
  // (a) Vorbelegung ist der Erste des FOLGEMONATS — „heute" war als erste Rate
  // praktisch nie gemeint und musste jedes Mal überschrieben werden.
  const [ersteRate, setErsteRate] = useState(vorhanden?.ratenplan?.erster_zahltag || ersterFolgemonatIso());
  const [betragJeRate, setBetragJeRate] = useState(vorhanden?.ratenplan?.betrag_je_rate ?? '');
  const [schlussrate, setSchlussrate] = useState(vorhanden?.ratenplan?.schlussrate_eur ?? '');
  // (b) Runde Raten statt 36.881,07 — der Rest landet in der Schlussrate.
  const [rundung, setRundung] = useState(String(vorhanden?.ratenplan?.rundung_eur ?? 0));
  const [freieRaten, setFreieRaten] = useState(() => (
    vorhanden?.art === 'raten' && !vorhanden?.ratenplan && Array.isArray(vorhanden?.raten)
      ? vorhanden.raten.map(r => ({ datum: r.datum, betrag: String(r.betrag_eur) }))
      : [{ datum: heuteIso(), betrag: '' }]
  ));
  const [teilzahlungen, setTeilzahlungen] = useState(() => (
    Array.isArray(vorhanden?.teilzahlungen)
      ? vorhanden.teilzahlungen.map(t => ({ datum: t.datum, betrag: String(t.betrag_eur) }))
      : []
  ));
  const [bezeichnung, setBezeichnung] = useState(vorhanden?.glaeubiger || ziel.bezeichnung || '');
  const [betragFrei, setBetragFrei] = useState(vorhanden?.gesamt_eur ?? ziel.gesamt_eur ?? '');
  const [notiz, setNotiz] = useState(vorhanden?.notiz || '');
  const [fehler, setFehler] = useState(null);
  const [feldFehler, setFeldFehler] = useState({});
  const [busy, setBusy] = useState(false);
  // Ein Abbruch mit ungesicherten Eingaben fragt nach — aber nur, wenn wirklich
  // etwas angefasst wurde; sonst wäre die Rückfrage bloss Lärm.
  const [beruehrt, setBeruehrt] = useState(false);
  // Ohne Fokus IM Dialog landen Tastendrücke am `body` und laufen an der
  // Eindämmung vorbei — der Rahmen holt ihn sich deshalb beim Öffnen.
  const rahmen = useRef(null);
  useEffect(() => { rahmen.current?.focus(); }, []);

  const gesamt = ziel.modus === 'frei'
    ? (ziel.frei_schluessel === 'ust_de' ? Number(ziel.gesamt_eur) : (dez(betragFrei) || 0))
    : Number(ziel.gesamt_eur) || 0;

  // Vorschau — Spiegel von expandiereRatenplan im Backend (dort ist die Quelle).
  const generatorRaten = useMemo(() => {
    const n = Math.max(1, Math.min(60, parseInt(anzahl, 10) || 1));
    const schritt = intervall === 'quartalsweise' ? 3 : 1;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ersteRate)) return [];
    const [j0, m0, t0] = ersteRate.split('-').map(Number);
    const rund = v => Math.round(v * 100) / 100;
    const jeEingabe = dez(betragJeRate);
    const jeGesetzt = jeEingabe !== null && !Number.isNaN(jeEingabe);
    let je = jeGesetzt ? rund(jeEingabe) : rund(gesamt / n);
    const stufe = Number(rundung) || 0;
    if (!jeGesetzt && stufe > 0 && n > 1) {
      const gerundet = Math.floor(gesamt / n / stufe) * stufe;
      if (gerundet > 0) je = rund(gerundet);
    }
    if (!(je > 0)) return [];
    const schluss = dez(schlussrate);
    // #1859 (2): terminieren laesst sich nur, was offen ist — die kumulierte
    // Ratensumme wird am Gesamtbetrag gedeckelt, ausgeschoepfte Raten
    // entfallen. Ohne bekannten Gesamtbetrag gibt es nichts zu deckeln.
    const deckel = gesamt > 0 ? Math.round(gesamt * 100) : null;
    let verteilt = 0;
    const out = [];
    for (let i = 0; i < n; i++) {
      const idx = (m0 - 1) + i * schritt;
      const j = j0 + Math.floor(idx / 12); const m = idx % 12;
      const letzterTag = new Date(Date.UTC(j, m + 1, 0)).getUTCDate();
      const dt = new Date(Date.UTC(j, m, Math.min(t0, letzterTag)));
      let betrag = je;
      if (i === n - 1) {
        if (schluss !== null && !Number.isNaN(schluss)) betrag = rund(schluss);
        else if (!jeGesetzt) betrag = rund(gesamt - je * (n - 1));
      }
      let cents = Math.round(betrag * 100);
      if (deckel !== null) {
        cents = Math.min(cents, deckel - verteilt);
        if (cents <= 0) break;
      }
      verteilt += cents;
      out.push({ datum: dt.toISOString().slice(0, 10), betrag_eur: cents / 100, gekuerzt: cents < Math.round(betrag * 100) });
    }
    return out;
  }, [anzahl, intervall, ersteRate, betragJeRate, schlussrate, rundung, gesamt]);

  const aktiveRaten = art !== 'raten' ? []
    : ratenModus === 'generator' ? generatorRaten
      : freieRaten.filter(r => r.datum && dez(r.betrag) > 0)
        .map(r => ({ datum: r.datum, betrag_eur: Math.round(dez(r.betrag) * 100) / 100 }));
  const teilSumme = teilzahlungen.reduce((s, t) => s + (dez(t.betrag) || 0), 0);
  const ratenSumme = aktiveRaten.reduce((s, r) => s + r.betrag_eur, 0);
  const summenDelta = art === 'raten' && gesamt > 0
    ? Math.round((ratenSumme + teilSumme - gesamt) * 100) / 100 : 0;
  // Der Deckel darf nicht stillschweigend wirken (#1860): gekürzte und
  // entfallene Raten werden benannt, sonst steht im Plan etwas anderes als
  // eingetippt und niemand erfährt warum.
  const gewuenschteAnzahl = Math.max(1, Math.min(60, parseInt(anzahl, 10) || 1));
  const deckelAktiv = art === 'raten' && ratenModus === 'generator' && gesamt > 0;
  const gekuerzt = deckelAktiv && generatorRaten.some(r => r.gekuerzt);
  const entfallen = deckelAktiv ? Math.max(0, gewuenschteAnzahl - generatorRaten.length) : 0;

  /**
   * Prüfung AM FELD statt stiller Fehlschlag: liefert je Feldnamen den Grund.
   * Sie läuft vor dem Absenden — der Server prüft danach nochmal (er bleibt die
   * durchsetzende Stelle, das hier ist nur die schnelle Rückmeldung).
   */
  function pruefe() {
    const f = {};
    const zahl = (wert, name, pflicht) => {
      const z = dez(wert);
      if (z === null) { if (pflicht) f[name] = 'Pflichtfeld'; return null; }
      if (Number.isNaN(z)) { f[name] = 'Keine gültige Zahl'; return null; }
      if (z <= 0) { f[name] = 'Muss größer 0 sein'; return null; }
      return z;
    };
    if (ziel.modus === 'frei') {
      if (!bezeichnung.trim()) f.bezeichnung = 'Pflichtfeld — wie soll die Verpflichtung heißen?';
      zahl(betragFrei, 'betragFrei', true);
    }
    if (art === 'einmal' || art === 'stundung') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(zahlungAm)) f.zahlungAm = 'Pflichtfeld — Datum im Format JJJJ-MM-TT';
    }
    if (art === 'raten' && ratenModus === 'generator') {
      const n = parseInt(anzahl, 10);
      if (!(n >= 1 && n <= 60)) f.anzahl = 'Zwischen 1 und 60';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ersteRate)) f.ersteRate = 'Pflichtfeld — Datum im Format JJJJ-MM-TT';
      const je = dez(betragJeRate);
      if (je !== null && (Number.isNaN(je) || je <= 0)) f.betragJeRate = 'Leer lassen oder Betrag größer 0';
      const sr = dez(schlussrate);
      if (sr !== null && (Number.isNaN(sr) || sr <= 0)) f.schlussrate = 'Leer lassen oder Betrag größer 0';
      // Der Fall, der Manuels Fehlschlag ausgelöst hat: kein Gesamtbetrag UND
      // kein Betrag je Rate — dann gibt es nichts zu teilen.
      if (je === null && !(gesamt > 0)) {
        f.betragJeRate = 'Ohne bekannten Gesamtbetrag braucht es hier einen Betrag';
      }
    }
    if (art === 'raten' && ratenModus === 'frei') {
      if (!aktiveRaten.length) f.freieRaten = 'Mindestens eine Rate mit Datum und Betrag größer 0';
      freieRaten.forEach((r, i) => {
        if (!r.datum && !String(r.betrag).trim()) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(r.datum)) f[`rate${i}`] = 'Datum fehlt';
        else zahl(r.betrag, `rate${i}`, true);
      });
    }
    teilzahlungen.forEach((t, i) => {
      if (!t.datum && !String(t.betrag).trim()) return;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(t.datum)) f[`teil${i}`] = 'Datum fehlt';
      else zahl(t.betrag, `teil${i}`, true);
    });
    return f;
  }

  async function speichern() {
    const f = pruefe();
    setFeldFehler(f);
    if (Object.keys(f).length) {
      setFehler('Bitte die rot markierten Felder prüfen — es wurde nichts gespeichert.');
      return;
    }
    setBusy(true); setFehler(null);
    try {
      const body = {
        id: vorhanden?.id || undefined,
        typ: ziel.modus === 'ust_de' ? 'frei' : ziel.modus,
        richtung: istForderung ? 'forderung' : 'verbindlichkeit',
        art, marke,
        zahlung_am: (art === 'raten' || art === 'uneinbringlich') ? null : zahlungAm,
        notiz: notiz || null,
        teilzahlungen: art === 'uneinbringlich' ? [] : teilzahlungen
          .filter(t => t.datum && dez(t.betrag) > 0)
          .map(t => ({ datum: t.datum, betrag_eur: Math.round(dez(t.betrag) * 100) / 100 })),
      };
      if (art === 'raten') {
        if (ratenModus === 'generator') {
          body.ratenplan = {
            anzahl: Math.max(1, Math.min(60, parseInt(anzahl, 10) || 1)),
            intervall,
            erster_zahltag: ersteRate,
            betrag_je_rate: dez(betragJeRate),
            schlussrate_eur: dez(schlussrate),
            rundung_eur: Number(rundung) || null,
          };
        } else {
          body.raten = aktiveRaten;
        }
      }
      if (ziel.modus === 'beleg') {
        body.move_line_id = ziel.zeile.move_line_id;
        body.partner_name = partnerVon(ziel.zeile);
        body.betrag_eur = ziel.zeile.offen_eur;
      } else if (ziel.modus === 'glaeubiger') {
        body.move_line_ids = ziel.zeilen.map(z => z.move_line_id);
        body.partner_name = partnerVon(ziel.zeilen[0]);
        body.partner_id = ziel.zeilen[0].partner_id ?? null;
        body.betrag_eur = gesamt;
      } else {
        body.frei_schluessel = ziel.modus === 'ust_de' ? 'ust_de' : (ziel.frei_schluessel || null);
        body.bezeichnung = ziel.modus === 'ust_de' ? 'Umsatzsteuer Deutschland' : bezeichnung;
        // ust_de bewusst `null`: der offene Stand kommt LIVE aus Odoo und wird
        // nie eingefroren — der Server löst ihn selbst auf (#1860-Nachbesserung).
        body.betrag_eur = ziel.modus === 'ust_de' ? null : (dez(betragFrei) || null);
        if (ziel.modus === 'ust_de') body.typ = 'frei';
      }
      await onSave(body);
    } catch (e) {
      setFehler(e.message); setBusy(false);
    }
  }

  const feld = 'block mt-0.5 px-2 py-1 text-sm border rounded-md focus:outline-none focus:border-[#338edc]';
  const rand = (name) => (feldFehler[name] ? 'border-[#DC2626] bg-[#FEF2F2]' : 'border-[#E5E7EB]');
  const Hinweis = ({ name }) => (feldFehler[name]
    ? <span className="block text-[10px] text-[#B91C1C] mt-0.5">{feldFehler[name]}</span> : null);
  /** Jede Eingabe macht den Dialog „berührt" und räumt die Markierung des Feldes weg. */
  const beruehre = (name) => { setBeruehrt(true); if (name && feldFehler[name]) setFeldFehler(f => { const n = { ...f }; delete n[name]; return n; }); };

  function schliessen() {
    if (beruehrt && !window.confirm('Es gibt ungespeicherte Eingaben. Dialog wirklich schließen?')) return;
    onClose();
  }

  /**
   * Tastendruck-Eindämmung: KEIN Tastendruck aus diesem Dialog erreicht die
   * Seite darunter oder das Dokument. Backspace ausserhalb eines echten
   * Eingabefeldes wird zusätzlich unterdrückt — in manchen Browsern/Erweiterungen
   * ist das „zurück", und genau das hat den Dialog geschlossen (Manuels Punkt 2).
   * Escape schliesst (mit Rückfrage), Enter im Textfeld speichert nicht
   * versehentlich — dafür gibt es den Knopf.
   */
  function tasten(e) {
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); schliessen(); return; }
    if (e.key !== 'Backspace') return;
    const t = e.target;
    const tippbar = t && (t.isContentEditable
      || (t.tagName === 'TEXTAREA')
      || (t.tagName === 'INPUT' && !['button', 'submit', 'checkbox', 'radio', 'date'].includes(t.type)));
    if (!tippbar) e.preventDefault();
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={schliessen} onKeyDown={tasten} onKeyUp={e => e.stopPropagation()}>
      <div ref={rahmen} tabIndex={-1}
        className="bg-white rounded-xl border border-[#E5E7EB] w-full max-w-xl p-5 my-8 focus:outline-none"
        onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-[#1A1A2E]">
          {istForderung
            ? (vorhanden ? 'Zahlungserwartung bearbeiten' : 'Zahlungserwartung setzen')
            : (vorhanden ? 'Zahlungsvereinbarung bearbeiten' : 'Zahlungsvereinbarung')}
        </h3>
        <p className="text-xs text-[#6B7280] mt-1">
          {ziel.modus === 'beleg' && <>{partnerVon(ziel.zeile)} · {ziel.zeile.beleg} · {eur(ziel.zeile.offen_eur, 2)} offen, fällig {datum(ziel.zeile.faellig_am)}</>}
          {ziel.modus === 'glaeubiger' && <>{istForderung ? 'Sammelerwartung' : 'Sammelvereinbarung'} {partnerVon(ziel.zeilen?.[0])} · {ziel.zeilen?.length} Belege · {eur(gesamt, 2)} offen</>}
          {ziel.modus === 'ust_de' && <>Umsatzsteuer Deutschland · {eur(gesamt, 2)} offen (Konten 3518 + 3522)</>}
          {ziel.modus === 'frei' && 'Freie Verpflichtung — Kontosaldo oder Zusage ohne Odoo-Beleg'}
        </p>

        {ziel.modus === 'frei' && (
          <div className="flex gap-3 mt-3">
            <label className="block flex-1">
              <span className="text-[11px] text-[#6B7280]">Bezeichnung</span>
              <input type="text" value={bezeichnung}
                onChange={e => { setBezeichnung(e.target.value); beruehre('bezeichnung'); }}
                placeholder="z. B. Sozialversicherung Rückstand" className={`${feld} ${rand('bezeichnung')} w-full`} />
              <Hinweis name="bezeichnung" />
            </label>
            <label className="block">
              <span className="text-[11px] text-[#6B7280]">Gesamtbetrag €</span>
              <input type="text" inputMode="decimal" value={betragFrei}
                onChange={e => { setBetragFrei(e.target.value); beruehre('betragFrei'); }}
                placeholder="0,00" className={`${feld} ${rand('betragFrei')} w-32 text-right`} />
              <Hinweis name="betragFrei" />
            </label>
          </div>
        )}

        <div className="flex gap-2 mt-4 flex-wrap">
          {[
            ['einmal', 'Einmalzahlung'],
            ['raten', 'Ratenplan'],
            [istForderung ? null : 'stundung', 'Stundung'],
            [istForderung ? 'uneinbringlich' : null, 'uneinbringlich'],
          ].filter(([w]) => w).map(([w, l]) => (
            <button key={w} type="button" onClick={() => { setArt(w); beruehre(); }}
              className={`px-3 py-1.5 text-xs rounded-md border ${
                art === w
                  ? (w === 'uneinbringlich'
                    ? 'bg-white border-[#DC2626] text-[#B91C1C] font-medium shadow-sm'
                    : 'bg-white border-[#338edc] text-[#338edc] font-medium shadow-sm')
                  : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'}`}>
              {l}
            </button>
          ))}
        </div>

        {art === 'uneinbringlich' ? (
          <div className="mt-4 rounded border border-[#FECACA] bg-[#FEF2F2] px-3 py-2">
            <div className="text-xs font-medium text-[#B91C1C]">Diese Forderung bleibt dauerhaft aus beiden Kurven.</div>
            <div className="mt-1 text-[11px] text-[#6B7280]">
              Sie wird als eigene Summe „davon uneinbringlich gekennzeichnet" ausgewiesen. Die Kennzeichnung ist
              eine Planungsangabe — die Wertberichtigung ist in der Buchhaltung separat zu buchen; hier wird
              nichts nach Odoo geschrieben.
            </div>
          </div>
        ) : art !== 'raten' ? (
          <label className="block mt-4">
            <span className="text-[11px] text-[#6B7280]">{art === 'stundung' ? 'Gestundet bis — Zahlung am' : 'Zahlung am'}</span>
            <input type="date" value={zahlungAm}
              onChange={e => { setZahlungAm(e.target.value); beruehre('zahlungAm'); }}
              className={`${feld} ${rand('zahlungAm')}`} />
            <Hinweis name="zahlungAm" />
          </label>
        ) : (
          <>
            <div className="flex gap-2 mt-4">
              {[['generator', 'Raten erzeugen'], ['frei', 'Freie Ratenliste']].map(([w, l]) => (
                <button key={w} type="button" onClick={() => { setRatenModus(w); beruehre(); }}
                  className={`px-2.5 py-1 text-[11px] rounded-md border ${
                    ratenModus === w ? 'bg-white border-[#338edc] text-[#338edc] font-medium shadow-sm'
                      : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'}`}>
                  {l}
                </button>
              ))}
            </div>

            {ratenModus === 'generator' ? (
              <>
              <div className="flex gap-3 mt-3 flex-wrap">
                <label className="block">
                  <span className="text-[11px] text-[#6B7280]">Anzahl</span>
                  <input type="text" inputMode="numeric" value={anzahl}
                    onChange={e => { setAnzahl(e.target.value.replace(/[^\d]/g, '')); beruehre('anzahl'); }}
                    className={`${feld} ${rand('anzahl')} w-16 text-right`} />
                  <Hinweis name="anzahl" />
                </label>
                <label className="block">
                  <span className="text-[11px] text-[#6B7280]">Intervall</span>
                  <select value={intervall} onChange={e => { setIntervall(e.target.value); beruehre(); }}
                    className={`${feld} border-[#E5E7EB]`}>
                    <option value="monatlich">monatlich</option>
                    <option value="quartalsweise">quartalsweise</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[11px] text-[#6B7280]">Erste Rate am</span>
                  <input type="date" value={ersteRate}
                    onChange={e => { setErsteRate(e.target.value); beruehre('ersteRate'); }}
                    className={`${feld} ${rand('ersteRate')}`} />
                  <Hinweis name="ersteRate" />
                </label>
                <label className="block">
                  <span className="text-[11px] text-[#6B7280]">€ je Rate <span className="text-[#9CA3AF]">(leer = Gesamt ÷ Anzahl)</span></span>
                  <input type="text" inputMode="decimal" value={betragJeRate} placeholder="automatisch"
                    onChange={e => { setBetragJeRate(e.target.value); beruehre('betragJeRate'); }}
                    className={`${feld} ${rand('betragJeRate')} w-28 text-right`} />
                  <Hinweis name="betragJeRate" />
                </label>
                <label className="block">
                  <span className="text-[11px] text-[#6B7280]">Schlussrate € <span className="text-[#9CA3AF]">(optional)</span></span>
                  <input type="text" inputMode="decimal" value={schlussrate} placeholder="Rest"
                    onChange={e => { setSchlussrate(e.target.value); beruehre('schlussrate'); }}
                    className={`${feld} ${rand('schlussrate')} w-28 text-right`} />
                  <Hinweis name="schlussrate" />
                </label>
                {/* (b) Runde Raten statt 36.881,07 — der Rest landet in der Schlussrate. */}
                <label className="block">
                  <span className="text-[11px] text-[#6B7280]">Raten runden auf</span>
                  <select value={rundung} onChange={e => { setRundung(e.target.value); beruehre(); }}
                    disabled={betragJeRate !== ''} className={`${feld} border-[#E5E7EB] disabled:opacity-50`}>
                    <option value="0">Cent-genau</option>
                    <option value="10">10 €</option>
                    <option value="100">100 €</option>
                    <option value="500">500 €</option>
                    <option value="1000">1.000 €</option>
                  </select>
                </label>
              </div>
              {betragJeRate !== '' && Number(rundung) > 0 && (
                <p className="text-[11px] text-[#9CA3AF] mt-1">
                  Rundung wirkt nur, wenn „€ je Rate" leer bleibt — ein fester Ratenbetrag ist bereits die Vorgabe.
                </p>
              )}
              </>
            ) : (
              <div className="mt-3 space-y-1.5">
                {freieRaten.map((r, i) => (
                  <div key={i}>
                    <div className="flex gap-2 items-center">
                      <span className="text-[11px] text-[#6B7280] w-12">{i + 1}. Rate</span>
                      <input type="date" value={r.datum}
                        onChange={e => { setFreieRaten(l => l.map((x, j) => j === i ? { ...x, datum: e.target.value } : x)); beruehre(`rate${i}`); }}
                        className={`px-2 py-1 text-sm border rounded-md ${rand(`rate${i}`)}`} />
                      <input type="text" inputMode="decimal" value={r.betrag} placeholder="0,00"
                        onChange={e => { setFreieRaten(l => l.map((x, j) => j === i ? { ...x, betrag: e.target.value } : x)); beruehre(`rate${i}`); }}
                        className={`w-28 px-2 py-1 text-sm text-right border rounded-md ${rand(`rate${i}`)}`} />
                      <button onClick={() => { setFreieRaten(l => l.filter((_, j) => j !== i)); beruehre(); }}
                        className="text-[11px] text-[#6B7280] hover:text-[#B91C1C]">entfernen</button>
                    </div>
                    <Hinweis name={`rate${i}`} />
                  </div>
                ))}
                <button onClick={() => { setFreieRaten(l => [...l, { datum: ersterFolgemonatIso(), betrag: '' }]); beruehre('freieRaten'); }}
                  className="text-[11px] text-[#338edc] hover:underline">+ Rate hinzufügen</button>
                <Hinweis name="freieRaten" />
              </div>
            )}

            {/* Die Vorschau ist IMMER da — verschwand sie, wusste man nicht, ob
                nichts zu zeigen ist oder etwas fehlt (Manuels Punkt 3). */}
            <div className="mt-3 border border-[#E5E7EB] rounded-md">
              {aktiveRaten.length > 0 ? (
                <>
                  <div className="max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {aktiveRaten.map((r, i) => (
                          <tr key={`${r.datum}-${i}`} className="border-b border-[#F3F4F6] last:border-0">
                            <td className="px-2 py-1 text-[#6B7280]">{i + 1}. Rate</td>
                            <td className="px-2 py-1 text-[#4B5563]">{datum(r.datum)}</td>
                            <td className="px-2 py-1 text-right text-[#1A1A2E] tabular-nums">{eur(r.betrag_eur, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-between px-2 py-1.5 border-t border-[#E5E7EB] bg-[#F8F9FC] text-xs">
                    <span className="text-[#6B7280]">{aktiveRaten.length} Raten, Summe</span>
                    <span className="font-semibold text-[#1A1A2E] tabular-nums">{eur(ratenSumme, 2)}</span>
                  </div>
                  {/* Deckung ausdrücklich benennen: ein Plan darf weniger decken
                      als die Schuld — der Rest bleibt ohne Termin ausgewiesen,
                      aber er darf nicht unbemerkt bleiben. */}
                  {gesamt > 0 && Math.abs(ratenSumme - gesamt) >= 0.01 && (
                    <p className={`px-2 py-1.5 text-[11px] ${ratenSumme < gesamt ? 'text-[#B45309]' : 'text-[#B91C1C]'}`}>
                      {ratenSumme < gesamt
                        ? `Der Plan deckt ${eur(ratenSumme, 2)} von ${eur(gesamt, 2)} — ${eur(gesamt - ratenSumme, 2)} bleiben ohne Termin ausgewiesen.`
                        : `Der Plan übersteigt die offene Summe um ${eur(ratenSumme - gesamt, 2)} (offen: ${eur(gesamt, 2)}).`}
                    </p>
                  )}
                </>
              ) : (
                <p className="px-2 py-2 text-[11px] text-[#B45309]">
                  Noch keine Raten — {ratenModus === 'frei'
                    ? 'bitte mindestens eine Rate mit Datum und Betrag größer 0 erfassen.'
                    : !/^\d{4}-\d{2}-\d{2}$/.test(ersteRate)
                      ? 'es fehlt das Datum der ersten Rate.'
                      : !(gesamt > 0) && dez(betragJeRate) === null
                        ? 'der Gesamtbetrag ist unbekannt — bitte „€ je Rate" ausfüllen.'
                        : 'der Betrag je Rate ergibt keinen Wert größer 0.'}
                </p>
              )}
            </div>
          </>
        )}

        {/* Teilzahlungen: bereits geleistet → mindern die offenen Raten FIFO. */}
        <div className={`mt-4 ${art === 'uneinbringlich' ? 'hidden' : ''}`}>
          <span className="text-[11px] font-medium text-[#6B7280]">
            {istForderung ? 'Bereits eingegangene Teilzahlungen' : 'Bereits geleistete Teilzahlungen'}
          </span>
          <div className="mt-1 space-y-1.5">
            {teilzahlungen.map((t, i) => (
              <div key={i}>
                <div className="flex gap-2 items-center">
                  <input type="date" value={t.datum}
                    onChange={e => { setTeilzahlungen(l => l.map((x, j) => j === i ? { ...x, datum: e.target.value } : x)); beruehre(`teil${i}`); }}
                    className={`px-2 py-1 text-sm border rounded-md ${rand(`teil${i}`)}`} />
                  <input type="text" inputMode="decimal" value={t.betrag} placeholder="0,00"
                    onChange={e => { setTeilzahlungen(l => l.map((x, j) => j === i ? { ...x, betrag: e.target.value } : x)); beruehre(`teil${i}`); }}
                    className={`w-28 px-2 py-1 text-sm text-right border rounded-md ${rand(`teil${i}`)}`} />
                  <button onClick={() => { setTeilzahlungen(l => l.filter((_, j) => j !== i)); beruehre(); }}
                    className="text-[11px] text-[#6B7280] hover:text-[#B91C1C]">entfernen</button>
                </div>
                <Hinweis name={`teil${i}`} />
              </div>
            ))}
            <button onClick={() => { setTeilzahlungen(l => [...l, { datum: heuteIso(), betrag: '' }]); beruehre(); }}
              className="text-[11px] text-[#338edc] hover:underline">+ Teilzahlung erfassen</button>
            {teilSumme > 0 && (
              <p className="text-[11px] text-[#6B7280]">Summe Teilzahlungen {eur(teilSumme, 2)} — mindern die offenen Raten FIFO.</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <span className="text-[11px] font-medium text-[#6B7280]">
            {istForderung ? 'Verbindlichkeit der Erwartung' : 'Verbindlichkeit der Zusage'}
          </span>
          <div className="flex gap-2 mt-1">
            {[['vereinbarung', 'VEREINBARUNG', 'schriftlich zugesagt'], ['annahme', 'ANNAHME', 'eigene Planung']].map(([w, l, t]) => (
              <button key={w} type="button" onClick={() => { setMarke(w); beruehre(); }} title={t}
                className={`px-3 py-1.5 text-xs rounded-md border ${
                  marke === w ? 'bg-white border-[#338edc] text-[#338edc] font-medium shadow-sm'
                    : 'bg-[#F8F9FC] border-[#E5E7EB] text-[#6B7280]'}`}>
                {l} <span className="text-[#9CA3AF] font-normal">— {t}</span>
              </button>
            ))}
          </div>
        </div>

        <label className="block mt-3">
          <span className="text-[11px] text-[#6B7280]">Bezug (optional)</span>
          <input type="text" value={notiz} onChange={e => { setNotiz(e.target.value); beruehre(); }}
            placeholder="z. B. Schreiben Finanzamt Neubrandenburg vom …"
            className={`${feld} border-[#E5E7EB] w-full`} />
        </label>

        {(gekuerzt || entfallen > 0) && (
          <p className="text-xs text-[#B45309] mt-3">
            Terminieren lässt sich nur der offene Betrag von {eur(gesamt, 2)}.
            {gekuerzt && <> Die letzte Rate wurde auf {eur(generatorRaten[generatorRaten.length - 1].betrag_eur, 2)} gekürzt.</>}
            {entfallen > 0 && <> {entfallen === 1 ? 'Eine Rate entfällt' : `${entfallen} Raten entfallen`}, weil der Betrag vorher ausgeschöpft ist.</>}
          </p>
        )}

        {art === 'raten' && gesamt > 0 && summenDelta <= -0.01 && (
          <p className="text-xs text-[#B45309] mt-3">
            Raten {eur(ratenSumme, 2)}{teilSumme > 0 && <> + Teilzahlungen {eur(teilSumme, 2)}</>} ergeben{' '}
            {eur(ratenSumme + teilSumme, 2)} — der offene Betrag ist {eur(gesamt, 2)}{' '}
            ({eur(summenDelta, 2)}). Der Rest bleibt ohne Termin ausgewiesen und läuft nicht in die Kurve.
          </p>
        )}

        {art === 'raten' && gesamt > 0 && summenDelta >= 0.01 && (
          <p className="text-xs text-[#B45309] mt-3">
            Raten {eur(ratenSumme, 2)}{teilSumme > 0 && <> + Teilzahlungen {eur(teilSumme, 2)}</>} ergeben{' '}
            {eur(ratenSumme + teilSumme, 2)} — mehr als der offene Betrag von {eur(gesamt, 2)}{' '}
            (+{eur(summenDelta, 2)}). Terminiert wird höchstens der offene Betrag.
          </p>
        )}

        {/* Der Grund steht AM Dialog, nicht am Seitenanfang — bei gescrollter
            Seite war das Banner dort gemessen 2.139 px ausserhalb des Blickfelds. */}
        {fehler && (
          <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">{fehler}</p>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={schliessen}
            className="text-xs px-3 py-1.5 text-[#6B7280] hover:text-[#1A1A2E]">Abbrechen</button>
          <button type="button" onClick={speichern} disabled={busy}
            className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-60">
            {busy ? 'Speichere…' : istForderung ? 'Erwartung setzen' : 'Vereinbarung setzen'}
          </button>
        </div>
      </div>
    </div>
  );
}
